import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Layout, 
  Card, 
  Table, 
  Tag, 
  Button, 
  Space, 
  Input, 
  InputNumber,
  Select, 
  Row, 
  Col, 
  Statistic, 
  Progress, 
  Badge,
  Tooltip,
  Modal,
  message,
  Spin,
  Empty,
  Typography,
  Divider,
  Timeline,
  Descriptions,
  Tabs,
  Alert,
  Switch,
  TimePicker,
  DatePicker,
  Pagination,
  Drawer,
  Collapse
} from 'antd';
import { 
  PlayCircleOutlined, 
  PauseCircleOutlined, 
  StopOutlined, 
  ReloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  BarChartOutlined,
  SettingOutlined,
  DownloadOutlined,
  FilterOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleFilled,
  ExclamationCircleFilled,
  SyncOutlined as SyncOutlinedIcon,
  MessageOutlined,
  RocketOutlined,
  MailOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  FolderOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  LeftOutlined,
  RightOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  ReloadOutlined as ResetOutlined,
  CloseOutlined,
  ClearOutlined,
  DeleteOutlined,
  BranchesOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  EditOutlined,
  CopyOutlined
} from '@ant-design/icons';
import PdfOverlayEditor from '../components/PdfOverlayEditor/PdfOverlayEditor';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import leafletMarkerIcon from 'leaflet/dist/images/marker-icon.png';
import leafletMarkerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import leafletMarkerShadow from 'leaflet/dist/images/marker-shadow.png';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { useLanguage } from '../contexts/LanguageContext';
import { getUserInterfacesFromStorage, hasInterfacePermission } from '../utils/permissionUtils';
import dayjs from 'dayjs';
// import duration from 'dayjs/plugin/duration'; // 已替換為 TimezoneUtils
import { TimezoneUtils } from '../utils/timezoneUtils';
import { buildFormPreviewBlobUrl, collectFilledHtmlFromIframe } from '../utils/formPreviewIframeUtils';
import { injectFlatpickrIntoIframe } from '../utils/eformDateUtils';
import { getEFormApprovalLabels } from '../utils/eformApprovalLabels';
import {
  canExportEformToPdf,
  exportSingleEformInstanceToPdf,
} from '../utils/eformPdfExportUtils';
import MessageSendStatusModal from '../components/MessageSendStatusModal';
import ChatBotConversationModal from '../components/ChatBotConversationModal';
import DefinitionCategoryTabs from '../components/DefinitionCategoryTabs/DefinitionCategoryTabs';

import { escapeHtml, convertJsonToHtmlTable, EFormContentViewer } from '../components/MetaFlowResponseView';
import EFormViewerBody from '../components/EFormViewerBody';

const workflowMonitorLeafletIcon = L.icon({
  iconUrl: leafletMarkerIcon,
  iconRetinaUrl: leafletMarkerIcon2x,
  shadowUrl: leafletMarkerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = workflowMonitorLeafletIcon;

// dayjs.extend(duration); // 已替換為 TimezoneUtils

const { Header, Content } = Layout;
const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const WORKFLOW_MONITOR_STORAGE_KEY = 'workflowMonitorState';

const DEFAULT_MONITOR_FILTERS = {
  status: 'all',
  workflowDefinitionId: null,
  startDateRange: null,
  endDateRange: null,
  searchText: ''
};

const WORKFLOW_MONITOR_MAIN_TABS = {
  activityBoard: 'activityBoard',
  instanceList: 'instanceList'
};
const HORIZONTAL_BOARD_REFRESH_SECONDS = 5;

// 監控頁 debug log 開關：這些 log 位於 render / polling 熱路徑，
// 每輪會對每個 step 輸出十多行並保留整個物件參照，開著 DevTools 時明顯拖慢頁面，預設關閉。
const WORKFLOW_MONITOR_DEBUG = false;
const wfmDebugLog = (...args) => {
  if (WORKFLOW_MONITOR_DEBUG) console.log(...args);
};

const parseWorkflowMonitorJson = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const toArray = (value) => Array.isArray(value) ? value : [];

const normalizeArtifactUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (/^https?:\/\//i.test(url)) return url;
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return encodeUploadUrlFilename(normalized);
};

const getArtifactDedupKey = (kind, url) => {
  const href = normalizeArtifactUrl(url);
  if (!href) return '';
  try {
    const parsed = new URL(href, window.location.origin);
    return `${kind}:${parsed.pathname}${parsed.search}${parsed.hash}`.toLowerCase();
  } catch (_) {
    return `${kind}:${String(href).toLowerCase()}`;
  }
};

const getWorkflowMonitorPreviewUrl = (kind, url) => {
  const href = normalizeArtifactUrl(url);
  if (!href) return href;
  if (kind !== 'report' && kind !== 'doc') return href;

  try {
    const parsed = new URL(href, window.location.origin);
    const reportMatch = parsed.pathname.match(/^\/report-instance\/([^/?#]+)/i);
    if (kind === 'report' && reportMatch?.[1]) {
      return `/preview/report-instance/${reportMatch[1]}${parsed.search}${parsed.hash}`;
    }

    const docMatch = parsed.pathname.match(/^\/doc-instance\/([^/?#]+)/i);
    if (kind === 'doc' && docMatch?.[1]) {
      return `/preview/doc-instance/${docMatch[1]}${parsed.search}${parsed.hash}`;
    }
  } catch (_) {
    // Keep the original URL if it is not parseable.
  }

  return href;
};

const safeDecodeText = (value) => {
  if (value == null) return '';
  const text = String(value);
  try {
    return decodeURIComponent(text);
  } catch (_) {
    return text;
  }
};

const getDisplayFileName = (...candidates) => {
  const raw = candidates.find(value => value != null && String(value).trim());
  if (raw == null) return '';
  const normalized = String(raw).replace(/\\/g, '/');
  const fileName = normalized.split('/').pop() || normalized;
  return safeDecodeText(fileName);
};

const getArtifactPreviewType = (url, kind) => {
  if (kind === 'report' || kind === 'eform') return 'iframe';
  const cleanUrl = String(url || '').split('?')[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(cleanUrl)) return 'image';
  if (/\.(mp4|webm|ogg|mov|m4v)$/.test(cleanUrl)) return 'video';
  if (/\.(mp3|wav|ogg|m4a|aac|flac)$/.test(cleanUrl)) return 'audio';
  return 'iframe';
};

const readWorkflowMonitorStorageRaw = () => {
  try {
    let raw = localStorage.getItem(WORKFLOW_MONITOR_STORAGE_KEY);
    if (!raw) {
      raw = sessionStorage.getItem(WORKFLOW_MONITOR_STORAGE_KEY);
      if (raw) {
        localStorage.setItem(WORKFLOW_MONITOR_STORAGE_KEY, raw);
        sessionStorage.removeItem(WORKFLOW_MONITOR_STORAGE_KEY);
      }
    }
    return raw;
  } catch (_) {
    return null;
  }
};

/** 從 localStorage 還原監控頁的篩選、分頁、排序（離開後返回時保留） */
function getInitialMonitorState() {
  try {
    const raw = readWorkflowMonitorStorageRaw();
    if (!raw) return null;
    const data = JSON.parse(raw);
    const filters = data.filters;
    if (!filters) return null;
    const toDayjsRange = (arr) => {
      if (!Array.isArray(arr) || arr.length !== 2) return null;
      const a = arr[0] && dayjs(arr[0]).isValid() ? dayjs(arr[0]) : null;
      const b = arr[1] && dayjs(arr[1]).isValid() ? dayjs(arr[1]) : null;
      return a && b ? [a, b] : null;
    };
    return {
      filters: {
        status: typeof filters.status === 'string' ? filters.status : 'all',
        workflowDefinitionId: filters.workflowDefinitionId != null && filters.workflowDefinitionId !== ''
          ? Number(filters.workflowDefinitionId)
          : null,
        startDateRange: toDayjsRange(filters.startDateRange),
        endDateRange: toDayjsRange(filters.endDateRange),
        searchText: typeof filters.searchText === 'string' ? filters.searchText : ''
      },
      pagination: data.pagination && typeof data.pagination.current === 'number' && typeof data.pagination.pageSize === 'number'
        ? { current: data.pagination.current, pageSize: data.pagination.pageSize, total: 0 }
        : null,
      tableSorter: data.tableSorter && typeof data.tableSorter.sortBy === 'string' && typeof data.tableSorter.sortOrder === 'string'
        ? { sortBy: data.tableSorter.sortBy, sortOrder: data.tableSorter.sortOrder }
        : null
    };
  } catch (_) {
    return null;
  }
}

/** 將篩選狀態寫入 localStorage（日期範圍存為 ISO 字串） */
function persistMonitorState(filters, pagination, tableSorter) {
  try {
    const toIsoRange = (range) => {
      if (!range || !Array.isArray(range) || range.length !== 2) return null;
      const a = range[0] && (range[0].toISOString ? range[0].toISOString() : dayjs(range[0]).toISOString());
      const b = range[1] && (range[1].toISOString ? range[1].toISOString() : dayjs(range[1]).toISOString());
      return a && b ? [a, b] : null;
    };
    localStorage.setItem(WORKFLOW_MONITOR_STORAGE_KEY, JSON.stringify({
      filters: {
        ...filters,
        startDateRange: toIsoRange(filters.startDateRange),
        endDateRange: toIsoRange(filters.endDateRange)
      },
      pagination: pagination ? { current: pagination.current, pageSize: pagination.pageSize } : null,
      tableSorter: tableSorter || null
    }));
  } catch (_) {}
}

/** 對 /Uploads/.../filename 的檔名做編碼（先 decode 再 encode 避免重複編碼），避免 ' 等字元在連結中導致 403 或路徑被截斷 */
function encodeUploadUrlFilename(url) {
  if (!url || typeof url !== 'string') return url;
  const s = url.replace(/\\/g, '/').replace(/^\/+/, '');
  const segs = s.split('/');
  if (segs.length < 2) return url;
  let last = segs[segs.length - 1];
  try {
    last = decodeURIComponent(last);
  } catch (_) {}
  segs[segs.length - 1] = encodeURIComponent(last);
  const path = segs.join('/');
  return url.startsWith('/') ? `/${path}` : path;
}

// ResizableTitle 元件
const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;
  if (!width) return <th {...restProps} />;
  return (
    <Resizable
      width={width}
      height={0}
      minConstraints={[30, 0]}
      handle={
        <span
          style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '8px', cursor: 'col-resize', zIndex: 1, userSelect: 'none' }}
          onClick={e => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} style={{ position: 'relative' }} />
    </Resizable>
  );
};

/**
 * 將 JSON 數據轉換為 HTML 表格
 */
/**
 * 提取 AI 分析結果，排除 base64 數據，轉換為 HTML 表格
 * 只處理 JSON 格式且包含 base64 的情況，純文字消息不受影響
 */
const formatParsedJsonForDisplay = (parsed) => {
  const hasRawField = parsed && typeof parsed === 'object' && 'raw' in parsed;
  const rawValue = hasRawField ? parsed.raw : null;
  const hasBase64 = rawValue && typeof rawValue === 'string' &&
    (rawValue.length > 1000 || rawValue.includes('base64') ||
     /^[A-Za-z0-9+/=]+$/.test(rawValue.substring(0, 100)));

  if (hasBase64) {
    const cleaned = { ...parsed };
    delete cleaned.raw;
    if (Object.keys(cleaned).length > 0) {
      return convertJsonToHtmlTable(cleaned);
    }
    return '[圖片消息 - AI 分析結果已用於填充表單]';
  }

  return convertJsonToHtmlTable(parsed);
};

const findJsonBlocksInText = (text) => {
  const blocks = [];
  let i = 0;

  while (i < text.length) {
    const ch = text[i];
    if (ch !== '{' && ch !== '[') {
      i += 1;
      continue;
    }

    const stack = [ch];
    let inString = false;
    let escaped = false;
    let foundEnd = -1;

    for (let j = i + 1; j < text.length; j += 1) {
      const c = text[j];
      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (c === '\\') {
          escaped = true;
        } else if (c === '"') {
          inString = false;
        }
        continue;
      }

      if (c === '"') {
        inString = true;
        continue;
      }

      if (c === '{' || c === '[') {
        stack.push(c);
        continue;
      }

      if (c === '}' || c === ']') {
        const top = stack[stack.length - 1];
        const isMatched = (top === '{' && c === '}') || (top === '[' && c === ']');
        if (!isMatched) {
          break;
        }

        stack.pop();
        if (stack.length === 0) {
          foundEnd = j;
          break;
        }
      }
    }

    if (foundEnd === -1) {
      i += 1;
      continue;
    }

    const raw = text.slice(i, foundEnd + 1);
    blocks.push({ start: i, end: foundEnd + 1, raw });
    i = foundEnd + 1;
  }

  return blocks;
};

const extractAiAnalysisResult = (userMessage) => {
  if (!userMessage || typeof userMessage !== 'string') {
    return userMessage;
  }

  const trimmed = userMessage.trim();

  // 1) 純 JSON 訊息：保持原有行為
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return formatParsedJsonForDisplay(parsed);
      }
      return userMessage;
    } catch (e) {
      if (userMessage.length > 10000 && /^[A-Za-z0-9+/=\s]+$/.test(userMessage.substring(0, 100))) {
        return '[圖片消息 - AI 分析結果已用於填充表單]';
      }
      // 非純 JSON，繼續走「混合文本 + JSON」流程
    }
  }

  // 2) 混合文本訊息：把內嵌 JSON 區塊轉成表格，其餘文字保留
  const blocks = findJsonBlocksInText(userMessage);
  if (blocks.length === 0) {
    return userMessage;
  }

  let cursor = 0;
  const htmlParts = [];
  let hasFormattedJson = false;

  blocks.forEach((block) => {
    if (block.start > cursor) {
      const plainText = userMessage.slice(cursor, block.start);
      htmlParts.push(escapeHtml(plainText).replace(/\n/g, '<br/>'));
    }

    try {
      const parsed = JSON.parse(block.raw);
      if (parsed && typeof parsed === 'object') {
        const formatted = formatParsedJsonForDisplay(parsed);
        if (typeof formatted === 'string' && formatted.includes('<table')) {
          htmlParts.push(formatted);
        } else {
          htmlParts.push(escapeHtml(String(formatted)).replace(/\n/g, '<br/>'));
        }
        hasFormattedJson = true;
      } else {
        htmlParts.push(escapeHtml(block.raw).replace(/\n/g, '<br/>'));
      }
    } catch (e) {
      htmlParts.push(escapeHtml(block.raw).replace(/\n/g, '<br/>'));
    }

    cursor = block.end;
  });

  if (cursor < userMessage.length) {
    htmlParts.push(escapeHtml(userMessage.slice(cursor)).replace(/\n/g, '<br/>'));
  }

  if (hasFormattedJson) {
    return htmlParts.join('');
  }

  return userMessage;
};

const WorkflowMonitorPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [instances, setInstances] = useState([]);
  const [selectedInstances, setSelectedInstances] = useState([]);
  const [pagination, setPagination] = useState(() => {
    const s = getInitialMonitorState();
    if (s && s.pagination) return { ...s.pagination, total: 0 };
    return { current: 1, pageSize: 20, total: 0 };
  });
  const [tableSorter, setTableSorter] = useState(() => {
    const s = getInitialMonitorState();
    if (s && s.tableSorter) return s.tableSorter;
    return { sortBy: 'startedAt', sortOrder: 'desc' };
  });
  const [filters, setFilters] = useState(() => {
    const s = getInitialMonitorState();
    if (s && s.filters) return s.filters;
    return { ...DEFAULT_MONITOR_FILTERS };
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [autoRefreshModalVisible, setAutoRefreshModalVisible] = useState(false);
  const [modalAutoRefreshEnabled, setModalAutoRefreshEnabled] = useState(autoRefresh);
  const [modalRefreshInterval, setModalRefreshInterval] = useState(refreshInterval);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [dataSetQueryModalVisible, setDataSetQueryModalVisible] = useState(false);
  const [dataSetQueryResult, setDataSetQueryResult] = useState(null);
  const [statistics, setStatistics] = useState({
    total: 0,
    running: 0,
    completed: 0,
    failed: 0,
    waiting: 0,
    averageExecutionTime: 0,
    successRate: 0
  });
  const [userInterfaces, setUserInterfaces] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [messageSendModalVisible, setMessageSendModalVisible] = useState(false);
  const [selectedMessageSend, setSelectedMessageSend] = useState(null);
  const [messageSendDetailModalVisible, setMessageSendDetailModalVisible] = useState(false);
  const [selectedMessageSendDetail, setSelectedMessageSendDetail] = useState(null);
  const [newMessageSendStatusModalVisible, setNewMessageSendStatusModalVisible] = useState(false);
  const [selectedMessageSendId, setSelectedMessageSendId] = useState(null);
  const [selectedWorkflowExecutionId, setSelectedWorkflowExecutionId] = useState(null);
  const [selectedWorkflowStepExecutionId, setSelectedWorkflowStepExecutionId] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [userTimezoneOffset, setUserTimezoneOffset] = useState('UTC+8'); // 默認香港時區
  /** 單筆刪除時若為 Email 觸發，顯示「是否允許該郵件再次觸發」的 Modal */
  const [emailDeleteModalVisible, setEmailDeleteModalVisible] = useState(false);
  const [emailDeleteInstance, setEmailDeleteInstance] = useState(null);
  /** 批次刪除時若含 Email 觸發，顯示「是否允許這些郵件再次觸發」的 Modal */
  const [batchDeleteEmailChoiceModalVisible, setBatchDeleteEmailChoiceModalVisible] = useState(false);
  /** 刪除實例（單筆或批次）前，詢問是否同時刪除相關檔案（圖片/影片/文件等）／是否先下載備份 */
  const [fileDeleteChoiceModalVisible, setFileDeleteChoiceModalVisible] = useState(false);
  const [fileDeleteChoiceTarget, setFileDeleteChoiceTarget] = useState(null); // 單筆刪除時的 instance
  const [fileDeleteChoiceIsBatch, setFileDeleteChoiceIsBatch] = useState(false);
  const [fileDeleteBackupLoading, setFileDeleteBackupLoading] = useState(false);
  /** 進入 Email 觸發確認 Modal 前，先記住使用者在「是否刪除檔案」步驟的選擇，待 Email Modal 選擇後一併帶入刪除請求 */
  const emailDeletePendingFilesRef = useRef(false);
  
  // 使用 ref 存儲最新的 filters 和 pagination，確保自動刷新時使用最新值
  const filtersRef = useRef(filters);
  const paginationRef = useRef(pagination);
  const tableSorterRef = useRef(tableSorter);
  filtersRef.current = filters;
  paginationRef.current = pagination;
  tableSorterRef.current = tableSorter;

  // 離開頁面後再返回時保留篩選、分頁、排序（寫入 localStorage）
  useEffect(() => {
    persistMonitorState(filters, pagination, tableSorter);
  }, [filters, pagination, tableSorter]);
  
  // 右側詳情面板狀態
  const [detailPanelVisible, setDetailPanelVisible] = useState(false);
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState(null);
  /** 左側列表與右側 Instance Details 的寬度比例 (0.25–0.75)，預設 60% 左 / 40% 右 */
  const [detailSplitRatio, setDetailSplitRatio] = useState(0.6);
  const detailSplitContainerRef = useRef(null);
  const detailScrollRef = useRef(null);
  const instanceTableWrapRef = useRef(null);
  const instanceTableResizeObserverRef = useRef(null);
  const [instanceTableScrollY, setInstanceTableScrollY] = useState(undefined);

  const INSTANCE_LIST_TABLE_HEAD_HEIGHT = 55;
  const measureInstanceTableScrollY = useCallback(() => {
    const el = instanceTableWrapRef.current;
    if (!el) return;
    const h = el.clientHeight;
    if (h > 0) {
      setInstanceTableScrollY(Math.max(120, h - INSTANCE_LIST_TABLE_HEAD_HEIGHT));
    }
  }, []);

  const scheduleInstanceTableScrollYMeasure = useCallback(() => {
    measureInstanceTableScrollY();
    requestAnimationFrame(measureInstanceTableScrollY);
    requestAnimationFrame(() => requestAnimationFrame(measureInstanceTableScrollY));
  }, [measureInstanceTableScrollY]);

  const bindInstanceTableWrapRef = useCallback((el) => {
    if (instanceTableResizeObserverRef.current) {
      instanceTableResizeObserverRef.current.disconnect();
      instanceTableResizeObserverRef.current = null;
    }
    instanceTableWrapRef.current = el;
    if (!el) return;

    scheduleInstanceTableScrollYMeasure();
    const ro = new ResizeObserver(() => scheduleInstanceTableScrollYMeasure());
    ro.observe(el);
    instanceTableResizeObserverRef.current = ro;
  }, [scheduleInstanceTableScrollYMeasure]);
  
  // 表格列寬調整相關狀態
  const [columnWidths, setColumnWidths] = useState({});
  
  // 內嵌表單相關狀態
  const [selectedFormInstanceId, setSelectedFormInstanceId] = useState(null);
  const [embedFormVisible, setEmbedFormVisible] = useState(false);
  const [embeddedFormInstance, setEmbeddedFormInstance] = useState(null);
  const [loadingEmbeddedForm, setLoadingEmbeddedForm] = useState(false);
  const [embedFormIframeSrc, setEmbedFormIframeSrc] = useState('');
  const [embedFormIframeLoaded, setEmbedFormIframeLoaded] = useState(0);
  const embedFormIframeRef = useRef(null);
  const [embedFormRelatedAttachments, setEmbedFormRelatedAttachments] = useState([]);
  const [embedFormLoadingAttachments, setEmbedFormLoadingAttachments] = useState(false);
  // 嵌入表單儲存／審批狀態（橫向看板開啟 HTML eForm 時的 Save / Approve / Reject 按鈕）
  const [embedFormSaving, setEmbedFormSaving] = useState(false);
  const [embedFormApproving, setEmbedFormApproving] = useState(false);
  const [embedFormRejecting, setEmbedFormRejecting] = useState(false);
  const [embedFormExportingPdf, setEmbedFormExportingPdf] = useState(false);
  const [embeddedArtifactPreview, setEmbeddedArtifactPreview] = useState({
    open: false,
    url: '',
    title: '',
    type: 'iframe',
    kind: ''
  });
  const reportIframeRef = useRef(null);
  const [copyingReport, setCopyingReport] = useState(false);
  const [activeMonitorTab, setActiveMonitorTab] = useState(WORKFLOW_MONITOR_MAIN_TABS.activityBoard);
  const [horizontalBoardLoading, setHorizontalBoardLoading] = useState(true);
  const [horizontalBoardSteps, setHorizontalBoardSteps] = useState([]);
  const [horizontalBoardArtifacts, setHorizontalBoardArtifacts] = useState({});
  const [horizontalBoardWorkflowDefinitions, setHorizontalBoardWorkflowDefinitions] = useState([]);
  const [workflowDefinitionCounts, setWorkflowDefinitionCounts] = useState({ total: 0, byId: {} });
  const [loadingWorkflowDefinitionCounts, setLoadingWorkflowDefinitionCounts] = useState(false);
  const [horizontalBoardDefinitionCounts, setHorizontalBoardDefinitionCounts] = useState({ total: 0, byId: {} });
  const [horizontalBoardWorkflowId, setHorizontalBoardWorkflowId] = useState(null);
  const [horizontalBoardDateRange, setHorizontalBoardDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);
  const [horizontalBoardEformApprovalLoading, setHorizontalBoardEformApprovalLoading] = useState({});
  const [horizontalBoardTriggerSourceModal, setHorizontalBoardTriggerSourceModal] = useState({ open: false, instance: null, loading: false });
  // PV 功能：definitions（帶 showInInstanceList）、各 execution 的 PV 值、篩選條件
  // horizontalBoardPVDefinitions: 單一 workflow 選中時用於 filter bar
  const [horizontalBoardPVDefinitions, setHorizontalBoardPVDefinitions] = useState([]);
  // horizontalBoardPVDefinitionsMap: 所有 workflow 的 PV defs，key = workflowDefinitionId，供 All 模式下每個 row 各自顯示
  const [horizontalBoardPVDefinitionsMap, setHorizontalBoardPVDefinitionsMap] = useState({});
  const [horizontalBoardPVValues, setHorizontalBoardPVValues] = useState({});
  const [horizontalBoardPVFilters, setHorizontalBoardPVFilters] = useState({});
  const horizontalBoardIntervalRef = useRef(null);
  const horizontalBoardTimeoutRef = useRef(null);
  const horizontalBoardPreviousStepsRef = useRef([]);
  const horizontalBoardInitialLoadRef = useRef(true);
  // 效能優化：記錄各 executionId 的 step 簽名，用於判斷 artifacts 是否需要重抓
  const horizontalBoardExecutionSignatureRef = useRef(new Map());
  // 已抓過 artifacts、且該 execution 已終結的 id 集合，不再重抓
  const horizontalBoardArtifactsLockedRef = useRef(new Set());
  // polling 互斥，避免前一輪未結束時又被 setInterval 觸發
  const horizontalBoardFetchingRef = useRef(false);
  // 換 tab / 換 filter 時 abort 舊的 artifact 請求
  const horizontalBoardAbortRef = useRef(null);

  // 聊天類節點對話檢視彈窗（橫向看板使用，支援 chatBot 與 chatOrderingCart）
  const [chatBotViewer, setChatBotViewer] = useState({
    open: false,
    stepExecutionId: null,
    taskName: '',
    nodeKind: 'chatBot'
  });

  // 載入真實數據
  // 獲取用戶時區信息
  useEffect(() => {
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      try {
        const parsedUserInfo = JSON.parse(userInfo);
        if (parsedUserInfo.timezone) {
          setUserTimezoneOffset(parsedUserInfo.timezone);
        }
      } catch (error) {
        console.error('解析用戶信息失敗:', error);
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadInterfaces = async () => {
      setLoadingPermissions(true);
      try {
        // 強制從 API 獲取最新權限，不使用緩存
        const interfaces = await getUserInterfacesFromStorage(true);
        console.log('[WorkflowMonitor] 從 API 獲取的權限列表:', interfaces);
        if (isMounted) {
          setUserInterfaces(interfaces || []);
        }
      } catch (error) {
        console.error('[WorkflowMonitor] 載入用戶介面權限失敗:', error);
        if (isMounted) {
          setUserInterfaces([]);
        }
      } finally {
        if (isMounted) {
          setLoadingPermissions(false);
        }
      }
    };

    loadInterfaces();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    loadInstances();
    loadStatistics();
    
    if (autoRefresh) {
      const interval = setInterval(() => {
        // 自動刷新時使用 ref 的值，確保使用最新的 filters、pagination 和 sort
        const { sortBy, sortOrder } = tableSorterRef.current;
        loadInstances(sortBy, sortOrder, true);
        loadStatistics();
      }, refreshInterval * 1000);
      
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  const canPauseExecution = !loadingPermissions && hasInterfacePermission(userInterfaces, 'workflowMonitor.pause');
  const canResumeExecution = !loadingPermissions && hasInterfacePermission(userInterfaces, 'workflowMonitor.resume');
  const canRetryExecution = !loadingPermissions && hasInterfacePermission(userInterfaces, 'workflowMonitor.retry');
  const canCancelExecution = !loadingPermissions && hasInterfacePermission(userInterfaces, 'workflowMonitor.cancel');
  const canDeleteExecution = !loadingPermissions && hasInterfacePermission(userInterfaces, 'workflowMonitor.delete');

  wfmDebugLog('[WorkflowMonitor] permission flags', {
    loadingPermissions,
    userInterfaces,
    canPauseExecution,
    canResumeExecution,
    canRetryExecution,
    canCancelExecution,
    canDeleteExecution
  });

  useEffect(() => {
    if (autoRefreshModalVisible) {
      setModalAutoRefreshEnabled(autoRefresh);
      setModalRefreshInterval(refreshInterval);
    }
  }, [autoRefreshModalVisible, autoRefresh, refreshInterval]);

  const loadHorizontalBoardPVDefinitions = useCallback(async (workflowDefinitionId) => {
    if (!workflowDefinitionId) {
      setHorizontalBoardPVDefinitions([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const resp = await fetch(`/api/processvariables/definitions?workflowDefinitionId=${workflowDefinitionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setHorizontalBoardPVDefinitions(data.data || []);
    } catch (e) {
      console.warn('[WorkflowMonitor] 載入 PV 定義失敗:', e);
    }
  }, []);

  // 載入所有 workflow definitions 的 PV defs，建立 map 供 All 模式下各 row 各自顯示
  const loadAllHorizontalBoardPVDefinitions = useCallback(async (workflowDefinitions) => {
    if (!Array.isArray(workflowDefinitions) || workflowDefinitions.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const entries = await runWithConcurrency(
        workflowDefinitions.filter(wd => wd.id != null),
        async (wd) => {
          try {
            const resp = await fetch(`/api/processvariables/definitions?workflowDefinitionId=${wd.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!resp.ok) return [wd.id, []];
            const data = await resp.json();
            return [wd.id, data.data || []];
          } catch {
            return [wd.id, []];
          }
        },
        6
      );
      const map = {};
      entries.forEach(entry => {
        if (!entry) return;
        const [id, defs] = entry;
        map[id] = defs;
      });
      setHorizontalBoardPVDefinitionsMap(map);
    } catch (e) {
      console.warn('[WorkflowMonitor] 載入所有 PV 定義失敗:', e);
    }
  }, []);

  const loadHorizontalBoardWorkflowDefinitions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/workflowdefinitions?page=1&pageSize=1000', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHorizontalBoardWorkflowDefinitions(Array.isArray(data.data) ? data.data : []);
      }
    } catch (error) {
      console.error('[WorkflowMonitor] 載入橫向看板流程定義失敗:', error);
    }
  };

  // 限制同時進行的 artifact fan-out，避免 browser 6-socket-per-host 塞爆。
  const runWithConcurrency = async (items, worker, limit = 6) => {
    const results = new Array(items.length);
    let cursor = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (cursor < items.length) {
        const idx = cursor++;
        try {
          results[idx] = await worker(items[idx], idx);
        } catch (err) {
          results[idx] = null;
        }
      }
    });
    await Promise.all(runners);
    return results;
  };

  const loadHorizontalBoardArtifacts = async (workflowExecutionIds, signal) => {
    const ids = [...new Set(workflowExecutionIds.filter(id => id !== null && id !== undefined))];
    if (ids.length === 0) return;

    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

    const artifactEntries = await runWithConcurrency(ids, async (executionId) => {
      if (signal?.aborted) return null;
      try {
        const opts = { headers, signal };
        const [mediaRes, eformRes, reportRes, docRes, pvRes] = await Promise.all([
          fetch(`/api/workflowexecutions/${executionId}/media-files`, opts),
          fetch(`/api/workflowexecutions/${executionId}/eform-instances`, opts),
          fetch(`/api/workflowexecutions/${executionId}/report-instances`, opts),
          fetch(`/api/workflowexecutions/${executionId}/doc-instances`, opts),
          fetch(`/api/processvariables/instance-values/${executionId}`, opts)
        ]);

        const [mediaData, eformData, reportData, docData, pvData] = await Promise.all([
          mediaRes.ok ? mediaRes.json() : Promise.resolve({ data: [] }),
          eformRes.ok ? eformRes.json() : Promise.resolve([]),
          reportRes.ok ? reportRes.json() : Promise.resolve({ data: [] }),
          docRes.ok ? docRes.json() : Promise.resolve({ data: [] }),
          pvRes.ok ? pvRes.json() : Promise.resolve({ data: [] })
        ]);

        return [
          executionId,
          {
            mediaFiles: toArray(mediaData.data),
            eformInstances: Array.isArray(eformData) ? eformData : toArray(eformData.data),
            reportInstances: toArray(reportData.data),
            docInstances: toArray(docData.data),
            pvValues: toArray(pvData.data)
          }
        ];
      } catch (error) {
        if (error?.name === 'AbortError') return null;
        console.error('[WorkflowMonitor] 載入橫向看板產物失敗:', executionId, error);
        return [executionId, { mediaFiles: [], eformInstances: [], reportInstances: [], docInstances: [], pvValues: [] }];
      }
    }, 6);

    if (signal?.aborted) return;

    setHorizontalBoardArtifacts(prev => {
      const next = { ...prev };
      artifactEntries.forEach(entry => {
        if (!entry) return;
        const [executionId, artifacts] = entry;
        next[executionId] = artifacts;
      });
      return next;
    });

    setHorizontalBoardPVValues(prev => {
      const next = { ...prev };
      artifactEntries.forEach(entry => {
        if (!entry) return;
        const [executionId, artifacts] = entry;
        if (Array.isArray(artifacts.pvValues)) {
          next[executionId] = artifacts.pvValues;
        }
      });
      return next;
    });
  };

  // 判斷 execution 是否已經終結（所有 step 都為 Completed / Failed / Cancelled）
  const isExecutionTerminal = (steps) => {
    if (!Array.isArray(steps) || steps.length === 0) return false;
    return steps.every(s => {
      const status = String(s?.status || '').toLowerCase();
      return status === 'completed' || status === 'failed' || status === 'cancelled' || status === 'canceled';
    });
  };

  // 計算某 execution 的 step 輕量簽名（不納入 outputJson 字串整段，顯著降低 stringify 負擔）
  const buildExecutionSignature = (steps) => {
    return steps
      .map(s => `${s.id}|${s.status}|${s.isWaiting ? 1 : 0}|${s.endedAt || ''}|${s.messageValidations?.length || 0}|${s.outputJson ? s.outputJson.length : 0}`)
      .join(';');
  };

  const loadHorizontalBoardSteps = async (silent = false) => {
    // polling 互斥：前一輪未完成時不重入
    if (horizontalBoardFetchingRef.current) return;
    horizontalBoardFetchingRef.current = true;

    if (!silent && horizontalBoardInitialLoadRef.current) {
      setHorizontalBoardLoading(true);
    }

    // 換 tab / filter 會呼叫此函數並開啟新 AbortController，舊的可直接丟棄
    const abortController = horizontalBoardAbortRef.current || new AbortController();

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setHorizontalBoardLoading(false);
        return;
      }

      const params = new URLSearchParams();
      if (horizontalBoardWorkflowId) {
        params.append('workflowDefinitionId', horizontalBoardWorkflowId);
      }
      if (horizontalBoardDateRange?.[0] && horizontalBoardDateRange?.[1]) {
        params.append('startDate', horizontalBoardDateRange[0].startOf('day').toISOString());
        params.append('endDate', horizontalBoardDateRange[1].endOf('day').toISOString());
      }

      const response = await fetch(`/api/workflowexecutions/steps/monitor${params.toString() ? `?${params.toString()}` : ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const steps = Array.isArray(data.data) ? data.data : [];

      // 以 executionId 分群，計算每個 execution 的輕量簽名，找出「真的有變化」的 execution
      const stepsByExecution = new Map();
      steps.forEach(s => {
        const exeId = s.workflowExecutionId;
        if (exeId == null) return;
        const arr = stepsByExecution.get(exeId) || [];
        arr.push(s);
        stepsByExecution.set(exeId, arr);
      });

      const prevSignatureMap = horizontalBoardExecutionSignatureRef.current;
      const nextSignatureMap = new Map();
      const changedExecutionIds = [];
      const currentExecutionIdSet = new Set();

      stepsByExecution.forEach((exeSteps, exeId) => {
        currentExecutionIdSet.add(exeId);
        const nextSig = buildExecutionSignature(exeSteps);
        nextSignatureMap.set(exeId, nextSig);
        const prevSig = prevSignatureMap.get(exeId);
        const locked = horizontalBoardArtifactsLockedRef.current.has(exeId);
        // 已鎖（終結+已抓過）且簽名未變 → 完全不動
        if (locked && prevSig === nextSig) return;
        // 初次看到 / 有異動 → 需重抓 artifacts
        if (horizontalBoardInitialLoadRef.current || prevSig !== nextSig) {
          changedExecutionIds.push(exeId);
        }
      });

      // 過期 execution（本次 response 不再出現）→ 釋放 signature / lock，同時可考慮釋放 artifacts 記憶體
      let shouldPruneArtifacts = false;
      prevSignatureMap.forEach((_, exeId) => {
        if (!currentExecutionIdSet.has(exeId)) {
          horizontalBoardArtifactsLockedRef.current.delete(exeId);
          shouldPruneArtifacts = true;
        }
      });
      horizontalBoardExecutionSignatureRef.current = nextSignatureMap;

      // 只在 steps 整體有任一變動時 setState（避免無謂重繪）
      const totalSignatureChanged =
        prevSignatureMap.size !== nextSignatureMap.size ||
        changedExecutionIds.length > 0 ||
        shouldPruneArtifacts;

      if (horizontalBoardInitialLoadRef.current || totalSignatureChanged) {
        horizontalBoardPreviousStepsRef.current = steps;
        setHorizontalBoardSteps(steps);
      }

      if (shouldPruneArtifacts) {
        setHorizontalBoardArtifacts(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(key => {
            // key 是字串，需要與 executionId（可能是 number 或 string）比對
            const keyMatch = currentExecutionIdSet.has(key) ||
              currentExecutionIdSet.has(Number(key)) ||
              currentExecutionIdSet.has(String(key));
            if (!keyMatch) delete next[key];
          });
          return next;
        });
        setHorizontalBoardPVValues(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(key => {
            const keyMatch = currentExecutionIdSet.has(key) ||
              currentExecutionIdSet.has(Number(key)) ||
              currentExecutionIdSet.has(String(key));
            if (!keyMatch) delete next[key];
          });
          return next;
        });
      }

      horizontalBoardInitialLoadRef.current = false;
      setHorizontalBoardLoading(false);

      if (changedExecutionIds.length > 0) {
        await loadHorizontalBoardArtifacts(changedExecutionIds, abortController.signal);
        // 抓完後，若該 execution 已終結，記入 lock 不再重抓
        changedExecutionIds.forEach(exeId => {
          const exeSteps = stepsByExecution.get(exeId) || [];
          if (isExecutionTerminal(exeSteps)) {
            horizontalBoardArtifactsLockedRef.current.add(exeId);
          }
        });
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        setHorizontalBoardLoading(false);
        return;
      }
      console.error('[WorkflowMonitor] 載入橫向看板步驟失敗:', error);
      setHorizontalBoardLoading(false);
      if (!silent) {
        message.error(t('workflowMonitor.loadHorizontalBoardFailed') || '載入橫向看板失敗');
      }
    } finally {
      horizontalBoardFetchingRef.current = false;
    }
  };

  useEffect(() => {
    loadHorizontalBoardWorkflowDefinitions();
  }, []);

  // workflow definitions 載入完成後，批量載入所有 workflow 的 PV defs（供 All 模式顯示）
  useEffect(() => {
    if (horizontalBoardWorkflowDefinitions.length > 0) {
      loadAllHorizontalBoardPVDefinitions(horizontalBoardWorkflowDefinitions);
    }
  }, [horizontalBoardWorkflowDefinitions, loadAllHorizontalBoardPVDefinitions]);

  useEffect(() => {
    if (activeMonitorTab !== WORKFLOW_MONITOR_MAIN_TABS.activityBoard) return undefined;

    // 切 tab / 換 filter：重置快取 + abort 舊請求
    horizontalBoardInitialLoadRef.current = true;
    horizontalBoardExecutionSignatureRef.current = new Map();
    horizontalBoardArtifactsLockedRef.current = new Set();
    if (horizontalBoardAbortRef.current) {
      try { horizontalBoardAbortRef.current.abort(); } catch (_) {}
    }
    horizontalBoardAbortRef.current = new AbortController();

    let cancelled = false;
    const schedule = () => {
      if (cancelled) return;
      horizontalBoardTimeoutRef.current = setTimeout(async () => {
        if (cancelled) return;
        await loadHorizontalBoardSteps(true);
        schedule();
      }, HORIZONTAL_BOARD_REFRESH_SECONDS * 1000);
    };

    (async () => {
      await loadHorizontalBoardSteps(false);
      schedule();
    })();

    return () => {
      cancelled = true;
      if (horizontalBoardTimeoutRef.current) {
        clearTimeout(horizontalBoardTimeoutRef.current);
        horizontalBoardTimeoutRef.current = null;
      }
      if (horizontalBoardIntervalRef.current) {
        clearInterval(horizontalBoardIntervalRef.current);
        horizontalBoardIntervalRef.current = null;
      }
      if (horizontalBoardAbortRef.current) {
        try { horizontalBoardAbortRef.current.abort(); } catch (_) {}
        horizontalBoardAbortRef.current = null;
      }
      horizontalBoardFetchingRef.current = false;
    };
    // loadHorizontalBoardSteps reads the current filters from state and mirrors the existing polling pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMonitorTab, horizontalBoardWorkflowId, horizontalBoardDateRange]);

  const openEmbeddedArtifactPreview = ({ url, title, kind, type }) => {
    const normalizedUrl = getWorkflowMonitorPreviewUrl(kind, url);
    if (!normalizedUrl) return;
    setEmbeddedArtifactPreview({
      open: true,
      url: normalizedUrl,
      title: safeDecodeText(title || getDisplayFileName(normalizedUrl) || t('workflowMonitor.artifactPreview')),
      type: type || getArtifactPreviewType(normalizedUrl, kind),
      kind: kind || ''
    });
  };

  const closeEmbeddedArtifactPreview = () => {
    setEmbeddedArtifactPreview({
      open: false,
      url: '',
      title: '',
      type: 'iframe',
      kind: ''
    });
  };

  // 遞迴收集文件文字：HTML report 頁面內文常嵌在巢狀 iframe（如 Email 內文），
  // 只讀外層 body.innerText 會漏掉內層內容，需一併走訪同源子 iframe。
  const collectDocumentText = (doc, depth = 0) => {
    if (!doc?.body || depth > 3) return '';
    const parts = [(doc.body.innerText || doc.body.textContent || '').trim()];
    doc.querySelectorAll('iframe').forEach((frame) => {
      try {
        const innerDoc = frame.contentDocument;
        if (innerDoc) {
          const innerText = collectDocumentText(innerDoc, depth + 1);
          if (innerText) parts.push(innerText);
        }
      } catch (_) { /* cross-origin iframe，略過 */ }
    });
    return parts.filter(Boolean).join('\n\n');
  };

  const copyReportContent = useCallback(async () => {
    setCopyingReport(true);
    try {
      // Try same-origin iframe document access first (works when URL is on the same host)
      const doc = reportIframeRef.current?.contentDocument;
      if (doc?.body) {
        const text = collectDocumentText(doc).trim();
        if (text) {
          await navigator.clipboard.writeText(text);
          message.success('內容已複製至剪貼板');
          return;
        }
      }
      // Fallback: fetch the URL and strip HTML tags
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(embeddedArtifactPreview.url, { headers, credentials: 'include' });
      const html = await res.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const text = (tmp.innerText || tmp.textContent || '').trim();
      await navigator.clipboard.writeText(text);
      message.success('內容已複製至剪貼板');
    } catch (e) {
      message.error('複製失敗：' + (e.message || '未知錯誤'));
    } finally {
      setCopyingReport(false);
    }
  }, [embeddedArtifactPreview.url]);

  const getCurrentUsername = () => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (!raw) return 'System';
      const userInfo = JSON.parse(raw);
      return userInfo.name || userInfo.username || userInfo.email || 'System';
    } catch (_) {
      return 'System';
    }
  };

  const isEformApprovalPending = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    return normalized === 'pending' || normalized === 'submitted';
  };

  const handleHorizontalBoardEformApproval = async (formId, action, approvalLabels = null, options = {}) => {
    if (!formId) return false;

    const isReject = action === 'reject';
    const endpoint = `/api/eforminstances/${formId}/${isReject ? 'reject' : 'approve'}`;
    const loadingKey = `${formId}:${action}`;

    setHorizontalBoardEformApprovalLoading(prev => ({ ...prev, [loadingKey]: true }));
    try {
      const body = {
        approvedBy: getCurrentUsername(),
        note: options.note || (isReject ? 'Rejected from workflow activity board' : 'Approved from workflow activity board')
      };
      // 從橫向看板開啟 HTML eForm 後的 Approve / Reject 也會帶上使用者最新填寫的 FilledHtmlCode 一併入庫
      if (options.filledHtmlCode) {
        body.filledHtmlCode = options.filledHtmlCode;
      }
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(body)
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || result.message || response.statusText);
      }

      const actionLabel = isReject ? approvalLabels?.reject : approvalLabels?.approve;
      message.success(result.message || (actionLabel || (isReject ? t('pendingTasks.rejectSuccess') : t('pendingTasks.approveSuccess'))));
      // 清空簽名 + lock，讓下一輪 poll 重新對有異動的 execution 抓 artifacts
      horizontalBoardPreviousStepsRef.current = [];
      horizontalBoardExecutionSignatureRef.current = new Map();
      horizontalBoardArtifactsLockedRef.current = new Set();
      // 不要 await：board 重新載入可能要幾秒，會卡住 modal 關閉與按鈕 loading 解除；
      // 改成 fire-and-forget，背景刷新即可。
      Promise.resolve().then(() => loadHorizontalBoardSteps(false)).catch(() => {});
      return true;
    } catch (error) {
      message.error(error.message || (isReject ? t('pendingTasks.rejectFailed') : t('pendingTasks.approveFailed')));
      return false;
    } finally {
      setHorizontalBoardEformApprovalLoading(prev => {
        const next = { ...prev };
        delete next[loadingKey];
        return next;
      });
    }
  };

  // 從嵌入表單 Modal 觸發 Approve / Reject：先抓 iframe 內最新填寫，再呼叫共用的 approval handler，最後關閉 Modal
  const handleEmbeddedFormApproval = async (action) => {
    if (!selectedFormInstanceId) return;
    const filledHtmlCode = collectFilledHtmlFromEmbeddedIframe();
    const approvalLabels = getEFormApprovalLabels(embeddedFormInstance, t);
    if (action === 'approve') setEmbedFormApproving(true);
    else setEmbedFormRejecting(true);
    try {
      const ok = await handleHorizontalBoardEformApproval(
        selectedFormInstanceId,
        action,
        approvalLabels,
        {
          filledHtmlCode: filledHtmlCode || undefined,
          note: action === 'reject'
            ? 'Rejected from workflow activity board (HTML eForm modal)'
            : 'Approved from workflow activity board (HTML eForm modal)'
        }
      );
      if (ok) {
        handleCloseEmbeddedForm();
      }
    } finally {
      if (action === 'approve') setEmbedFormApproving(false);
      else setEmbedFormRejecting(false);
    }
  };

  // 載入內嵌表單數據
  useEffect(() => {
    if (embedFormVisible && selectedFormInstanceId) {
      loadEmbeddedFormInstance();
    }
  }, [embedFormVisible, selectedFormInstanceId]);

  // 表單預覽：完整 HTML 用 iframe Blob URL 顯示，避免 dangerouslySetInnerHTML 無法渲染 DOCTYPE/head/body
  useEffect(() => {
    if (!embeddedFormInstance) {
      setEmbedFormIframeSrc(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      return;
    }
    const html = embeddedFormInstance.filledHtmlCode || embeddedFormInstance.htmlCode || '';
    if (!html) {
      setEmbedFormIframeSrc(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      return;
    }
    const url = buildFormPreviewBlobUrl(html);
    setEmbedFormIframeSrc(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [embeddedFormInstance]);

  // 當篩選條件改變時，重新載入數據（保持當前排序）
  useEffect(() => {
    loadInstances(tableSorter.sortBy, tableSorter.sortOrder);
  }, [filters, pagination.current, pagination.pageSize]);

  // 實例列表表格高度：依容器動態計算 scroll.y（含延遲重試，避免初次渲染 clientHeight 為 0）
  useEffect(() => {
    if (activeMonitorTab !== WORKFLOW_MONITOR_MAIN_TABS.instanceList) return undefined;

    scheduleInstanceTableScrollYMeasure();
    const t1 = setTimeout(scheduleInstanceTableScrollYMeasure, 0);
    const t2 = setTimeout(scheduleInstanceTableScrollYMeasure, 100);
    const t3 = setTimeout(scheduleInstanceTableScrollYMeasure, 350);

    const onWindowResize = () => scheduleInstanceTableScrollYMeasure();
    window.addEventListener('resize', onWindowResize);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener('resize', onWindowResize);
    };
  }, [
    activeMonitorTab,
    loading,
    instances.length,
    detailPanelVisible,
    detailSplitRatio,
    selectedInstances.length,
    selectedInstanceId,
    pagination.pageSize,
    scheduleInstanceTableScrollYMeasure
  ]);

  // 診斷：量測右側詳情捲動容器與其內容、各層父元素的實際高度，找出高度鏈在哪斷掉
  useEffect(() => {
    if (!detailPanelVisible) return undefined;

    const dump = (tag) => {
      const el = detailScrollRef.current;
      if (!el) {
        console.log(`[ScrollDiag][${tag}] detailScrollRef 為 null`);
        return;
      }
      const rect = el.getBoundingClientRect();
      const cs = window.getComputedStyle(el);
      const chain = [];
      let node = el;
      let depth = 0;
      while (node && depth < 8) {
        const ncs = window.getComputedStyle(node);
        chain.push({
          depth,
          tag: node.tagName,
          cls: (node.className || '').toString().slice(0, 40),
          clientH: node.clientHeight,
          scrollH: node.scrollHeight,
          offsetH: node.offsetHeight,
          styleHeight: ncs.height,
          flex: ncs.flex,
          minHeight: ncs.minHeight,
          overflowY: ncs.overflowY,
          display: ncs.display,
        });
        node = node.parentElement;
        depth += 1;
      }
      console.log(`[ScrollDiag][${tag}] 捲動容器`, {
        clientHeight: el.clientHeight,
        scrollHeight: el.scrollHeight,
        canScroll: el.scrollHeight > el.clientHeight,
        rectHeight: Math.round(rect.height),
        rectTop: Math.round(rect.top),
        rectBottom: Math.round(rect.bottom),
        viewportH: window.innerHeight,
        computedHeight: cs.height,
        computedOverflowY: cs.overflowY,
        computedFlex: cs.flex,
        firstChildScrollH: el.firstElementChild ? el.firstElementChild.scrollHeight : null,
      });
      console.table(chain);
    };

    dump('immediate');
    const t1 = setTimeout(() => dump('t100'), 100);
    const t2 = setTimeout(() => dump('t500'), 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [detailPanelVisible, selectedInstanceId, selectedInstance]);

  const loadInstances = async (sortBy = 'startedAt', sortOrder = 'desc', useRefValues = false) => {
    // 使用 ref 的值（自動刷新時）或當前狀態值（手動刷新時）
    const currentFilters = useRefValues ? filtersRef.current : filters;
    const currentPagination = useRefValues ? paginationRef.current : pagination;
    
    wfmDebugLog('[WorkflowMonitor] start loading instances', { 
      sortBy, 
      sortOrder, 
      useRefValues,
      currentFiltersStatus: currentFilters.status,
      filtersStatus: filters.status,
      filtersRefStatus: filtersRef.current.status
    });
    
    setLoading(true);
    try {
      
      // 構建查詢參數（確保 page、pageSize 有預設值，避免 undefined 導致 API 失敗）
      const params = new URLSearchParams({
        page: currentPagination.current ?? 1,
        pageSize: currentPagination.pageSize ?? 20,
        sortBy: sortBy,
        sortOrder: sortOrder
      });

      // 狀態值映射：將前端的小寫狀態值轉換為後端期望的格式
      // 注意：後端使用精確匹配，所以需要正確的格式
      if (currentFilters.status !== 'all') {
        const statusMap = {
          'running': 'Running',
          'completed': 'Completed',
          'failed': 'Failed',
          'paused': 'Paused',
          'cancelled': 'Cancelled'
        };
        // 如果狀態值在映射中，使用映射值；否則使用首字母大寫的原始值
        const mappedStatus = statusMap[currentFilters.status.toLowerCase()] || 
          (currentFilters.status.charAt(0).toUpperCase() + currentFilters.status.slice(1).toLowerCase());
        params.append('status', mappedStatus);
        wfmDebugLog('[WorkflowMonitor] status filter mapping:', { 
          original: currentFilters.status, 
          mapped: mappedStatus 
        });
      }

      if (currentFilters.searchText) {
        params.append('search', currentFilters.searchText);
      }

      if (currentFilters.workflowDefinitionId) {
        params.append('workflowDefinitionId', String(currentFilters.workflowDefinitionId));
      }

      if (currentFilters.startDateRange && currentFilters.startDateRange.length === 2) {
        params.append('startDateFrom', currentFilters.startDateRange[0].toISOString());
        params.append('startDateTo', currentFilters.startDateRange[1].toISOString());
      }

      if (currentFilters.endDateRange && currentFilters.endDateRange.length === 2) {
        params.append('endDateFrom', currentFilters.endDateRange[0].toISOString());
        params.append('endDateTo', currentFilters.endDateRange[1].toISOString());
      }

      const url = `/api/workflowexecutions/monitor?${params}`;
      wfmDebugLog('[WorkflowMonitor] request url', url);
      wfmDebugLog('[WorkflowMonitor] request params', Object.fromEntries(params));
      wfmDebugLog('[WorkflowMonitor] current pagination', { current: currentPagination.current, pageSize: currentPagination.pageSize });

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('workflowMonitor.loadInstancesFailed'));
      }

      const data = await response.json();
      wfmDebugLog('[WorkflowMonitor] api data', data);
      wfmDebugLog('[WorkflowMonitor] pagination info', { page: data.page, pageSize: data.pageSize, total: data.total });
      
      setInstances(data.data);
      setPagination(prev => ({ 
        ...prev, 
        total: data.total,
        current: data.page,
        pageSize: data.pageSize
      }));
    } catch (error) {
      message.error(t('workflowMonitor.loadInstancesFailed') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await fetch('/api/workflowexecutions/monitor/statistics', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('workflowMonitor.loadStatisticsFailed'));
      }

      const data = await response.json();
      setStatistics(data);
    } catch (error) {
      console.error(t('workflowMonitor.loadStatisticsFailed'), error);
    }
  };

  const buildMonitorDefinitionCountParams = (filterSource, dateRangeOverride) => {
    const params = new URLSearchParams();
    const f = filterSource;
    if (f.status && f.status !== 'all') {
      const statusMap = {
        running: 'Running',
        completed: 'Completed',
        failed: 'Failed',
        paused: 'Paused',
        cancelled: 'Cancelled'
      };
      const mappedStatus = statusMap[f.status?.toLowerCase?.()] ||
        (f.status.charAt(0).toUpperCase() + f.status.slice(1).toLowerCase());
      params.append('status', mappedStatus);
    }
    if (f.searchText) {
      params.append('search', f.searchText);
    }
    const startRange = dateRangeOverride?.startDateRange ?? f.startDateRange;
    const endRange = dateRangeOverride?.endDateRange ?? f.endDateRange;
    if (startRange && startRange.length === 2) {
      params.append('startDateFrom', startRange[0].toISOString());
      params.append('startDateTo', startRange[1].toISOString());
    }
    if (endRange && endRange.length === 2) {
      params.append('endDateFrom', endRange[0].toISOString());
      params.append('endDateTo', endRange[1].toISOString());
    }
    return params;
  };

  const loadWorkflowDefinitionCounts = async () => {
    setLoadingWorkflowDefinitionCounts(true);
    try {
      const params = buildMonitorDefinitionCountParams(filters, null);
      const response = await fetch(`/api/workflowexecutions/monitor/statistics/by-definition?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed to load definition counts');
      const data = await response.json();
      const byId = {};
      (data.items || []).forEach((item) => {
        if (item.workflowDefinitionId != null) {
          byId[item.workflowDefinitionId] = item.count;
        }
      });
      setWorkflowDefinitionCounts({ total: data.total ?? 0, byId });
    } catch (error) {
      console.error('[WorkflowMonitor] loadWorkflowDefinitionCounts failed', error);
      setWorkflowDefinitionCounts({ total: 0, byId: {} });
    } finally {
      setLoadingWorkflowDefinitionCounts(false);
    }
  };

  const loadHorizontalBoardDefinitionCounts = async () => {
    try {
      const params = buildMonitorDefinitionCountParams(
        { status: 'all', searchText: '' },
        {
          startDateRange: horizontalBoardDateRange,
          endDateRange: null
        }
      );
      const response = await fetch(`/api/workflowexecutions/monitor/statistics/by-definition?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      const byId = {};
      (data.items || []).forEach((item) => {
        if (item.workflowDefinitionId != null) {
          byId[item.workflowDefinitionId] = item.count;
        }
      });
      setHorizontalBoardDefinitionCounts({ total: data.total ?? 0, byId });
    } catch (_) {
      setHorizontalBoardDefinitionCounts({ total: 0, byId: {} });
    }
  };

  useEffect(() => {
    loadWorkflowDefinitionCounts();
  }, [filters.status, filters.searchText, filters.startDateRange, filters.endDateRange]);

  useEffect(() => {
    loadHorizontalBoardDefinitionCounts();
  }, [horizontalBoardDateRange]);

  useEffect(() => {
    loadHorizontalBoardPVDefinitions(horizontalBoardWorkflowId);
    setHorizontalBoardPVFilters({});
  }, [horizontalBoardWorkflowId, loadHorizontalBoardPVDefinitions]);

  const workflowDefinitionTabItems = useMemo(() =>
    horizontalBoardWorkflowDefinitions
      .filter((workflow) => workflow.status !== 'Disabled')
      .map((workflow) => ({
        id: workflow.id,
        name: workflow.name,
        count: workflowDefinitionCounts.byId[workflow.id] ?? 0
      })),
    [horizontalBoardWorkflowDefinitions, workflowDefinitionCounts]
  );

  const horizontalBoardDefinitionTabItems = useMemo(() =>
    horizontalBoardWorkflowDefinitions
      .filter((workflow) => workflow.status !== 'Disabled')
      .map((workflow) => ({
        id: workflow.id,
        name: workflow.name,
        count: horizontalBoardDefinitionCounts.byId[workflow.id] ?? 0
      })),
    [horizontalBoardWorkflowDefinitions, horizontalBoardDefinitionCounts]
  );

  const handleStatusFilter = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleWorkflowDefinitionFilter = (value) => {
    setFilters(prev => ({ ...prev, workflowDefinitionId: value ?? null }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStartDateRangeChange = (dates) => {
    setFilters(prev => ({ ...prev, startDateRange: dates }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleEndDateRangeChange = (dates) => {
    setFilters(prev => ({ ...prev, endDateRange: dates }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ ...DEFAULT_MONITOR_FILTERS });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleOpenAutoRefreshSettings = () => {
    setAutoRefreshModalVisible(true);
  };

  const handleApplyAutoRefreshSettings = () => {
    const normalizedInterval = Number(modalRefreshInterval);
    const sanitizedInterval = Math.max(5, Math.min(600, Number.isFinite(normalizedInterval) ? normalizedInterval : refreshInterval));
    setRefreshInterval(sanitizedInterval);
    setAutoRefresh(modalAutoRefreshEnabled);
    setModalRefreshInterval(sanitizedInterval);
    setAutoRefreshModalVisible(false);
  };

  const handleInstanceAction = async (action, instance) => {
    try {
      const response = await fetch(`/api/workflowexecutions/${instance.id}/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('workflowMonitor.operationFailed'));
      }

      const result = await response.json();
      message.success(result.message || t('workflowMonitor.operationSuccess', { action }));
      
      // 重新載入數據
      loadInstances();
      loadStatistics();
    } catch (error) {
      message.error(t('workflowMonitor.operationFailed', { action }) + ': ' + error.message);
    }
  };

  // 取得可執行指定操作的選中實例
  const getApplicableInstances = (action) => {
    const statusLower = (s) => (typeof s === 'string' ? s.toLowerCase() : '');
    const isRunning = (s) => s === 'running' || (s && s.includes('wait'));
    return selectedInstances.filter((inst) => {
      const status = statusLower(inst.status);
      switch (action) {
        case 'pause':
        case 'cancel':
          return isRunning(status);
        case 'resume':
          return status === 'paused';
        case 'retry':
          return status === 'failed';
        case 'delete':
          return true;
        default:
          return false;
      }
    });
  };

  // 批次操作；options.removeEmailProcessed 僅在 action === 'delete' 且執行為 Email 觸發時有效
  const handleBatchAction = async (action, instancesToApply, options = {}) => {
    const list = instancesToApply.length > 0 ? instancesToApply : getApplicableInstances(action);
    if (list.length === 0) {
      message.warning(t('workflowMonitor.batchNoApplicableInstances', { action: t(`workflowMonitor.${action}`) }));
      return;
    }
    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    try {
      for (const inst of list) {
        try {
          if (action === 'delete') {
            const removeEmailProcessed = isEmailTriggered(inst) && options.removeEmailProcessed === true;
            const params = new URLSearchParams();
            if (removeEmailProcessed) params.set('removeEmailProcessed', 'true');
            if (options.deleteFiles) params.set('deleteFiles', 'true');
            const qs = params.toString();
            const url = `/api/workflowexecutions/${inst.id}${qs ? `?${qs}` : ''}`;
            const response = await fetch(url, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (!response.ok) {
              const errData = await response.json().catch(() => ({}));
              throw new Error(errData?.error || 'Delete failed');
            }
          } else {
            const response = await fetch(`/api/workflowexecutions/${inst.id}/${action}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            });
            if (!response.ok) {
              const errData = await response.json().catch(() => ({}));
              throw new Error(errData?.error || t('workflowMonitor.operationFailed'));
            }
          }
          successCount++;
        } catch (e) {
          failCount++;
          console.error(`[WorkflowMonitor] batch ${action} failed for ${inst.id}`, e);
        }
      }
      if (successCount > 0) {
        message.success(t('workflowMonitor.batchOperationSuccess', { count: successCount, action: t(`workflowMonitor.${action}`) }));
        setSelectedInstances(prev => prev.filter(p => !list.some(l => l.id === p.id)));
        if (selectedInstanceId && list.some(l => l.id === selectedInstanceId)) {
          setDetailPanelVisible(false);
          setSelectedInstanceId(null);
          setSelectedInstance(null);
        }
        await loadInstances();
        await loadStatistics();
      }
      if (failCount > 0) {
        message.warning(t('workflowMonitor.batchOperationPartialFail', { success: successCount, fail: failCount }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBatchCancel = () => {
    const applicable = getApplicableInstances('cancel');
    if (applicable.length === 0) {
      message.warning(t('workflowMonitor.batchNoApplicableInstances', { action: t('workflowMonitor.cancel') }));
      return;
    }
    Modal.confirm({
      title: t('workflowMonitor.batchCancelConfirmTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('workflowMonitor.batchCancelConfirmMessage', { count: applicable.length }),
      okText: t('workflowMonitor.cancel'),
      cancelText: t('common.cancel'),
      onOk: () => handleBatchAction('cancel', applicable)
    });
  };

  const handleBatchDelete = () => {
    if (selectedInstances.length === 0) return;
    emailDeletePendingFilesRef.current = false;
    setFileDeleteChoiceTarget(null);
    setFileDeleteChoiceIsBatch(true);
    setFileDeleteChoiceModalVisible(true);
  };

  // 依刪除實例（單筆用 targetInstance，批次則為 null）進行下一步：若含 Email 觸發先詢問是否允許再次觸發，否則直接確認刪除
  const proceedDeleteAfterFileChoice = (deleteFiles, targetInstance) => {
    if (targetInstance) {
      if (isEmailTriggered(targetInstance)) {
        setEmailDeleteInstance(targetInstance);
        setEmailDeleteModalVisible(true);
        emailDeletePendingFilesRef.current = deleteFiles;
        return;
      }
      Modal.confirm({
        title: t('workflowMonitor.deleteConfirmTitle'),
        icon: <ExclamationCircleOutlined />,
        content: t('workflowMonitor.deleteConfirmMessage', {
          workflowName: targetInstance.workflowName || '-',
          instanceId: targetInstance.id
        }),
        okText: t('common.delete'),
        okType: 'danger',
        cancelText: t('common.cancel'),
        onOk: () => performDeleteInstance(targetInstance, false, deleteFiles)
      });
      return;
    }

    const emailCount = selectedInstances.filter(isEmailTriggered).length;
    if (emailCount > 0) {
      setBatchDeleteEmailChoiceModalVisible(true);
      emailDeletePendingFilesRef.current = deleteFiles;
      return;
    }
    Modal.confirm({
      title: t('workflowMonitor.batchDeleteConfirmTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('workflowMonitor.batchDeleteConfirmMessage', { count: selectedInstances.length }),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: () => handleBatchAction('delete', selectedInstances, { deleteFiles })
    });
  };

  // 打包並下載某筆／多筆實例的相關檔案（圖片/影片/文件等）作為刪除前備份
  const downloadExecutionFilesBackup = async (targetInstance) => {
    const ids = targetInstance ? [targetInstance.id] : selectedInstances.map(i => i.id);
    const isBatch = ids.length > 1;
    const url = isBatch ? '/api/workflowexecutions/files/zip-batch' : `/api/workflowexecutions/${ids[0]}/files/zip`;
    const response = await fetch(url, {
      method: isBatch ? 'POST' : 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...(isBatch ? { 'Content-Type': 'application/json' } : {})
      },
      ...(isBatch ? { body: JSON.stringify({ ids }) } : {})
    });

    const contentType = response.headers.get('Content-Type') || '';
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.error || t('workflowMonitor.deleteFilesBackupFailed'));
    }
    if (!contentType.includes('zip')) {
      // 沒有任何檔案可打包，後端回傳 JSON 而非 zip
      const data = await response.json().catch(() => ({}));
      return { hasFiles: false, message: data?.message };
    }

    const blob = await response.blob();
    const cd = response.headers.get('Content-Disposition') || '';
    const utf8Match = /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
    const quotedMatch = /filename="([^"]+)"/i.exec(cd);
    let filename = isBatch ? 'workflow_instances_files.zip' : `workflow_${ids[0]}_files.zip`;
    if (utf8Match) {
      try { filename = decodeURIComponent(utf8Match[1]); } catch { /* keep default */ }
    } else if (quotedMatch) {
      filename = quotedMatch[1];
    }

    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
    return { hasFiles: true };
  };

  // 「先下載備份再刪除」選項的處理：下載成功（或確認沒有檔案可下載）後，才繼續刪除流程
  const handleDownloadThenDelete = async (targetInstance) => {
    setFileDeleteBackupLoading(true);
    try {
      const result = await downloadExecutionFilesBackup(targetInstance);
      if (result.hasFiles === false) {
        message.info(result.message || t('workflowMonitor.deleteFilesBackupEmpty'));
      } else {
        message.success(t('workflowMonitor.deleteFilesBackupSuccess'));
      }
      setFileDeleteChoiceModalVisible(false);
      proceedDeleteAfterFileChoice(true, targetInstance);
    } catch (error) {
      message.error(error.message || t('workflowMonitor.deleteFilesBackupFailed'));
    } finally {
      setFileDeleteBackupLoading(false);
    }
  };

  const handleCancelInstance = (instance) => {
    Modal.confirm({
      title: t('workflowMonitor.cancelConfirmTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('workflowMonitor.cancelConfirmMessage', {
        workflowName: instance.workflowName || '-',
        instanceId: instance.id
      }),
      okText: t('workflowMonitor.cancel'),
      cancelText: t('common.cancel'),
      onOk: () => handleInstanceAction('cancel', instance)
    });
  };

  const handleDeleteInstance = (instance) => {
    emailDeletePendingFilesRef.current = false;
    setFileDeleteChoiceTarget(instance);
    setFileDeleteChoiceIsBatch(false);
    setFileDeleteChoiceModalVisible(true);
  };

  const performDeleteInstance = async (instance, removeEmailProcessed = false, deleteFiles = false) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (removeEmailProcessed) params.set('removeEmailProcessed', 'true');
      if (deleteFiles) params.set('deleteFiles', 'true');
      const qs = params.toString();
      const url = `/api/workflowexecutions/${instance.id}${qs ? `?${qs}` : ''}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      let responseData = null;
      try {
        responseData = await response.json();
      } catch (parseError) {
        // 忽略解析錯誤，部分情況可能沒有 JSON 內容
      }

      if (!response.ok) {
        throw new Error(responseData?.error || t('workflowMonitor.deleteFailed'));
      }

      message.success(responseData?.message || t('workflowMonitor.deleteSuccess'));

      if (selectedInstanceId === instance.id) {
        setDetailPanelVisible(false);
        setSelectedInstanceId(null);
        setSelectedInstance(null);
      }

      setSelectedInstances(prev => prev.filter(item => item.id !== instance.id));

      await loadInstances();
      await loadStatistics();
    } catch (error) {
      message.error(error.message || t('workflowMonitor.deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (instance, initialTab = null) => {
    setDetailInitialTab(initialTab);
    try {
      const response = await fetch(`/api/workflowexecutions/${instance.id}/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('workflowMonitor.loadDetailsFailed'));
      }

      const details = await response.json();
      setSelectedInstance(details);
      setSelectedInstanceId(instance.id);
      setDetailPanelVisible(true);
      requestAnimationFrame(() => scheduleInstanceTableScrollYMeasure());
      setTimeout(scheduleInstanceTableScrollYMeasure, 100);
      setTimeout(scheduleInstanceTableScrollYMeasure, 350);
    } catch (error) {
      message.error(t('workflowMonitor.loadDetailsFailed') + ': ' + error.message);
    }
  };

  // 關閉右側詳情面板
  const handleCloseDetailPanel = () => {
    setDetailPanelVisible(false);
    setSelectedInstanceId(null);
    setDetailInitialTab(null);
    requestAnimationFrame(() => scheduleInstanceTableScrollYMeasure());
    setTimeout(scheduleInstanceTableScrollYMeasure, 100);
  };

  // 拖拉調整左側列表 / 右側 Instance Details 寬度比例
  const handleDetailSplitResizeStart = useCallback((e) => {
    e.preventDefault();
    const container = detailSplitContainerRef.current;
    if (!container) return;
    const onMove = (moveEvent) => {
      const rect = container.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const ratio = Math.max(0.25, Math.min(0.75, x / rect.width));
      setDetailSplitRatio(ratio);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      scheduleInstanceTableScrollYMeasure();
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [scheduleInstanceTableScrollYMeasure]);

  // 處理內嵌表單 Modal 關閉
  const handleCloseEmbeddedForm = () => {
    setEmbedFormIframeSrc(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setEmbedFormVisible(false);
    setSelectedFormInstanceId(null);
    setEmbeddedFormInstance(null);
    setEmbedFormSaving(false);
    setEmbedFormApproving(false);
    setEmbedFormRejecting(false);
    setEmbedFormRelatedAttachments([]);
    setEmbedFormLoadingAttachments(false);
  };

  // 從 iframe contentDocument 收集使用者填寫後的「完整 HTML 文件」（含 <head> 內 <style>/<link> 與 body 欄位最新值）。
  // 共用實作見 src/utils/formPreviewIframeUtils.collectFilledHtmlFromIframe。
  const collectFilledHtmlFromEmbeddedIframe = () => collectFilledHtmlFromIframe(embedFormIframeRef);

  // Save：僅儲存 FilledHtmlCode（不變更狀態、不走審批流程）。對應後端 POST /api/eforminstances/{id}/save
  const handleSaveEmbeddedForm = async () => {
    if (!selectedFormInstanceId) return;
    const filledHtmlCode = collectFilledHtmlFromEmbeddedIframe();
    if (filledHtmlCode == null) {
      message.error(t('pendingTasks.saveFailed') + ': ' + t('workflowMonitor.cannotLoadFormInstance'));
      return;
    }
    setEmbedFormSaving(true);
    try {
      const response = await fetch(`/api/eforminstances/${selectedFormInstanceId}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ filledHtmlCode })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || result.message || response.statusText);
      }
      message.success(result.message || t('pendingTasks.saveSuccess'));
      setEmbeddedFormInstance(prev => prev ? { ...prev, filledHtmlCode, htmlCode: filledHtmlCode } : prev);
    } catch (error) {
      message.error((t('pendingTasks.saveFailed') || 'Save failed') + ': ' + (error.message || ''));
    } finally {
      setEmbedFormSaving(false);
    }
  };

  const handleExportEmbeddedFormPdf = async () => {
    const instance = embeddedFormInstance;
    if (!instance?.id || embedFormExportingPdf) return;

    const filledHtmlCode = collectFilledHtmlFromEmbeddedIframe();
    const html = filledHtmlCode || instance.filledHtmlCode || instance.htmlCode || '';
    if (!canExportEformToPdf(instance.fillType, html)) {
      message.warning(t('workflowMonitor.metaFlowsPdfNotSupported'));
      return;
    }

    setEmbedFormExportingPdf(true);
    try {
      const today = TimezoneUtils.formatDateTime(new Date(), 'YYYY-MM-DD');
      const safeName = (instance.formName || instance.instanceName || `eform_${instance.id}`)
        .replace(/[^\w\-]+/g, '_')
        .slice(0, 80);
      await exportSingleEformInstanceToPdf({
        instanceId: instance.id,
        html,
        fillType: instance.fillType,
        token: localStorage.getItem('token') || '',
        fileName: `${safeName}_${today}.pdf`,
      });
      message.success(t('reports.exportSuccess') || t('workflowMonitor.exportPdfSuccess'));
    } catch (error) {
      if (error.message === 'META_FLOWS_NOT_SUPPORTED') {
        message.warning(t('workflowMonitor.metaFlowsPdfNotSupported'));
      } else {
        message.error((t('reports.exportFailed') || t('workflowMonitor.exportPdfFailed')) + (error.message ? `: ${error.message}` : ''));
      }
    } finally {
      setEmbedFormExportingPdf(false);
    }
  };

  /** 是否為 Email 觸發的執行（用於刪除時詢問是否一併移除 email_monitor_processed） */
  const isEmailTriggered = (record) => {
    const raw = record?.inputJson;
    if (raw == null || raw === '') return false;
    try {
      const data =
        typeof raw === 'object' && raw !== null && !Array.isArray(raw)
          ? raw
          : typeof raw === 'string'
            ? JSON.parse(raw)
            : null;
      if (!data || typeof data !== 'object') return false;
      const ts = String(data.triggerSource ?? data.TriggerSource ?? '').toLowerCase();
      const mid = data.messageId ?? data.MessageId;
      return ts === 'email' && mid;
    } catch {
      return false;
    }
  };

  // 根據 InputJson 解析啟動來源，返回 Tag 顯示（WhatsApp 選項 / Email subject / 手動觸發），含 Tooltip 顯示完整內容
  const getTriggerSourceTag = (record) => {
    const raw = record?.inputJson;
    let data;
    try {
      if (raw == null) data = null;
      else if (typeof raw === 'object' && !Array.isArray(raw)) data = raw;
      else if (typeof raw === 'string') data = JSON.parse(raw);
      else data = null;
    } catch {
      data = null;
    }
    let displayText = t('workflowMonitor.manualTrigger');
    let tagContent;
    if (!data || typeof data !== 'object') {
      tagContent = <Tag color="default">{displayText}</Tag>;
    } else {
      const ts = String(data.triggerSource ?? data.TriggerSource ?? '').toLowerCase();
      if (ts === 'email') {
        const subject = data.subject || data.Subject || '—';
        displayText = `${t('workflowMonitor.triggerSourceEmail')}: ${subject}`;
        tagContent = (
          <Tag color="processing" icon={<MailOutlined />}>
            {displayText}
          </Tag>
        );
      } else if (data.Source === 'MetaWebhook' || data.WaId != null) {
        const menuOption = data.MessageText || data.Caption || '—';
        displayText = `${t('workflowMonitor.triggerSourceWhatsApp')}: ${menuOption}`;
        tagContent = (
          <Tag color="#25d366" style={{ color: '#fff', borderColor: '#25d366' }} icon={<MessageOutlined />}>
            {displayText}
          </Tag>
        );
      } else if (ts === 'manual') {
        const bodyRaw = data.body ?? data.Body;
        const bodyText = bodyRaw != null && String(bodyRaw).trim() ? String(bodyRaw).trim() : '—';
        displayText = `${t('workflowMonitor.triggerSourcePublishedManual')}: ${bodyText}`;
        tagContent = (
          <Tag color="success" icon={<RocketOutlined />}>
            {displayText}
          </Tag>
        );
      } else {
        tagContent = <Tag color="default">{displayText}</Tag>;
      }
    }
    return <Tooltip title={displayText}>{tagContent}</Tooltip>;
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      running: { color: 'processing', icon: <SyncOutlinedIcon spin />, text: t('workflowMonitor.statusRunning') },
      completed: { color: 'success', icon: <CheckCircleFilled />, text: t('workflowMonitor.statusCompleted') },
      failed: { color: 'error', icon: <CloseCircleFilled />, text: t('workflowMonitor.statusFailed') },
      waiting: { color: 'processing', icon: <SyncOutlinedIcon spin />, text: t('workflowMonitor.statusRunning') },
      waitingforqrcode: { color: 'processing', icon: <SyncOutlinedIcon spin />, text: t('workflowMonitor.statusRunning') },
      waitingforformapproval: { color: 'processing', icon: <SyncOutlinedIcon spin />, text: t('workflowMonitor.statusRunning') },
      paused: { color: 'default', icon: <PauseCircleOutlined />, text: t('workflowMonitor.statusPaused') },
      cancelled: { color: 'default', icon: <StopOutlined />, text: t('workflowMonitor.statusCancelled') }
    };
    
    // 將狀態轉為小寫進行匹配
    const statusLower = status?.toLowerCase() || '';
    let config = statusConfig[statusLower];
    
    // 如果直接匹配失敗，檢查是否為運行相關狀態（包括 waiting）
    if (!config) {
      if (statusLower.includes('wait') || statusLower.includes('run')) {
        // waiting 狀態也視為 running（因為流程仍在運行中，只是在等待用戶輸入）
        config = statusConfig.running;
      } else {
        // 默認使用 running
        config = statusConfig.running;
      }
    }
    
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getDurationText = (duration) => {
    if (!duration) return '-';
    if (duration < 60) return `${Math.round(duration)} ${t('workflowMonitor.minutes')}`;
    const hours = Math.floor(duration / 60);
    const minutes = Math.round(duration % 60);
    return `${hours} ${t('workflowMonitor.hours')} ${minutes} ${t('workflowMonitor.minutes')}`;
  };

  // 查看消息發送詳情
  const handleViewMessageSend = async (messageSendId) => {
    try {
      const response = await fetch(`/api/workflowmessagesend/${messageSendId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('workflowMonitor.loadMessageSendDetailsFailed'));
      }

      const data = await response.json();
      setSelectedMessageSend(data.data);
      setMessageSendModalVisible(true);
    } catch (error) {
      message.error(t('workflowMonitor.loadMessageSendDetailsFailed') + ': ' + error.message);
    }
  };

  // 查看消息發送詳細狀態（包含收件人詳情）
  // workflowStepExecutionIdFromStep：從實例時間軸點「查看發送狀態」時傳入 step.id，用於合併顯示同一等待步驟的 normal / retry / escalation
  const handleViewMessageSendDetail = async (messageSendId, workflowStepExecutionIdFromStep = null) => {
    try {
      // 先獲取單個消息發送記錄的詳情
      const response = await fetch(`/api/workflowmessagesend/${messageSendId}/detail`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('workflowMonitor.loadMessageSendStatusFailed'));
      }

      const data = await response.json();
      const messageSend = data.data;

      const stepExecId =
        workflowStepExecutionIdFromStep != null && workflowStepExecutionIdFromStep !== undefined
          ? workflowStepExecutionIdFromStep
          : messageSend.workflowStepExecutionId;

      // 使用新的模態框組件
      setSelectedMessageSendId(messageSendId);
      setSelectedWorkflowExecutionId(messageSend.workflowExecutionId);
      setSelectedWorkflowStepExecutionId(stepExecId ?? null);
      setSelectedNodeId(messageSend.nodeId);
      setNewMessageSendStatusModalVisible(true);
    } catch (error) {
      message.error(t('workflowMonitor.loadMessageSendStatusFailed') + ': ' + error.message);
    }
  };

  // 載入內嵌表單實例
  const loadEmbeddedFormInstance = async () => {
    try {
      setLoadingEmbeddedForm(true);
      setEmbedFormRelatedAttachments([]);
      console.log('[WorkflowMonitor] loading embedded form instance', selectedFormInstanceId);
      
      const response = await fetch(`/api/eforminstances/${selectedFormInstanceId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(t('workflowMonitor.loadFormInstanceFailed'));
      }
      
      const data = await response.json();
      console.log('[WorkflowMonitor] loaded embedded form instance', data);
      setEmbeddedFormInstance(data);

      // Load related media attachments
      const execId = data?.workflowExecutionId;
      if (execId && execId > 0) {
        loadEmbedFormMediaFiles(execId);
      }
    } catch (error) {
      console.error('[WorkflowMonitor] load embedded form instance failed', error);
      message.error(t('workflowMonitor.loadFormInstanceFailed') + ': ' + error.message);
      setEmbeddedFormInstance(null);
    } finally {
      setLoadingEmbeddedForm(false);
    }
  };

  // 表單狀態顏色映射
  const getEformStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'orange';
      case 'Approved': return 'green';
      case 'Rejected': return 'red';
      case 'Submitted': return 'blue';
      default: return 'default';
    }
  };

  const loadEmbedFormMediaFiles = (execId) => {
    if (!execId || execId <= 0) return;
    setEmbedFormLoadingAttachments(true);
    fetch(`/api/workflowexecutions/${execId}/media-files`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => res.ok ? res.json() : { data: [] })
      .then(mediaData => setEmbedFormRelatedAttachments(mediaData.data || []))
      .catch(() => setEmbedFormRelatedAttachments([]))
      .finally(() => setEmbedFormLoadingAttachments(false));
  };

  // 表單狀態文字映射
  const getEformStatusText = (status, eform = null) => {
    const approvalLabels = getEFormApprovalLabels(eform, t, {
      approve: 'workflowMonitor.eformStatusApproved',
      reject: 'workflowMonitor.eformStatusRejected',
    });
    switch (status) {
      case 'Pending': return t('workflowMonitor.eformStatusPending');
      case 'Approved': return approvalLabels.approve;
      case 'Rejected': return approvalLabels.reject;
      case 'Submitted': return t('workflowMonitor.eformStatusSubmitted');
      default: return status;
    }
  };

  // 表格列寬調整處理
  const handleResize = useCallback(
    (key) => (e, { size }) => {
      setColumnWidths(prev => ({
        ...prev,
        [key]: size.width,
      }));
    },
    []
  );

  // 表格變化處理（包括排序）
  const handleTableChange = (paginationInfo, filters, sorter) => {
    console.log('[WorkflowMonitor] table change', { paginationInfo, filters, sorter });
    console.log('[WorkflowMonitor] sorter details', {
      field: sorter?.field,
      order: sorter?.order,
      columnKey: sorter?.columnKey,
      column: sorter?.column
    });
    
    // 處理分頁 - 僅在 paginationInfo 包含有效值時更新（Table 使用 pagination={false} 時，paginationInfo 為空物件，避免覆寫為 undefined）
    if (paginationInfo && paginationInfo.current !== undefined && paginationInfo.pageSize !== undefined) {
      console.log('[WorkflowMonitor] pagination change', paginationInfo);
      setPagination(prev => ({ 
        ...prev, 
        current: paginationInfo.current, 
        pageSize: paginationInfo.pageSize 
      }));
    }
    
    // 處理排序
    if (sorter && (sorter.field || sorter.columnKey) && sorter.order) {
      const sortField = sorter.field || sorter.columnKey;
      // 後端 API 期望 "asc"/"desc"，Ant Design 傳送 "ascend"/"descend"
      const sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
      setTableSorter({ sortBy: sortField, sortOrder });
      loadInstances(sortField, sortOrder);
    } else if (paginationInfo && paginationInfo.current !== undefined && paginationInfo.pageSize !== undefined) {
      // 只有分頁變更時
      console.log('[WorkflowMonitor] pagination changed with default sort');
      loadInstances();
    }
  };

  // 取得當前排序列的 sortOrder（Ant Design 使用 'ascend'/'descend'）
  const getColumnSortOrder = (columnKey) =>
    tableSorter.sortBy === columnKey ? (tableSorter.sortOrder === 'asc' ? 'ascend' : 'descend') : undefined;

  // 基礎表格列定義
  const baseColumns = useMemo(() => [
    {
      title: t('workflowMonitor.instanceId'),
      dataIndex: 'id',
      key: 'id',
      width: 120,
      ellipsis: true,
      sorter: true,
      sortOrder: getColumnSortOrder('id'),
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: t('workflowMonitor.workflowName'),
      dataIndex: 'workflowName',
      key: 'workflowName',
      width: 200,
      ellipsis: true,
      sorter: true,
      sortOrder: getColumnSortOrder('workflowName'),
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: t('workflowMonitor.triggerSource'),
      key: 'triggerSource',
      width: 220,
      ellipsis: true,
      render: (_, record) => (
        <span
          onClick={(e) => {
            e.stopPropagation();
            handleViewDetails(record, 'triggerSource');
          }}
          style={{ cursor: 'pointer', display: 'inline-block' }}
        >
          {getTriggerSourceTag(record)}
        </span>
      )
    },
    {
      title: t('workflowMonitor.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: true,
      sortOrder: getColumnSortOrder('status'),
      render: (status) => getStatusTag(status)
    },
    {
      title: t('workflowMonitor.currentStep'),
      dataIndex: 'currentStep',
      key: 'currentStep',
      width: 120,
      sorter: true,
      sortOrder: getColumnSortOrder('currentStep'),
      render: (step, record) => {
        // 判斷是否為運行中狀態（包括 waiting）
        const status = typeof record.status === 'string' ? record.status.toLowerCase() : '';
        const isRunning = status === 'running' || status.includes('wait');
        if (isRunning && step !== null) {
          return (
            <div>
              <Text>{step}</Text>
              <Progress 
                percent={Math.min((step / record.stepCount) * 100, 100)} 
                size="small" 
                showInfo={false}
                strokeColor="#1890ff"
              />
            </div>
          );
        }
        return step || '-';
      }
    },
    {
      title: t('workflowMonitor.startedAt'),
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: 150,
      sorter: true,
      sortOrder: getColumnSortOrder('startedAt'),
      render: (date) => TimezoneUtils.formatDateWithTimezone(date, userTimezoneOffset)
    },
    {
      title: t('workflowMonitor.duration'),
      dataIndex: 'duration',
      key: 'duration',
      width: 120,
      sorter: true,
      sortOrder: getColumnSortOrder('duration'),
      render: (duration, record) => {
        // 判斷是否為運行中狀態（包括 waiting）
        const status = typeof record.status === 'string' ? record.status.toLowerCase() : '';
        const isRunning = status === 'running' || status.includes('wait');
        if (isRunning) {
          const runningDuration = TimezoneUtils.calculateDuration(record.startedAt, new Date());
          return getDurationText(runningDuration);
        }
        return getDurationText(duration);
      }
    },
    {
      title: t('workflowMonitor.createdBy'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 140,
      sorter: true,
      sortOrder: getColumnSortOrder('createdBy'),
      render: (text, record) => {
        const label = record.createdByDisplayName || text;
        const tip = record.createdByDisplayName && text && record.createdByDisplayName !== text
          ? `${record.createdByDisplayName} (${text})`
          : (label || '—');
        return (
          <Text ellipsis={{ tooltip: tip }} style={{ maxWidth: 132 }}>
            {label || '—'}
          </Text>
        );
      }
    },
    {
      title: t('common.action'),
      key: 'action',
      width: 250,
      render: (_, record) => {
        const status = typeof record.status === 'string' ? record.status.toLowerCase() : '';
        // 判斷是否為運行中狀態（包括 waiting，因為它們仍在運行中）
        const isRunning = status === 'running' || status.includes('wait');
        return (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          {isRunning && (
            <>
              {canPauseExecution && (
                <Tooltip title={t('workflowMonitor.pause')}>
                  <Button 
                    type="text" 
                    icon={<PauseCircleOutlined />} 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInstanceAction('pause', record);
                    }}
                  />
                </Tooltip>
              )}
              {canCancelExecution && (
                <Tooltip title={t('workflowMonitor.cancel')}>
                  <Button 
                    type="text" 
                    icon={<StopOutlined />} 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelInstance(record);
                    }}
                  />
                </Tooltip>
              )}
            </>
          )}
          
          {status === 'failed' && canRetryExecution && (
            <Tooltip title={t('workflowMonitor.retry')}>
              <Button 
                type="text" 
                icon={<ReloadOutlined />} 
                onClick={(e) => {
                  e.stopPropagation();
                  handleInstanceAction('retry', record);
                }}
              />
            </Tooltip>
          )}
          
          {status === 'paused' && canResumeExecution && (
            <Tooltip title={t('workflowMonitor.resume')}>
              <Button 
                type="text" 
                icon={<PlayCircleOutlined />} 
                onClick={(e) => {
                  e.stopPropagation();
                  handleInstanceAction('resume', record);
                }}
              />
            </Tooltip>
          )}

          {canDeleteExecution && (
            <Tooltip title={t('workflowMonitor.delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteInstance(record);
                }}
              />
            </Tooltip>
          )}
        </Space>
      );
      }
    }
  ], [
    t,
    tableSorter,
    userTimezoneOffset,
    selectedInstanceId,
    canPauseExecution,
    canCancelExecution,
    canRetryExecution,
    canResumeExecution,
    canDeleteExecution,
    handleInstanceAction,
    handleCancelInstance,
    handleDeleteInstance,
    getDurationText
  ]);

  // 合併列配置，添加調整功能
  const mergedColumns = baseColumns.map((col) => ({
    ...col,
    onHeaderCell: column => ({
      width: columnWidths[col.key] || col.width,
      onResize: handleResize(col.key),
    }),
    width: columnWidths[col.key] || col.width
  }));

  // 表格組件配置
  const components = {
    header: {
      cell: ResizableTitle,
    },
  };

  const horizontalBoardRows = useMemo(() => {
    const getStepDisplayOrder = (step) => {
      const stepType = String(step.stepType || '').toLowerCase();
      const typeRank = stepType === 'start' ? -1 : (stepType === 'end' ? 1 : 0);
      return ((step.stepIndex ?? 0) * 10) + typeRank;
    };
    const grouped = new Map();
    horizontalBoardSteps.forEach(step => {
      if (!grouped.has(step.workflowExecutionId)) {
        grouped.set(step.workflowExecutionId, {
          workflowExecutionId: step.workflowExecutionId,
          workflowName: step.workflowName,
          workflowDefinitionId: step.workflowDefinitionId,
          initiatedBy: step.initiatedBy,
          executionStatus: step.executionStatus,
          latestStartedAt: step.startedAt,
          executionInputJson: step.executionInputJson || null,
          steps: []
        });
      }
      const row = grouped.get(step.workflowExecutionId);
      // 任何步驟帶回 executionInputJson 時補齊 row 層快取
      if (!row.executionInputJson && step.executionInputJson) {
        row.executionInputJson = step.executionInputJson;
      }
      row.steps.push(step);
      if (step.startedAt && (!row.latestStartedAt || new Date(step.startedAt) > new Date(row.latestStartedAt))) {
        row.latestStartedAt = step.startedAt;
      }
    });

    return Array.from(grouped.values())
      .map(row => ({
        ...row,
        steps: row.steps.sort((a, b) => getStepDisplayOrder(b) - getStepDisplayOrder(a))
      }))
      .sort((a, b) => new Date(b.latestStartedAt || 0) - new Date(a.latestStartedAt || 0));
  }, [horizontalBoardSteps]);

  // 把 PV definitions 陣列展平為「欄位條目」（非 JSON 直接 1 條；JSON 展開有 showInInstanceList=true 的子 Key）
  const buildInstanceListDisplayItems = useCallback((pvDefs) => {
    const items = [];
    if (!Array.isArray(pvDefs)) return items;
    pvDefs.forEach(pvDef => {
      if (!pvDef.showInInstanceList) return;
      if (String(pvDef.dataType || '').toLowerCase() === 'json') {
        let jsonKeys = [];
        if (pvDef.jsonKeyDisplaySettings) {
          try {
            const parsed = typeof pvDef.jsonKeyDisplaySettings === 'string'
              ? JSON.parse(pvDef.jsonKeyDisplaySettings)
              : pvDef.jsonKeyDisplaySettings;
            if (Array.isArray(parsed)) jsonKeys = parsed;
          } catch (_) {}
        }
        jsonKeys
          .filter(k => !!k.showInInstanceList)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .forEach(k => {
            items.push({
              filterKey: `${pvDef.variableName}.__json__.${k.jsonKey}`,
              label: k.displayLabel || k.jsonKey,
              pvVariableName: pvDef.variableName,
              isJsonKey: true,
              jsonKey: k.jsonKey
            });
          });
      } else {
        items.push({
          filterKey: pvDef.variableName,
          label: pvDef.displayLabel || pvDef.displayName || pvDef.variableName,
          pvVariableName: pvDef.variableName,
          isJsonKey: false,
          jsonKey: null
        });
      }
    });
    return items;
  }, []);

  // Activity Board filter bar 用：單一 workflow 選中時的展平欄位（供搜尋框使用）
  const instanceListDisplayItems = useMemo(
    () => buildInstanceListDisplayItems(horizontalBoardPVDefinitions),
    [buildInstanceListDisplayItems, horizontalBoardPVDefinitions]
  );

  // 從 pvValues 陣列中取得某個 display item 的顯示值
  const extractPVDisplayValue = useCallback((pvValues, item) => {
    const pv = pvValues.find(v => v.variableName === item.pvVariableName);
    if (!pv) return '';
    const rawVal = pv.value ?? pv.currentValue ?? pv.stringValue ?? '';
    if (item.isJsonKey) {
      try {
        const jsonObj = typeof rawVal === 'string' ? JSON.parse(rawVal) : rawVal;
        const val = jsonObj?.[item.jsonKey];
        return val !== null && val !== undefined ? String(val) : '';
      } catch (_) {
        return '';
      }
    }
    return String(rawVal ?? '');
  }, []);

  // Activity Board PV 篩選：對 horizontalBoardRows 再過濾一遍
  const filteredHorizontalBoardRows = useMemo(() => {
    const activePVFilters = Object.entries(horizontalBoardPVFilters)
      .filter(([, v]) => v && String(v).trim() !== '');
    if (activePVFilters.length === 0) return horizontalBoardRows;
    return horizontalBoardRows.filter(row => {
      const pvValues = horizontalBoardPVValues[row.workflowExecutionId] || [];
      return activePVFilters.every(([filterKey, filterText]) => {
        const item = instanceListDisplayItems.find(i => i.filterKey === filterKey);
        if (!item) return true;
        const val = extractPVDisplayValue(pvValues, item);
        return val.toLowerCase().includes(filterText.toLowerCase());
      });
    });
  }, [horizontalBoardRows, horizontalBoardPVFilters, horizontalBoardPVValues, instanceListDisplayItems, extractPVDisplayValue]);

  const getBoardStepDuration = (startedAt, endedAt) => {
    if (!startedAt) return '-';
    const toDate = (value) => {
      const text = String(value);
      return new Date(text.includes('Z') || /[+-]\d{2}:\d{2}$/.test(text) ? text : `${text}Z`);
    };
    const start = toDate(startedAt);
    const end = endedAt ? toDate(endedAt) : new Date();
    const diffMins = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 60000));
    if (diffMins < 1) return '<1m';
    if (diffMins < 60) return `${diffMins}m`;
    return `${Math.floor(diffMins / 60)}h ${diffMins % 60}m`;
  };

  const collectStepArtifactLinks = (step) => {
    const outputData = parseWorkflowMonitorJson(step.outputJson);
    const artifacts = horizontalBoardArtifacts[step.workflowExecutionId] || {};
    const links = [];
    const seen = new Set();
    const stepType = String(step.stepType || '').toLowerCase();
    const isMediaReplyStep = ['waitreply', 'waitforuserreply', 'waitforqrcode', 'waitforemailreply'].includes(stepType);
    const isSameMediaStep = (file) => {
      if (file?.workflowStepExecutionId !== null && file?.workflowStepExecutionId !== undefined) {
        return String(file.workflowStepExecutionId) === String(step.id);
      }
      // Legacy fallback only for nodes that can actually receive media, to avoid duplicated start attachments
      // when old records share the same numeric stepIndex.
      return isMediaReplyStep &&
        file?.stepIndex !== null &&
        file?.stepIndex !== undefined &&
        String(file.stepIndex) === String(step.stepIndex);
    };
    const addLink = (kind, label, url, icon = null, extra = {}) => {
      const href = normalizeArtifactUrl(url);
      const dedupKey = getArtifactDedupKey(kind, href);
      if (!href || seen.has(dedupKey)) return;
      seen.add(dedupKey);
      const displayLabel = safeDecodeText(label || getDisplayFileName(href) || href);
      links.push({ kind, label: displayLabel, url: href, icon, ...extra });
    };

    toArray(step.messageValidations)
      .filter(v => isMediaReplyStep && String(v.stepIndex) === String(step.stepIndex) && v.mediaUrl)
      .forEach((v, index) => addLink('media', getDisplayFileName(v.mediaUrl) || `${t('workflowMonitor.receivedMedia')} ${index + 1}`, v.mediaUrl, <PictureOutlined />));

    toArray(artifacts.mediaFiles)
      .filter(isSameMediaStep)
      .forEach(file => addLink('media', getDisplayFileName(file.originalFileName, file.fileName, file.filePath) || t('workflowMonitor.file'), file.filePath, <PictureOutlined />));

    const outputFormInstanceId = outputData?.formInstanceId;
    toArray(artifacts.eformInstances)
      .filter(form => form.workflowStepExecutionId === step.id || (outputFormInstanceId && String(form.id) === String(outputFormInstanceId)))
      .forEach(form => addLink(
        'eform',
        form.instanceName || form.formName || t('workflowMonitor.formInstance'),
        form.formUrl || `/eform-instance/${form.id}`,
        <FileTextOutlined />,
        {
          formId: form.id,
          formStatus: form.status,
          approveButtonLabel: form.approveButtonLabel,
          rejectButtonLabel: form.rejectButtonLabel,
          approvalLabels: form.approvalLabels,
          requireApproval: form.requireApproval
        }
      ));

    if (outputFormInstanceId) {
      addLink('eform', t('workflowMonitor.formInstance'), `/eform-instance/${outputFormInstanceId}`, <FileTextOutlined />, { formId: outputFormInstanceId });
    }

    const outputReportIds = [outputData?.reportInstanceId].filter(Boolean).map(String);
    const matchedReports = toArray(artifacts.reportInstances)
      .filter(report => report.workflowStepExecutionId === step.id || outputReportIds.includes(String(report.id)));
    matchedReports
      .forEach(report => addLink('report', report.instanceName || getDisplayFileName(report.reportUrl) || t('workflowMonitor.openHtmlReport'), report.reportUrl, <FileTextOutlined />));
    if (outputData?.reportUrl && matchedReports.length === 0) {
      addLink('report', getDisplayFileName(outputData.reportUrl) || t('workflowMonitor.openHtmlReport'), outputData.reportUrl, <FileTextOutlined />);
    }

    const outputDocIds = [outputData?.docInstanceId, ...toArray(outputData?.instances).map(item => item?.id)].filter(Boolean).map(String);
    toArray(artifacts.docInstances)
      .filter(doc => doc.workflowStepExecutionId === step.id || outputDocIds.includes(String(doc.id)))
      .forEach(doc => addLink('doc', getDisplayFileName(doc.fileName) || doc.instanceName || t('workflowMonitor.generatedDocument'), doc.docUrl || (doc.id ? `/doc-instance/${doc.id}` : doc.publicUrl), <FileOutlined />));
    if (outputData?.docUrl) {
      addLink('doc', t('workflowMonitor.generatedDocument'), outputData.docUrl, <FileOutlined />);
    }
    toArray(outputData?.instances)
      .forEach((doc, index) => addLink('doc', getDisplayFileName(doc.fileName, doc.docUrl, doc.relativePath) || `${t('workflowMonitor.generatedDocument')} ${index + 1}`, doc.docUrl || (doc.id ? `/doc-instance/${doc.id}` : doc.relativePath), <FileOutlined />));

    return links.sort((a, b) =>
      (a.label || '').localeCompare(b.label || '', undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const renderHorizontalBoardStepCard = (step) => {
    const artifactLinks = collectStepArtifactLinks(step);
    const statusLower = String(step.status || '').toLowerCase();
    const borderColor = statusLower.includes('fail') || statusLower.includes('error')
      ? '#ff4d4f'
      : statusLower.includes('complete') || statusLower.includes('success')
        ? '#52c41a'
        : statusLower.includes('wait')
          ? '#faad14'
          : '#1890ff';

    return (
      <Card
        key={step.id}
        size="small"
        hoverable
        style={{
          width: 280,
          flex: '0 0 280px',
          borderLeft: `4px solid ${borderColor}`,
          borderRadius: 8
        }}
        bodyStyle={{ padding: 12 }}
      >
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <Text strong ellipsis={{ tooltip: step.taskName || step.stepType }} style={{ maxWidth: 170 }}>
              {step.taskName || `${t('workflowMonitor.step')} ${(step.stepIndex ?? 0) + 1}`}
            </Text>
            <Badge count={(step.stepIndex ?? 0) + 1} style={{ backgroundColor: '#1677ff' }} />
          </div>
          <Space size={4} wrap>
            {getStatusTag(step.status)}
            {step.stepType && <Tag color="blue">{getNodeTypeLabel(step.stepType, t)}</Tag>}
          </Space>
          {(() => {
            const lowerStepType = String(step.stepType || '').toLowerCase();
            const isChatBot = lowerStepType === 'chatbot';
            const isOrderingCart = lowerStepType === 'chatorderingcart';
            const isStartNode = lowerStepType === 'start';
            if (!isChatBot && !isOrderingCart && !isStartNode) return null;
            if (isStartNode) {
              return (
                <>
                  <Button
                    size="small"
                    type="link"
                    icon={<InfoCircleOutlined />}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setHorizontalBoardTriggerSourceModal({ open: true, instance: null, loading: true });
                      try {
                        const res = await fetch(`/api/workflowexecutions/${step.workflowExecutionId}/details`, {
                          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                        });
                        const details = res.ok ? await res.json() : null;
                        setHorizontalBoardTriggerSourceModal({ open: true, instance: details, loading: false });
                      } catch {
                        setHorizontalBoardTriggerSourceModal({ open: true, instance: null, loading: false });
                      }
                    }}
                    style={{ padding: 0, height: 22, fontSize: 12 }}
                  >
                    {t('workflowMonitor.triggerSource')}
                  </Button>
                  {step.executionInputJson && (
                    <div style={{ maxWidth: 252, overflow: 'hidden', marginTop: 2 }}>
                      {getTriggerSourceTag({ inputJson: step.executionInputJson })}
                    </div>
                  )}
                </>
              );
            }
            const labelKey = isOrderingCart
              ? 'workflowMonitor.viewChatOrderingCartConversation'
              : 'workflowMonitor.viewChatBotConversation';
            return (
              <Button
                size="small"
                type="link"
                icon={<MessageOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setChatBotViewer({
                    open: true,
                    stepExecutionId: step.id,
                    taskName: step.taskName || step.stepName || step.stepType,
                    nodeKind: isOrderingCart ? 'chatOrderingCart' : 'chatBot'
                  });
                }}
                style={{ padding: 0, height: 22, fontSize: 12 }}
              >
                {t(labelKey) || (isOrderingCart ? '查看購物車對話' : '查看對話')}
              </Button>
            );
          })()}
          <div style={{ fontSize: 12, color: '#666' }}>
            <div>{t('workflowMonitor.stepStartTime')}: {step.startedAt ? TimezoneUtils.formatDateWithTimezone(step.startedAt, userTimezoneOffset) : '-'}</div>
            <div>{t('workflowMonitor.executionTime')}: <Text strong>{getBoardStepDuration(step.startedAt, step.endedAt)}</Text></div>
            {step.waitingForUser && <div>{t('workflowMonitor.waitingFor')}: {step.waitingForUser}</div>}
          </div>
          {artifactLinks.length > 0 && (
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{t('workflowMonitor.artifactLinks')}</Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {artifactLinks.map((link, index) => {
                  const formId = link.formId || String(link.url).split('/eform-instance/')[1]?.split(/[?#]/)[0];
                  // 節點明確設定 requireApproval = false 時不顯示批准/拒絕按鈕
                  const requireApprovalDisabled = link.kind === 'eform' && link.requireApproval === false;
                  const canApproveForm = link.kind === 'eform' && formId && !requireApprovalDisabled && isEformApprovalPending(link.formStatus);
                  const approveKey = `${formId}:approve`;
                  const rejectKey = `${formId}:reject`;
                  const approvalLabels = getEFormApprovalLabels(link, t);

                  return (
                    <div
                      key={`${link.kind}-${index}-${link.url}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}
                    >
                      <Tooltip title={link.label}>
                        <Button
                          size="small"
                          type="link"
                          icon={link.icon}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (link.kind === 'eform' && formId) {
                              setSelectedFormInstanceId(formId);
                              setEmbedFormVisible(true);
                              return;
                            }
                            openEmbeddedArtifactPreview({
                              ...link,
                              title: step.taskName || step.stepName || step.stepType || link.label
                            });
                          }}
                          style={{ padding: 0, height: 20, fontSize: 12, minWidth: 0, maxWidth: canApproveForm ? 130 : 220 }}
                        >
                          <span style={{ display: 'inline-block', maxWidth: canApproveForm ? 110 : 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                            {link.label}
                          </span>
                        </Button>
                      </Tooltip>
                      {canApproveForm && (
                        <Space size={2}>
                          <Button
                            size="small"
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            loading={!!horizontalBoardEformApprovalLoading[approveKey]}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHorizontalBoardEformApproval(formId, 'approve', approvalLabels);
                            }}
                            style={{ height: 20, paddingInline: 6, fontSize: 11 }}
                          >
                            {approvalLabels.approve}
                          </Button>
                          <Button
                            size="small"
                            danger
                            icon={<CloseCircleOutlined />}
                            loading={!!horizontalBoardEformApprovalLoading[rejectKey]}
                            onClick={(e) => {
                              e.stopPropagation();
                              Modal.confirm({
                                title: approvalLabels.reject,
                                content: t('pendingTasks.confirmStatusChangeQuestion'),
                                okText: approvalLabels.reject,
                                okButtonProps: { danger: true },
                                cancelText: t('pendingTasks.cancel'),
                                onOk: () => handleHorizontalBoardEformApproval(formId, 'reject', approvalLabels)
                              });
                            }}
                            style={{ height: 20, paddingInline: 6, fontSize: 11 }}
                          >
                            {approvalLabels.reject}
                          </Button>
                        </Space>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Space>
      </Card>
    );
  };

  const renderHorizontalBoard = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Card size="small" style={{ flexShrink: 0, marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
        <DefinitionCategoryTabs
          iconType="workflow"
          allLabel={t('workflowMonitor.filterAll')}
          allValue={null}
          value={horizontalBoardWorkflowId}
          allCount={horizontalBoardDefinitionCounts.total}
          items={horizontalBoardDefinitionTabItems}
          loading={false}
          onChange={(value) => {
            setHorizontalBoardWorkflowId(value || null);
            horizontalBoardPreviousStepsRef.current = [];
          }}
        />
        <Row gutter={[8, 8]} align="middle" style={{ marginTop: 4 }}>
          <Col flex="300px">
            <RangePicker
              value={horizontalBoardDateRange}
              onChange={(range) => {
                setHorizontalBoardDateRange(range);
                horizontalBoardPreviousStepsRef.current = [];
              }}
              style={{ width: '100%' }}
            />
          </Col>
          {instanceListDisplayItems.map(item => (
            <Col key={item.filterKey} flex="180px">
              <Input
                size="small"
                allowClear
                placeholder={item.label}
                prefix={<FilterOutlined style={{ color: '#bfbfbf' }} />}
                value={horizontalBoardPVFilters[item.filterKey] || ''}
                onChange={e => setHorizontalBoardPVFilters(prev => ({
                  ...prev,
                  [item.filterKey]: e.target.value
                }))}
              />
            </Col>
          ))}
          <Col flex="auto">
            <Text type="secondary">
              {t('workflowMonitor.horizontalBoardDescription')}
            </Text>
          </Col>
          <Col flex="none">
            <Button icon={<ReloadOutlined />} loading={horizontalBoardLoading} onClick={() => loadHorizontalBoardSteps(false)}>
              {t('workflowMonitor.refresh')}
            </Button>
          </Col>
        </Row>
      </Card>
      <Card
        size="small"
        style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
        bodyStyle={{ height: '100%', overflow: 'auto', padding: 12 }}
      >
        {horizontalBoardLoading && filteredHorizontalBoardRows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
            <div style={{ marginTop: 12 }}>{t('workflowMonitor.loadingHorizontalBoard')}</div>
          </div>
        ) : filteredHorizontalBoardRows.length > 0 ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {filteredHorizontalBoardRows.map(row => {
              const rowPVValues = horizontalBoardPVValues[row.workflowExecutionId] || [];
              // 每個 row 按自己的 workflowDefinitionId 取對應的 PV 展示欄位
              // 單一 workflow 選中時用 instanceListDisplayItems（已含 filter bar 的同源）
              // All 模式下從 map 查各自 workflow 的 defs
              const rowDisplayItems = horizontalBoardWorkflowId
                ? instanceListDisplayItems
                : buildInstanceListDisplayItems(horizontalBoardPVDefinitionsMap[row.workflowDefinitionId] || []);
              const instancePVDisplay = rowDisplayItems.length > 0
                ? rowDisplayItems
                    .map(item => {
                      const val = extractPVDisplayValue(rowPVValues, item);
                      return val !== '' ? { label: item.label, value: val } : null;
                    })
                    .filter(Boolean)
                : [];
              return (
              <div key={row.workflowExecutionId} style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: 12, background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: instancePVDisplay.length > 0 ? 6 : 10 }}>
                  <Space size="small" wrap>
                    <Text strong>{row.workflowName || `${t('workflowMonitor.workflowExecutionId')} ${row.workflowExecutionId}`}</Text>
                    <Tag color="purple">#{row.workflowExecutionId}</Tag>
                    {row.executionStatus && getStatusTag(row.executionStatus)}
                    {row.initiatedBy && <Text type="secondary">{t('workflowMonitor.initiator')}: {row.initiatedBy}</Text>}
                  </Space>
                  <Button
                    size="small"
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      setActiveMonitorTab(WORKFLOW_MONITOR_MAIN_TABS.instanceList);
                      handleViewDetails({ id: row.workflowExecutionId });
                    }}
                  >
                    {t('workflowMonitor.instanceDetails')}
                  </Button>
                </div>
                {instancePVDisplay.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    {instancePVDisplay.map(item => (
                      <span key={item.label} style={{ fontSize: 12, color: '#595959', background: '#f5f5f5', borderRadius: 4, padding: '2px 8px', border: '1px solid #e8e8e8' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{item.label}：</Text>
                        <Text strong style={{ fontSize: 12 }}>{item.value}</Text>
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4 }}>
                  {row.steps.map(step => renderHorizontalBoardStepCard(
                    step.executionInputJson ? step : { ...step, executionInputJson: row.executionInputJson }
                  ))}
                </div>
              </div>
              );
            })}
          </Space>
        ) : (
          <Empty description={t('workflowMonitor.noHorizontalBoardSteps')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 48 }} />
        )}
      </Card>
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <style>{`
        .workflow-monitor-main-tabs.ant-tabs {
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .workflow-monitor-main-tabs .ant-tabs-content-holder {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .workflow-monitor-main-tabs .ant-tabs-content {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .workflow-monitor-main-tabs .ant-tabs-tabpane.ant-tabs-tabpane-active {
          flex: 1;
          min-height: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
      `}</style>
      <Content style={{ padding: '16px', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 32px)' }}>
        <Tabs
          className="workflow-monitor-main-tabs"
          activeKey={activeMonitorTab}
          onChange={setActiveMonitorTab}
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          tabBarStyle={{ flexShrink: 0, marginBottom: 12 }}
        >
          <TabPane tab={t('workflowMonitor.horizontalBoardTab')} key={WORKFLOW_MONITOR_MAIN_TABS.activityBoard}>
            <div style={{ flex: 1, minHeight: 0, height: '100%' }}>
              {renderHorizontalBoard()}
            </div>
          </TabPane>
          <TabPane tab={t('workflowMonitor.instanceListTab')} key={WORKFLOW_MONITOR_MAIN_TABS.instanceList}>
        {/* 主內容區域 - 左右分欄（可拖拉調整比例） */}
        <div
          ref={detailSplitContainerRef}
          style={{
            display: 'flex',
            gap: 0,
            flex: 1,
            minHeight: 0,
            height: 'calc(100vh - 110px)',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* 左側列表區域 */}
          <div style={{
            flex: detailPanelVisible ? `0 0 ${detailSplitRatio * 100}%` : '1',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            overflow: 'hidden',
            transition: detailPanelVisible ? 'none' : 'flex 0.3s ease',
            minHeight: 0
          }}>

            {/* 統計卡片 */}
            <div style={{ flexShrink: 0 }}>
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small" bodyStyle={{ padding: '12px' }}>
                    <Statistic
                      title={t('workflowMonitor.totalInstancesCount')}
                      value={statistics.total}
                      prefix={<FileTextOutlined />}
                      valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small" bodyStyle={{ padding: '12px' }}>
                    <Statistic
                      title={t('workflowMonitor.runningCount')}
                      value={statistics.running}
                      prefix={<SyncOutlinedIcon spin />}
                      valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small" bodyStyle={{ padding: '12px' }}>
                    <Statistic
                      title={t('workflowMonitor.completedCount')}
                      value={statistics.completed}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small" bodyStyle={{ padding: '12px' }}>
                    <Statistic
                      title={t('workflowMonitor.successRate')}
                      value={statistics.successRate}
                      suffix="%"
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                    />
                  </Card>
                </Col>
              </Row>
            </div>

        {/* 篩選和搜索 */}
            <div style={{ flexShrink: 0 }}>
              <Card size="small" bodyStyle={{ padding: '12px' }}>
                <DefinitionCategoryTabs
                  iconType="workflow"
                  allLabel={t('workflowMonitor.filterAll')}
                  allValue={null}
                  value={filters.workflowDefinitionId}
                  allCount={workflowDefinitionCounts.total}
                  items={workflowDefinitionTabItems}
                  loading={loadingWorkflowDefinitionCounts}
                  onChange={handleWorkflowDefinitionFilter}
                />
                <Row gutter={[8, 8]} align="middle" wrap={false} style={{ flexWrap: 'nowrap' }}>
            <Col flex="150px">
              <Select
                placeholder={t('workflowMonitor.selectStatus')}
                value={filters.status}
                onChange={handleStatusFilter}
                style={{ width: '100%' }}
              >
                <Option value="all">{t('workflowMonitor.filterAll')}</Option>
                <Option value="running">{t('workflowMonitor.filterRunning')}</Option>
                <Option value="completed">{t('workflowMonitor.filterCompleted')}</Option>
                <Option value="failed">{t('workflowMonitor.filterFailed')}</Option>
              </Select>
            </Col>
            
            <Col flex="240px">
              <RangePicker
                placeholder={[t('workflowMonitor.startDateRange'), t('workflowMonitor.startDateRange')]}
                value={filters.startDateRange}
                onChange={handleStartDateRangeChange}
                style={{ width: '100%' }}
              />
            </Col>
            
            <Col flex="240px">
              <RangePicker
                placeholder={[t('workflowMonitor.endDateRange'), t('workflowMonitor.endDateRange')]}
                value={filters.endDateRange}
                onChange={handleEndDateRangeChange}
                style={{ width: '100%' }}
              />
            </Col>
            
            <Col flex="auto">
              <Search
                placeholder={t('workflowMonitor.searchPlaceholder')}
                value={filters.searchText}
                onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
                onSearch={handleSearch}
                style={{ width: '100%' }}
              />
            </Col>
            
            <Col flex="none">
              <Space>
                <Button
                  icon={<ClearOutlined />}
                  onClick={handleClearFilters}
                >
                  {t('workflowMonitor.clearFilters')}
                </Button>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={loadInstances}
                  loading={loading}
                >
                  {t('workflowMonitor.refresh')}
                </Button>
                
                <Tooltip title={t('workflowMonitor.autoRefreshSettings')}>
                  <Button 
                    icon={<SettingOutlined />}
                    onClick={handleOpenAutoRefreshSettings}
                  />
                </Tooltip>
              </Space>
            </Col>
          </Row>
        </Card>
            </div>

            {/* 實例列表 */}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Card 
                size="small"
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
                bodyStyle={{
                  padding: '12px',
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
          <div style={{ marginBottom: 12, flexShrink: 0 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Space>
                <Text strong>{t('workflowMonitor.instanceList')}</Text>
                <Badge count={instances.length} showZero />
              </Space>
              {selectedInstances.length > 0 && (
                <Alert
                  type="info"
                  showIcon
                  message={
                    <Space split={<Divider type="vertical" />} wrap>
                      <Text>{t('workflowMonitor.selectedInstances', { count: selectedInstances.length })}</Text>
                      {canPauseExecution && getApplicableInstances('pause').length > 0 && (
                        <Button
                          type="link"
                          size="small"
                          icon={<PauseCircleOutlined />}
                          onClick={() => handleBatchAction('pause', getApplicableInstances('pause'))}
                          loading={loading}
                        >
                          {t('workflowMonitor.batchPause')} ({getApplicableInstances('pause').length})
                        </Button>
                      )}
                      {canResumeExecution && getApplicableInstances('resume').length > 0 && (
                        <Button
                          type="link"
                          size="small"
                          icon={<PlayCircleOutlined />}
                          onClick={() => handleBatchAction('resume', getApplicableInstances('resume'))}
                          loading={loading}
                        >
                          {t('workflowMonitor.batchResume')} ({getApplicableInstances('resume').length})
                        </Button>
                      )}
                      {canCancelExecution && getApplicableInstances('cancel').length > 0 && (
                        <Button
                          type="link"
                          size="small"
                          icon={<StopOutlined />}
                          onClick={handleBatchCancel}
                          loading={loading}
                        >
                          {t('workflowMonitor.batchCancel')} ({getApplicableInstances('cancel').length})
                        </Button>
                      )}
                      {canRetryExecution && getApplicableInstances('retry').length > 0 && (
                        <Button
                          type="link"
                          size="small"
                          icon={<ReloadOutlined />}
                          onClick={() => handleBatchAction('retry', getApplicableInstances('retry'))}
                          loading={loading}
                        >
                          {t('workflowMonitor.batchRetry')} ({getApplicableInstances('retry').length})
                        </Button>
                      )}
                      {canDeleteExecution && (
                        <Button
                          type="link"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={handleBatchDelete}
                          loading={loading}
                        >
                          {t('workflowMonitor.batchDelete')} ({selectedInstances.length})
                        </Button>
                      )}
                      <Button
                        type="link"
                        size="small"
                        onClick={() => setSelectedInstances([])}
                      >
                        {t('workflowMonitor.clearSelection')}
                      </Button>
                    </Space>
                  }
                  style={{ marginBottom: 0 }}
                />
              )}
            </Space>
          </div>
          
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div ref={bindInstanceTableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <Table
                components={components}
                columns={mergedColumns}
                dataSource={instances}
                rowKey="id"
                loading={loading}
                pagination={false}
                rowSelection={{
                  selectedRowKeys: selectedInstances.map(i => i.id),
                  onChange: (selectedRowKeys, selectedRows) => {
                    setSelectedInstances(selectedRows);
                  }
                }}
                onChange={handleTableChange}
                scroll={{
                  x: 1420,
                  y: instanceTableScrollY ?? Math.max(120, window.innerHeight - 480)
                }}
                sticky={{
                  offsetHeader: 0
                }}
                onRow={(record) => ({
                  onClick: () => handleViewDetails(record),
                  style: {
                    cursor: 'pointer',
                    backgroundColor: selectedInstanceId === record.id ? '#e6f7ff' : 'transparent'
                  }
                })}
              />
            </div>
            <div style={{ flexShrink: 0, marginTop: 12, paddingTop: 4, textAlign: 'left' }}>
              <Pagination
                current={pagination.current || 1}
                pageSize={pagination.pageSize || 20}
                total={pagination.total || 0}
                showSizeChanger
                showQuickJumper
                pageSizeOptions={['10', '20', '50', '100']}
                showTotal={(total, range) =>
                  t('workflowMonitor.paginationTotal', { start: range[0], end: range[1], total })
                }
                onChange={(page, pageSize) => {
                  setPagination(prev => ({ ...prev, current: page, pageSize }));
                }}
                onShowSizeChange={(current, size) => {
                  setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                }}
              />
            </div>
          </div>
        </Card>
            </div>
          </div>

          {/* 可拖拉的左右分欄 Resizer（僅在 Instance Details 開啟時顯示） */}
          {detailPanelVisible && (
            <div
              role="separator"
              aria-orientation="vertical"
              onMouseDown={handleDetailSplitResizeStart}
              style={{
                flex: '0 0 8px',
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                minWidth: 8,
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div
                style={{
                  width: 4,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: '#d9d9d9'
                }}
              />
            </div>
          )}

          {/* 右側詳情面板 */}
          {detailPanelVisible && (
            <div style={{
              flex: `0 0 ${(1 - detailSplitRatio) * 100}%`,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              minWidth: 0,
              minHeight: 0,
              marginLeft: 8
            }}>
              {/* 詳情面板標題欄 */}
              <div style={{
                padding: '16px 24px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#fafafa',
                flexShrink: 0
              }}>
                <Title level={4} style={{ margin: 0 }}>
                  {t('workflowMonitor.instanceDetails')}
                </Title>
                <Button 
                  type="text" 
                  icon={<CloseOutlined />}
                  onClick={handleCloseDetailPanel}
                  style={{ 
                    color: '#666',
                    fontSize: '16px'
                  }}
                />
              </div>
              
              {/* 詳情面板內容：flex:1 + minHeight:0 讓高度由父層決定；overflow-y:scroll 強制顯示捲軸 */}
              <div
                ref={detailScrollRef}
                className="workflow-monitor-detail-scroll"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'scroll',
                  overflowX: 'hidden',
                }}
              >
          {selectedInstance && (
            <InstanceDetailModal 
              instance={selectedInstance}
              initialActiveTab={detailInitialTab}
              onClose={handleCloseDetailPanel}
              onOpenArtifact={openEmbeddedArtifactPreview}
              onViewMessageSend={handleViewMessageSend}
              onViewMessageSendDetail={handleViewMessageSendDetail}
              onViewDataSetQuery={(data) => {
                setDataSetQueryResult(data);
                setDataSetQueryModalVisible(true);
              }}
              onViewFormInstance={(formInstanceId) => {
                setSelectedFormInstanceId(formInstanceId);
                setEmbedFormVisible(true);
              }}
              userTimezoneOffset={userTimezoneOffset}
            />
          )}
          
          {/* 數據集查詢結果模態框 */}
          <Modal
            title="數據集查詢結果"
            open={dataSetQueryModalVisible}
            onCancel={() => setDataSetQueryModalVisible(false)}
            closeIcon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
            footer={[
              <Button key="close" onClick={() => setDataSetQueryModalVisible(false)}>
                {t('workflowMonitor.close')}
              </Button>
            ]}
            width={1200}
            style={{ top: 20 }}
          >
            {dataSetQueryResult && (
              <div>
                {/* 查詢信息摘要 */}
                <div style={{ 
                  background: '#f0f8ff', 
                  padding: '16px', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  border: '1px solid #1890ff'
                }}>
                  <Row gutter={[16, 8]}>
                    <Col span={6}>
                      <div>
                        <strong>步驟執行ID:</strong><br/>
                        <span style={{ color: '#666' }}>{dataSetQueryResult.stepExecutionId}</span>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div>
                        <strong>查詢類型:</strong><br/>
                        <Tag color="blue">{dataSetQueryResult.queryType || 'SELECT'}</Tag>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div>
                        <strong>記錄數量:</strong><br/>
                        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                          {dataSetQueryResult.recordCount || 0} 條
                        </span>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div>
                        <strong>執行時間:</strong><br/>
                        <span style={{ color: '#666' }}>
                          {dataSetQueryResult.executedAt ? 
                            TimezoneUtils.formatDateWithTimezone(dataSetQueryResult.executedAt, userTimezoneOffset) : 
                            '-'
                          }
                        </span>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* 查詢結果表格 */}
                {dataSetQueryResult.queryResult && (
                  <div>
                    <h4 style={{ marginBottom: '16px', color: '#1890ff' }}>
                      <BarChartOutlined style={{ marginRight: '8px' }} />
                      查詢結果詳情
                    </h4>
                    <DataSetQueryResultTable 
                      data={JSON.parse(dataSetQueryResult.queryResult)}
                      recordCount={dataSetQueryResult.recordCount}
                    />
                  </div>
                )}
              </div>
            )}
          </Modal>
              </div>
            </div>
          )}
        </div>
          </TabPane>
        </Tabs>

        {/* 刪除實例前：詢問是否同時刪除相關檔案（圖片/影片/文件等），或先下載備份再刪除 */}
        <Modal
          title={t('workflowMonitor.deleteFilesChoiceTitle')}
          width={720}
          open={fileDeleteChoiceModalVisible}
          onCancel={() => {
            if (fileDeleteBackupLoading) return;
            setFileDeleteChoiceModalVisible(false);
            setFileDeleteChoiceTarget(null);
          }}
          maskClosable={!fileDeleteBackupLoading}
          closable={!fileDeleteBackupLoading}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Button
                disabled={fileDeleteBackupLoading}
                onClick={() => {
                  setFileDeleteChoiceModalVisible(false);
                  setFileDeleteChoiceTarget(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flexShrink: 0 }}>
                <Button
                  disabled={fileDeleteBackupLoading}
                  style={{ minWidth: 220 }}
                  onClick={() => {
                    const target = fileDeleteChoiceTarget;
                    setFileDeleteChoiceModalVisible(false);
                    proceedDeleteAfterFileChoice(false, target);
                  }}
                >
                  {t('workflowMonitor.deleteFilesChoiceKeepFiles')}
                </Button>
                <Button
                  danger
                  disabled={fileDeleteBackupLoading}
                  style={{ minWidth: 220 }}
                  onClick={() => {
                    const target = fileDeleteChoiceTarget;
                    setFileDeleteChoiceModalVisible(false);
                    proceedDeleteAfterFileChoice(true, target);
                  }}
                >
                  {t('workflowMonitor.deleteFilesChoiceDeleteFiles')}
                </Button>
                <Button
                  type="primary"
                  loading={fileDeleteBackupLoading}
                  style={{ minWidth: 220 }}
                  onClick={() => handleDownloadThenDelete(fileDeleteChoiceTarget)}
                >
                  {t('workflowMonitor.deleteFilesChoiceDownloadThenDelete')}
                </Button>
              </div>
            </div>
          }
        >
          <p>
            {fileDeleteChoiceIsBatch
              ? t('workflowMonitor.deleteFilesChoiceContentBatch', { count: selectedInstances.length })
              : t('workflowMonitor.deleteFilesChoiceContentSingle', {
                  instanceId: fileDeleteChoiceTarget?.id,
                  workflowName: fileDeleteChoiceTarget?.workflowName || '-'
                })}
          </p>
        </Modal>

        {/* 單筆刪除：Email 觸發時詢問是否允許該郵件再次觸發 */}
        <Modal
          title={t('workflowMonitor.emailDeleteConfirmTitle')}
          width={720}
          open={emailDeleteModalVisible}
          onCancel={() => {
            setEmailDeleteModalVisible(false);
            setEmailDeleteInstance(null);
          }}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <Button
                onClick={() => {
                  setEmailDeleteModalVisible(false);
                  setEmailDeleteInstance(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
                <Button
                  type="primary"
                  style={{ minWidth: 280 }}
                  onClick={() => {
                    const inst = emailDeleteInstance;
                    setEmailDeleteModalVisible(false);
                    setEmailDeleteInstance(null);
                    if (inst) performDeleteInstance(inst, true, emailDeletePendingFilesRef.current);
                  }}
                >
                  {t('workflowMonitor.emailDeleteAllowRetrigger')}
                </Button>
                <Button
                  danger
                  style={{ minWidth: 280 }}
                  onClick={() => {
                    const inst = emailDeleteInstance;
                    setEmailDeleteModalVisible(false);
                    setEmailDeleteInstance(null);
                    if (inst) performDeleteInstance(inst, false, emailDeletePendingFilesRef.current);
                  }}
                >
                  {t('workflowMonitor.emailDeleteKeepProcessed')}
                </Button>
              </div>
            </div>
          }
        >
          <p>{t('workflowMonitor.emailDeleteConfirmContent')}</p>
        </Modal>

        {/* 批次刪除：所選中含 Email 觸發時詢問是否允許這些郵件再次觸發 */}
        <Modal
          title={t('workflowMonitor.batchDeleteEmailConfirmTitle')}
          width={720}
          open={batchDeleteEmailChoiceModalVisible}
          onCancel={() => setBatchDeleteEmailChoiceModalVisible(false)}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <Button onClick={() => setBatchDeleteEmailChoiceModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
                <Button
                  type="primary"
                  style={{ minWidth: 280 }}
                  onClick={() => {
                    setBatchDeleteEmailChoiceModalVisible(false);
                    handleBatchAction('delete', selectedInstances, { removeEmailProcessed: true, deleteFiles: emailDeletePendingFilesRef.current });
                  }}
                >
                  {t('workflowMonitor.emailDeleteAllowRetrigger')}
                </Button>
                <Button
                  danger
                  style={{ minWidth: 280 }}
                  onClick={() => {
                    setBatchDeleteEmailChoiceModalVisible(false);
                    handleBatchAction('delete', selectedInstances, { removeEmailProcessed: false, deleteFiles: emailDeletePendingFilesRef.current });
                  }}
                >
                  {t('workflowMonitor.emailDeleteKeepProcessed')}
                </Button>
              </div>
            </div>
          }
        >
          <p>{t('workflowMonitor.batchDeleteEmailConfirmContent', {
            total: selectedInstances.length,
            emailCount: selectedInstances.filter(isEmailTriggered).length
          })}</p>
        </Modal>

        {/* 自動刷新設定 */}
        <Modal
          title={t('workflowMonitor.autoRefreshSettings')}
          open={autoRefreshModalVisible}
          onCancel={() => setAutoRefreshModalVisible(false)}
          onOk={handleApplyAutoRefreshSettings}
          okText={t('common.confirm')}
          cancelText={t('common.cancel')}
          destroyOnClose
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text strong>{t('workflowMonitor.autoRefresh')}</Text>
              <Switch
                checked={modalAutoRefreshEnabled}
                onChange={setModalAutoRefreshEnabled}
                checkedChildren={t('common.yes')}
                unCheckedChildren={t('common.no')}
              />
            </div>
            <div>
              <Text strong>{t('workflowMonitor.refreshInterval')}</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <InputNumber
                  min={5}
                  max={600}
                  value={modalRefreshInterval}
                  onChange={(value) => setModalRefreshInterval(value ?? 5)}
                  style={{ width: 140 }}
                />
                <Text type="secondary">{t('workflowMonitor.seconds')}</Text>
              </div>
            </div>
          </Space>
        </Modal>

        <Modal
          title={embeddedArtifactPreview.title || t('workflowMonitor.artifactPreview')}
          open={embeddedArtifactPreview.open}
          onCancel={closeEmbeddedArtifactPreview}
          closeIcon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
          footer={null}
          width="100vw"
          style={{ top: 0, padding: 0, maxWidth: '100vw' }}
          styles={{ body: { height: 'calc(100vh - 55px)', padding: 8, overflow: 'hidden' }, header: { borderRadius: 0 }, content: { borderRadius: 0, padding: 0 } }}
          destroyOnClose
        >
          {embeddedArtifactPreview.type === 'image' ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111' }}>
              <img
                src={embeddedArtifactPreview.url}
                alt={embeddedArtifactPreview.title}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </div>
          ) : embeddedArtifactPreview.type === 'video' ? (
            <video src={embeddedArtifactPreview.url} controls style={{ width: '100%', maxHeight: '100%' }} />
          ) : embeddedArtifactPreview.type === 'audio' ? (
            <div style={{ padding: 24 }}>
              <audio src={embeddedArtifactPreview.url} controls style={{ width: '100%' }} />
            </div>
          ) : (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <iframe
                ref={reportIframeRef}
                src={embeddedArtifactPreview.url}
                title={embeddedArtifactPreview.title || t('workflowMonitor.artifactPreview')}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
              {/* 複製按鈕：只在 HTML Report（generateHtmlReport 節點）顯示，浮在內容框右上角 */}
              {embeddedArtifactPreview.kind === 'report' && (
                <Tooltip title="複製全文內容">
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<CopyOutlined />}
                    loading={copyingReport}
                    onClick={copyReportContent}
                    style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      zIndex: 10,
                      width: 36,
                      height: 36,
                      minWidth: 36,
                      padding: 0,
                      flex: 'none',
                      opacity: 0.85,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                    }}
                  />
                </Tooltip>
              )}
            </div>
          )}
        </Modal>

        {/* 消息發送詳情模態框 */}
        <Modal
          title={t('workflowMonitor.messageSendDetails')}
          visible={messageSendModalVisible}
          onCancel={() => setMessageSendModalVisible(false)}
          closeIcon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
          footer={null}
          width={1000}
        >
          {selectedMessageSend && (
            <MessageSendDetailModal 
              messageSend={selectedMessageSend} 
              onClose={() => setMessageSendModalVisible(false)}
              userTimezoneOffset={userTimezoneOffset}
            />
          )}
        </Modal>

        {/* 消息發送詳細狀態模態框 */}
        <Modal
          title={t('workflowMonitor.messageSendStatusDetails')}
          visible={messageSendDetailModalVisible}
          onCancel={() => setMessageSendDetailModalVisible(false)}
          closeIcon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
          footer={null}
          width={1200}
        >
          {selectedMessageSendDetail && (
            <MessageSendStatusDetailModal 
              messageSend={selectedMessageSendDetail} 
              onClose={() => setMessageSendDetailModalVisible(false)}
              onViewMessageSend={handleViewMessageSend}
              onViewMessageSendDetail={handleViewMessageSendDetail}
              userTimezoneOffset={userTimezoneOffset}
            />
          )}
        </Modal>

        {/* 新的消息發送狀態模態框 */}
        <MessageSendStatusModal
          visible={newMessageSendStatusModalVisible}
          onClose={() => {
            setNewMessageSendStatusModalVisible(false);
            setSelectedWorkflowStepExecutionId(null);
          }}
          messageSendId={selectedMessageSendId}
          workflowExecutionId={selectedWorkflowExecutionId}
          workflowStepExecutionId={selectedWorkflowStepExecutionId}
          nodeId={selectedNodeId}
          userTimezoneOffset={userTimezoneOffset}
        />

        {/* 聊天類節點對話檢視彈窗（chatBot / chatOrderingCart 共用） */}
        <ChatBotConversationModal
          visible={chatBotViewer.open}
          stepExecutionId={chatBotViewer.stepExecutionId}
          taskName={chatBotViewer.taskName}
          nodeKind={chatBotViewer.nodeKind || 'chatBot'}
          onClose={() => setChatBotViewer({ open: false, stepExecutionId: null, taskName: '', nodeKind: 'chatBot' })}
        />

        {/* 橫向看板 — 啟動來源彈窗（Start 節點直接開啟，不跳頁籤） */}
        <Modal
          title={<><InfoCircleOutlined style={{ marginRight: 6 }} />{t('workflowMonitor.triggerSource')}</>}
          open={horizontalBoardTriggerSourceModal.open}
          onCancel={() => setHorizontalBoardTriggerSourceModal({ open: false, instance: null, loading: false })}
          closeIcon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
          footer={[
            <Button key="close" onClick={() => setHorizontalBoardTriggerSourceModal({ open: false, instance: null, loading: false })}>
              {t('workflowMonitor.close')}
            </Button>
          ]}
          width="100vw"
          style={{ top: 0, padding: 0, maxWidth: '100vw' }}
          styles={{ body: { height: 'calc(100vh - 55px)', overflow: 'hidden', padding: 16, display: 'flex', flexDirection: 'column' }, header: { borderRadius: 0 }, content: { borderRadius: 0, padding: 0 } }}
          destroyOnClose
        >
          {horizontalBoardTriggerSourceModal.loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin />
            </div>
          ) : (
            <TriggerSourceContent
              instance={horizontalBoardTriggerSourceModal.instance}
              userTimezoneOffset={userTimezoneOffset}
            />
          )}
        </Modal>

        {/* 內嵌表單 Modal：標題列直接放置 Save / Approve / Reject / Open in new tab，與 PendingTasksPage 一致 */}
        <Modal
          title={(() => {
            const instance = embeddedFormInstance;
            const titleText = instance
              ? `${t('workflowMonitor.formInstance')}: ${instance.formName || t('workflowMonitor.unnamedForm')}`
              : t('workflowMonitor.formInstance');
            const approvalLabels = getEFormApprovalLabels(instance, t);
            const isMetaFlows = instance?.fillType === 'MetaFlows';
            const canSave = !!instance && !isMetaFlows;
            const canApproveOrReject = !!instance && isEformApprovalPending(instance.status) && !isMetaFlows;
            const canExportPdf = !!instance && !isMetaFlows;
            const anyBusy = embedFormSaving || embedFormApproving || embedFormRejecting || embedFormExportingPdf;

            const btnStyle = { borderRadius: 20, height: 32, display: 'inline-flex', alignItems: 'center' };
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span style={{ fontSize: '16px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 16 }}>
                  {titleText}
                </span>
                <div style={{ display: 'flex', gap: 8, marginRight: 16, flexShrink: 0, flexWrap: 'wrap' }}>
                  {canSave && (
                    <Button
                      key="save-header"
                      icon={<SaveOutlined />}
                      loading={embedFormSaving}
                      disabled={embedFormApproving || embedFormRejecting}
                      onClick={handleSaveEmbeddedForm}
                      style={{ ...btnStyle, minWidth: 72 }}
                    >
                      {t('pendingTasks.save') || '儲存'}
                    </Button>
                  )}
                  {canExportPdf && (
                    <Button
                      key="export-pdf-header"
                      icon={<FilePdfOutlined />}
                      loading={embedFormExportingPdf}
                      disabled={embedFormSaving || embedFormApproving || embedFormRejecting}
                      onClick={handleExportEmbeddedFormPdf}
                      style={{ ...btnStyle, minWidth: 96 }}
                    >
                      {t('common.exportPdf') || '匯出 PDF'}
                    </Button>
                  )}
                  {canApproveOrReject && (
                    <>
                      <Button
                        key="approve-header"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        loading={embedFormApproving}
                        disabled={embedFormSaving || embedFormRejecting}
                        onClick={() => handleEmbeddedFormApproval('approve')}
                        style={{ ...btnStyle, backgroundColor: '#52c41a', borderColor: '#52c41a', minWidth: 80 }}
                      >
                        {approvalLabels.approve}
                      </Button>
                      <Button
                        key="reject-header"
                        type="primary"
                        danger
                        icon={<CloseCircleOutlined />}
                        loading={embedFormRejecting}
                        disabled={embedFormSaving || embedFormApproving}
                        onClick={() => {
                          Modal.confirm({
                            title: approvalLabels.reject,
                            content: t('pendingTasks.confirmStatusChangeQuestion'),
                            okText: approvalLabels.reject,
                            okButtonProps: { danger: true },
                            cancelText: t('pendingTasks.cancel'),
                            onOk: () => handleEmbeddedFormApproval('reject')
                          });
                        }}
                        style={{ ...btnStyle, minWidth: 80 }}
                      >
                        {approvalLabels.reject}
                      </Button>
                    </>
                  )}
                  {!!instance && (
                    <Button
                      key="newTab-header"
                      onClick={() => window.open(`/eform-instance/${instance.id}`, '_blank')}
                      disabled={anyBusy}
                      style={{ ...btnStyle, minWidth: 80 }}
                    >
                      {t('pendingTasks.openInNewTab') || 'Open in new tab'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
          open={embedFormVisible}
          onCancel={handleCloseEmbeddedForm}
          afterClose={handleCloseEmbeddedForm}
          closeIcon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
          footer={null}
          width="90%"
          style={{ top: 20 }}
          zIndex={1050}
          destroyOnClose={true}
          maskClosable={false}
          className="embedded-form-modal"
        >
          {loadingEmbeddedForm ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>{t('workflowMonitor.loadingFormInstance')}</p>
            </div>
          ) : embeddedFormInstance ? (
            <div style={{ padding: '16px 24px 24px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <EFormViewerBody
                instance={embeddedFormInstance}
                relatedAttachments={embedFormRelatedAttachments}
                loadingAttachments={embedFormLoadingAttachments}
                userTimezoneOffset={userTimezoneOffset}
                processUserMessage={extractAiAnalysisResult}
                lightboxZIndex={2000}
                contentMinHeight={600}
                workflowInstanceId={embeddedFormInstance?.workflowExecutionId || null}
                workflowStepExecutionId={embeddedFormInstance?.workflowStepExecutionId || null}
                onAttachmentsUploaded={() => loadEmbedFormMediaFiles(embeddedFormInstance?.workflowExecutionId)}
                formContentSlot={
                  <div style={{
                    minHeight: 'calc(100vh - 220px)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <EFormContentViewer
                      fillType={embeddedFormInstance.fillType}
                      htmlCode={embeddedFormInstance.filledHtmlCode || embeddedFormInstance.htmlCode || ''}
                      fieldDisplaySettings={embeddedFormInstance.fieldDisplaySettings}
                      metaFlowJson={embeddedFormInstance.metaFlowJson}
                      fallback={
                        embedFormIframeSrc ? (
                          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <iframe
                              ref={embedFormIframeRef}
                              src={embedFormIframeSrc}
                              title={t('workflowMonitor.formContent')}
                              onLoad={() => {
                                setEmbedFormIframeLoaded(n => n + 1);
                                const iframe = embedFormIframeRef.current;
                                const doc = iframe?.contentDocument;
                                if (doc) injectFlatpickrIntoIframe(doc);
                              }}
                              style={{
                                width: '100%',
                                height: 'calc(100vh - 220px)',
                                border: 'none',
                                backgroundColor: '#fff',
                                display: 'block',
                              }}
                            />
                          </div>
                        ) : (
                          <div style={{ border: '1px solid #e8e8e8', borderRadius: '8px', padding: '20px', backgroundColor: '#fafafa', minHeight: '300px', overflow: 'auto', fontSize: '14px', lineHeight: '1.6' }}>
                            {t('workflowMonitor.loadingFormInstance')}
                          </div>
                        )
                      }
                    />
                  </div>
                }
              />
            </div>
          ) : (
            <Empty description={t('workflowMonitor.cannotLoadFormInstance')} />
          )}
        </Modal>
      </Content>
    </Layout>
  );
};

/** WorkflowEngine 寫入 OutputJson 的固定英文字串 → 語言包 */
function translateEngineOutputMessage(text, t) {
  if (text == null || text === '') return text;
  const s = String(text);
  if (s === 'User replied, continuing workflow') return t('workflowMonitor.outputUserRepliedContinuing');
  if (s === 'User provided response') return t('workflowMonitor.outputUserProvidedResponse');
  return text;
}

function parseLocationJsonPayload(raw) {
  if (raw == null || raw === '') return null;
  const str = typeof raw === 'string' ? raw.trim() : '';
  if (!str.startsWith('{')) return null;
  try {
    const j = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const lat = j.latitude ?? j.Latitude;
    const lng = j.longitude ?? j.Longitude;
    if (lat == null || lng == null) return null;
    const la = Number(lat);
    const ln = Number(lng);
    if (Number.isNaN(la) || Number.isNaN(ln)) return null;
    if (la < -90 || la > 90 || ln < -180 || ln > 180) return null;
    return {
      lat: la,
      lng: ln,
      name: j.name ?? j.Name ?? '',
      address: j.address ?? j.Address ?? '',
    };
  } catch {
    return null;
  }
}

function parseLocationFromStepValidation(v) {
  if (!v) return null;
  const mt = (v.messageType || '').toLowerCase();
  const fromUser = parseLocationJsonPayload(v.userMessage);
  const fromProcessed = parseLocationJsonPayload(v.processedData);
  if (mt === 'location') {
    return fromUser || fromProcessed;
  }
  return fromUser || fromProcessed;
}

function pickLocationFromValidations(validations) {
  if (!validations?.length) return null;
  for (let i = validations.length - 1; i >= 0; i--) {
    const p = parseLocationFromStepValidation(validations[i]);
    if (p) return p;
  }
  return null;
}

/** Modal / 隱藏容器內 Leaflet 需在尺寸可用後 invalidateSize，否則僅左上角有圖磚、其餘灰色 */
function WorkflowMonitorMapInvalidateSize() {
  const map = useMap();
  useEffect(() => {
    const invalidate = () => {
      map.invalidateSize({ animate: false });
    };
    invalidate();
    const raf = requestAnimationFrame(invalidate);
    const t0 = setTimeout(invalidate, 0);
    const t1 = setTimeout(invalidate, 100);
    const t2 = setTimeout(invalidate, 350);
    const el = map.getContainer();
    const ro = new ResizeObserver(() => {
      invalidate();
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      ro.disconnect();
    };
  }, [map]);
  return null;
}

function WorkflowMonitorLocationMap({ lat, lng, height = 200, zoom = 15 }) {
  if (lat == null || lng == null) return null;
  return (
    <MapContainer
      key={`wm-map-${lat}-${lng}-${height}`}
      center={[lat, lng]}
      zoom={zoom}
      style={{ height, width: '100%', minHeight: height }}
      scrollWheelZoom={false}
      whenReady={(e) => {
        const m = e.target;
        const inv = () => m.invalidateSize({ animate: false });
        inv();
        requestAnimationFrame(inv);
        setTimeout(inv, 100);
      }}
    >
      <WorkflowMonitorMapInvalidateSize />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Marker position={[lat, lng]} />
    </MapContainer>
  );
}

const NODE_TYPE_TRANSLATION_KEYS = {
  'start': 'workflowDesigner.startNode',
  'end': 'workflowDesigner.endNode',
  'waitReply': 'workflowDesigner.waitReplyNode',
  'waitForQRCode': 'workflowDesigner.waitForQRCodeNode',
  'waitForLocation': 'workflowDesigner.waitForLocationNode',
  'waitForEmailReply': 'workflowDesigner.waitForEmailReplyNode',
  'sendWhatsApp': 'workflowDesigner.sendWhatsAppNode',
  'sendWhatsAppTemplate': 'workflowDesigner.sendTemplateNode',
  'sendWhatsAppShoppingCart': 'workflowDesigner.shoppingCartNode',
  'sendInstitutionOrderingCart': 'workflowDesigner.institutionOrderingCartNode',
  'chatOrderingCart': 'workflowDesigner.chatOrderingCartNode',
  'chatBot': 'workflowDesigner.chatBotNode',
  'switch': 'workflowDesigner.switchNode',
  'dataSetQuery': 'workflowDesigner.dataSetQueryNode',
  'processDataCell': 'workflowDesigner.processDataCellNode',
  'callApi': 'workflowDesigner.callApiNode',
  'sendEForm': 'workflowDesigner.sendEFormNode',
  'generateHtmlReport': 'workflowDesigner.generateHtmlReportNode',
  'generateDocument': 'workflowDesigner.generateDocumentNode',
  'eFormResult': 'workflowDesigner.eFormResultNode',
  'sendEmail': 'workflowDesigner.sendEmail',
};

const getNodeTypeLabel = (nodeType, t) => {
  if (!nodeType) return nodeType;
  const normalized =
    String(nodeType).toLowerCase() === 'waitforlocation' ? 'waitForLocation' : nodeType;
  const key = NODE_TYPE_TRANSLATION_KEYS[normalized];
  return key ? t(key) : nodeType;
};

const parseSwitchPathTarget = (edgePath) => {
  if (!edgePath) return null;
  // xy-edge__{sourceId}{handle}-source-{targetId}{handle}-target（targetId 可含多段 _copy_ 等）
  const match = edgePath.match(/-source-(.+?)(top|bottom|left|right)-target$/i);
  if (!match) return null;
  const targetNodeId = match[1];
  const typeMatch = targetNodeId.match(/^([a-zA-Z][a-zA-Z0-9]*)_/);
  return {
    nodeId: targetNodeId,
    nodeType: typeMatch ? typeMatch[1] : null
  };
};

/**
 * 將啟動 payload 中的 userId / from 等 GUID 替換為 details API 的 resolvedUserDisplayNames（無對應時保留原字串）。
 */
function applyResolvedUserLabelsToTriggerPayload(payload, instance) {
  if (!payload || typeof payload !== 'object' || !instance) return payload;
  const map = instance.resolvedUserDisplayNames;
  const isGuidString = (s) =>
    typeof s === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
  const lookup = (rawVal) => {
    if (rawVal == null || typeof rawVal !== 'string') return rawVal;
    const t = rawVal.trim();
    if (!isGuidString(t)) return rawVal;
    if (map && typeof map === 'object') {
      for (const [k, label] of Object.entries(map)) {
        if (k && String(k).toLowerCase() === t.toLowerCase()) return label;
      }
    }
    const cb = instance.createdBy != null ? String(instance.createdBy).trim() : '';
    if (cb && instance.createdByDisplayName && cb.toLowerCase() === t.toLowerCase()) {
      return instance.createdByDisplayName;
    }
    return rawVal;
  };
  const out = { ...payload };
  ['userId', 'UserId', 'from', 'From'].forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(out, k)) out[k] = lookup(out[k]);
  });
  return out;
}

/**
 * 將 email body HTML 中的 cid: 圖片來源替換為可見的實際圖片 URL 或 SVG 佔位符。
 * 瀏覽器無法解析 CID URL（僅 email client 支援），直接渲染會顯示破圖框。
 * @param {string} html - email body HTML
 * @param {Object} inlineImageMap - contentId → relativeUrl 對映（由後台下載後存入 InputJson）
 */
const sanitizeEmailBodyForDisplay = (html, inlineImageMap = {}) => {
  if (!html) return html;
  const placeholder = `data:image/svg+xml,` + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="380" height="52">` +
    `<rect width="380" height="52" fill="#f5f5f5" rx="4" stroke="#d9d9d9" stroke-width="1"/>` +
    `<text x="190" y="30" text-anchor="middle" fill="#888" font-family="sans-serif" font-size="12">` +
    `[📎 Inline image — visible in email client only]</text></svg>`
  );
  return html.replace(/src=["']cid:([^"']*)["']/gi, (match, contentId) => {
    const url = inlineImageMap && inlineImageMap[contentId];
    if (url) return `src="${url}"`;
    return `src="${placeholder}"`;
  });
};

// 啟動來源內容 — 共用於實例詳情 Tab 與橫向看板彈窗
const TriggerSourceContent = ({ instance, userTimezoneOffset }) => {
  const { t } = useLanguage();
  const raw = instance?.inputJson;
  if (raw == null || raw === '') {
    return <Empty description={t('workflowMonitor.triggerSourceNoInput')} />;
  }
  let data;
  let rawStringFallback = null;
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    data = raw;
  } else if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      rawStringFallback = raw;
    }
  } else {
    return <Empty description={t('workflowMonitor.triggerSourceNoInput')} />;
  }
  if (rawStringFallback != null) {
    return (
      <Card size="small" title={t('workflowMonitor.triggerSourceRawJson')}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 400, overflow: 'auto' }}>{rawStringFallback}</pre>
      </Card>
    );
  }
  if (!data || typeof data !== 'object') {
    return <Empty description={t('workflowMonitor.triggerSourceNoInput')} />;
  }

  const triggerSourceLower = String(data.triggerSource ?? data.TriggerSource ?? '').toLowerCase();

  if (triggerSourceLower === 'email') {
    const from = data.from ?? '';
    const to = data.to ?? '';
    const subject = data.subject ?? '';
    const body = data.body ?? '';
    const receivedAt = data.receivedAt != null ? TimezoneUtils.formatDateWithTimezone(data.receivedAt, userTimezoneOffset) : '—';
    const messageId = data.messageId ?? '';
    const attachmentUrls = Array.isArray(data.attachmentUrls) ? data.attachmentUrls : [];
    const inlineImageMap = (data.inlineImageMap && typeof data.inlineImageMap === 'object') ? data.inlineImageMap : {};
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12 }}>
        {/* 可收合的 Email 標頭 card */}
        <Collapse
          defaultActiveKey={[]}
          size="small"
          items={[{
            key: 'header',
            label: (
              <span style={{ fontWeight: 500 }}>
                <MailOutlined style={{ marginRight: 6 }} />
                {t('workflowMonitor.triggerSourceEmail')}
                {subject ? <span style={{ marginLeft: 8, color: '#555', fontWeight: 400 }}>— {subject}</span> : null}
              </span>
            ),
            children: (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t('workflowMonitor.triggerSourceFrom')}>{from || '—'}</Descriptions.Item>
                <Descriptions.Item label={t('workflowMonitor.triggerSourceTo')}>{to || '—'}</Descriptions.Item>
                <Descriptions.Item label={t('workflowMonitor.triggerSourceSubject')}>{subject || '—'}</Descriptions.Item>
                <Descriptions.Item label={t('workflowMonitor.triggerSourceReceivedAt')}>{receivedAt}</Descriptions.Item>
                <Descriptions.Item label={t('workflowMonitor.triggerSourceMessageId')}>
                  <Text copyable style={{ wordBreak: 'break-all' }}>{messageId || '—'}</Text>
                </Descriptions.Item>
                {attachmentUrls.length > 0 && (
                  <Descriptions.Item label={t('workflowMonitor.triggerSourceAttachments')}>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {attachmentUrls.map((url, i) => {
                        const safeUrl = encodeUploadUrlFilename(url);
                        const href = url.startsWith('http') ? safeUrl : (window.location.origin + (safeUrl.startsWith('/') ? safeUrl : '/' + safeUrl));
                        return (
                          <li key={i}>
                            <a href={href} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{safeUrl}</a>
                            <Text copyable={{ text: href }} style={{ marginLeft: 8, fontSize: 12 }} />
                          </li>
                        );
                      })}
                    </ul>
                  </Descriptions.Item>
                )}
              </Descriptions>
            )
          }]}
        />
        {/* Email 內文 — 全屏 modal 內佔滿剩餘高度；tab 內 minHeight 320 */}
        {body ? (
          <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', minHeight: 320, border: '1px solid #e8e8e8', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '6px 12px', background: '#fafafa', borderBottom: '1px solid #e8e8e8', fontSize: 12, color: '#666', fontWeight: 500, flexShrink: 0 }}>
              {t('workflowMonitor.triggerSourceBody')}
            </div>
            <div
              className="email-body-html"
              style={{ flex: 1, overflow: 'auto', wordBreak: 'break-word', fontSize: 13, padding: 16, minHeight: 0 }}
              dangerouslySetInnerHTML={{ __html: sanitizeEmailBodyForDisplay(body, inlineImageMap) }}
            />
          </div>
        ) : (
          <Empty description={t('workflowMonitor.triggerSourceNoInput')} style={{ marginTop: 40 }} />
        )}
      </div>
    );
  }

  if (data.Source === 'MetaWebhook' || data.WaId != null) {
    const contactName = data.ContactName ?? '—';
    const waId = data.WaId ?? '—';
    const messageId = data.MessageId ?? '—';
    const messageText = data.MessageText ?? data.Caption ?? '—';
    const timestamp = data.Timestamp != null ? TimezoneUtils.formatDateWithTimezone(data.Timestamp, userTimezoneOffset) : '—';
    const messageType = data.MessageType ?? data.InteractiveType ?? '—';
    return (
      <Card size="small" title={<><MessageOutlined /> {t('workflowMonitor.triggerSourceWhatsApp')}</>}>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={t('workflowMonitor.triggerSourceContact')}>{contactName}</Descriptions.Item>
          <Descriptions.Item label="WaId">{waId}</Descriptions.Item>
          <Descriptions.Item label={t('workflowMonitor.triggerSourceMessageId')}>
            <Text copyable style={{ wordBreak: 'break-all' }}>{messageId}</Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('workflowMonitor.triggerSourceMessageText')}>{messageText}</Descriptions.Item>
          <Descriptions.Item label={t('workflowMonitor.triggerSourceTimestamp')}>{timestamp}</Descriptions.Item>
          <Descriptions.Item label={t('workflowMonitor.messageType')}>{messageType}</Descriptions.Item>
          {data.PhoneNumberId != null && <Descriptions.Item label="PhoneNumberId">{data.PhoneNumberId}</Descriptions.Item>}
        </Descriptions>
      </Card>
    );
  }

  if (triggerSourceLower === 'manual') {
    const displayPayload = applyResolvedUserLabelsToTriggerPayload(data, instance);
    return (
      <Card size="small" title={<><RocketOutlined /> {t('workflowMonitor.triggerSourcePublishedManual')}</>}>
        <div
          className="workflow-monitor-trigger-source-kv-table"
          style={{ maxHeight: 560, overflow: 'auto' }}
          dangerouslySetInnerHTML={{ __html: convertJsonToHtmlTable(displayPayload) }}
        />
      </Card>
    );
  }

  const displayPayloadUnknown = applyResolvedUserLabelsToTriggerPayload(data, instance);
  return (
    <Card size="small" title={t('workflowMonitor.triggerSourceStructured')}>
      <div
        className="workflow-monitor-trigger-source-kv-table"
        style={{ maxHeight: 560, overflow: 'auto' }}
        dangerouslySetInnerHTML={{ __html: convertJsonToHtmlTable(displayPayloadUnknown) }}
      />
    </Card>
  );
};

// 實例詳情組件
const InstanceDetailModal = ({ instance, initialActiveTab, onClose, onOpenArtifact, onViewMessageSend, onViewMessageSendDetail, onViewDataSetQuery, onViewFormInstance, userTimezoneOffset }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(initialActiveTab || 'history');

  const getStepPreviewTitle = (step, fallback) => {
    return step?.taskName || step?.stepName || step?.nodeName || fallback;
  };

  const getStepPreviewTitleByExecutionId = (workflowStepExecutionId, fallback) => {
    const matchedStep = instance.stepExecutions?.find(step => String(step.id) === String(workflowStepExecutionId));
    return getStepPreviewTitle(matchedStep, fallback);
  };

  // 當打開詳情時指定了 initialActiveTab，自動切換到該 tab
  useEffect(() => {
    if (instance?.id && initialActiveTab) {
      setActiveTab(initialActiveTab);
    }
  }, [instance?.id, initialActiveTab]);

  const [eformInstances, setEformInstances] = useState([]);
  const [loadingEforms, setLoadingEforms] = useState(false);
  const [processVariables, setProcessVariables] = useState([]);
  const [loadingProcessVariables, setLoadingProcessVariables] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loadingMediaFiles, setLoadingMediaFiles] = useState(false);
  const [reportInstances, setReportInstances] = useState([]);
  const [loadingReportInstances, setLoadingReportInstances] = useState(false);
  const [docInstances, setDocInstances] = useState([]);
  const [loadingDocInstances, setLoadingDocInstances] = useState(false);
  const [overlayEditorDocId, setOverlayEditorDocId] = useState(null);
  const [messageValidations, setMessageValidations] = useState([]);
  const [loadingMessageValidations, setLoadingMessageValidations] = useState(false);
  const [lightboxVisible, setLightboxVisible] = useState(false);
  const [lightboxFile, setLightboxFile] = useState(null);
  const [lightboxFiles, setLightboxFiles] = useState([]);
  const [lightboxCurrentIndex, setLightboxCurrentIndex] = useState(0);
  const [lightboxTransform, setLightboxTransform] = useState({
    rotate: 0,
    scale: 1,
    flipH: false,
    flipV: false
  });
  const [locationMapModal, setLocationMapModal] = useState({
    open: false,
    lat: null,
    lng: null,
    name: '',
    address: '',
  });

  // 聊天類節點對話檢視彈窗（支援 chatBot 與 chatOrderingCart）
  const [chatBotViewer, setChatBotViewer] = useState({
    open: false,
    stepExecutionId: null,
    taskName: '',
    nodeKind: 'chatBot'
  });

  // 載入表單實例數據
  useEffect(() => {
    if (activeTab === 'forms') {
      loadEformInstances();
    }
  }, [activeTab, instance.id]);

  // 載入流程變量數據
  useEffect(() => {
    if (activeTab === 'variables') {
      loadProcessVariables();
    }
  }, [activeTab, instance.id]);

  // 載入媒體文件數據
  useEffect(() => {
    if (activeTab === 'media') {
      loadMediaFiles();
    }
  }, [activeTab, instance.id]);

  // 載入 HTML Report 實例數據
  useEffect(() => {
    if (activeTab === 'reports') {
      loadReportInstances();
    }
    // loadReportInstances follows the existing tab-load pattern in this detail component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, instance.id]);

  // 載入文件生成實例數據
  useEffect(() => {
    if (activeTab === 'documents') {
      loadDocInstances();
    }
    // loadDocInstances follows the existing tab-load pattern in this detail component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, instance.id]);

  // 載入消息驗證數據
  useEffect(() => {
    if (activeTab === 'history') {
      loadMessageValidations();
    }
  }, [activeTab, instance.id]);


  // 鍵盤快捷鍵支持
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!lightboxVisible) return;
      
      switch (event.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          if (lightboxFiles.length > 1) {
            goToPrevious();
          }
          break;
        case 'ArrowRight':
          if (lightboxFiles.length > 1) {
            goToNext();
          }
          break;
        case 'r':
        case 'R':
          if (getFileType(lightboxFile?.fileName) === 'image') {
            rotateImage('right');
          }
          break;
        case 'l':
        case 'L':
          if (getFileType(lightboxFile?.fileName) === 'image') {
            rotateImage('left');
          }
          break;
        case 'h':
        case 'H':
          if (getFileType(lightboxFile?.fileName) === 'image') {
            flipImage('horizontal');
          }
          break;
        case 'v':
        case 'V':
          if (getFileType(lightboxFile?.fileName) === 'image') {
            flipImage('vertical');
          }
          break;
        case '+':
        case '=':
          if (getFileType(lightboxFile?.fileName) === 'image') {
            zoomImage('in');
          }
          break;
        case '-':
          if (getFileType(lightboxFile?.fileName) === 'image') {
            zoomImage('out');
          }
          break;
        case '0':
          if (getFileType(lightboxFile?.fileName) === 'image') {
            resetTransform();
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxVisible, lightboxFiles, lightboxFile]);

  const loadEformInstances = async () => {
    try {
      setLoadingEforms(true);
      console.log(t('workflowMonitor.loadingEformInstances', { instanceId: instance.id }));
      
      const response = await fetch(`/api/workflowexecutions/${instance.id}/eform-instances`);
      console.log(t('workflowMonitor.apiResponseStatus'), response.status);
      console.log(t('workflowMonitor.apiResponseStatusText'), response.statusText);
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log(t('workflowMonitor.apiEndpointNotExists'));
          // 如果 API 端點不存在，顯示提示信息
          setEformInstances([]);
          message.warning(t('workflowMonitor.eformApiNotImplemented'));
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log(t('workflowMonitor.loadedEformData'), data);
      setEformInstances(data);
    } catch (error) {
      console.error(t('workflowMonitor.loadEformInstancesFailed'), error);
      
      // 根據錯誤類型顯示不同的提示信息
      if (error.message.includes('404')) {
        message.error(t('workflowMonitor.eformApiNotExists'));
      } else if (error.message.includes('500')) {
        message.error(t('workflowMonitor.backendServerError'));
      } else {
        message.error(t('workflowMonitor.loadEformInstancesFailed') + ': ' + error.message);
      }
      
      setEformInstances([]);
    } finally {
      setLoadingEforms(false);
    }
  };

  const loadProcessVariables = async () => {
    try {
      setLoadingProcessVariables(true);
      console.log(t('workflowMonitor.loadingProcessVariables', { instanceId: instance.id }));
      
      const response = await fetch(`/api/processvariables/instance-values/${instance.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log(t('workflowMonitor.processVariablesApiNotExists'));
          setProcessVariables([]);
          message.warning(t('workflowMonitor.processVariablesApiNotImplemented'));
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log(t('workflowMonitor.loadedProcessVariablesData'), data);
      setProcessVariables(data.data || []);
    } catch (error) {
      console.error(t('workflowMonitor.loadProcessVariablesFailed'), error);
      
      if (error.message.includes('404')) {
        message.error(t('workflowMonitor.processVariablesApiNotExists'));
      } else if (error.message.includes('500')) {
        message.error(t('workflowMonitor.backendServerError'));
      } else {
        message.error(t('workflowMonitor.loadProcessVariablesFailed') + ': ' + error.message);
      }
      
      setProcessVariables([]);
    } finally {
      setLoadingProcessVariables(false);
    }
  };

  const loadReportInstances = async () => {
    try {
      setLoadingReportInstances(true);
      const response = await fetch(`/api/workflowexecutions/${instance.id}/report-instances`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setReportInstances(data.data || []);
    } catch (error) {
      console.error(t('workflowMonitor.loadReportInstancesFailed'), error);
      message.error((t('workflowMonitor.loadReportInstancesFailed') || '載入 HTML Report 實例失敗') + ': ' + error.message);
      setReportInstances([]);
    } finally {
      setLoadingReportInstances(false);
    }
  };

  const loadDocInstances = async () => {
    try {
      setLoadingDocInstances(true);
      const response = await fetch(`/api/workflowexecutions/${instance.id}/doc-instances`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setDocInstances(data.data || []);
    } catch (error) {
      console.error(t('workflowMonitor.loadDocInstancesFailed'), error);
      message.error((t('workflowMonitor.loadDocInstancesFailed') || '載入文件生成實例失敗') + ': ' + error.message);
      setDocInstances([]);
    } finally {
      setLoadingDocInstances(false);
    }
  };

  const loadMediaFiles = async () => {
    try {
      setLoadingMediaFiles(true);
      console.log('🔵 [WorkflowMonitor] loadMediaFiles STARTED for instance:', instance.id);
      console.log(t('workflowMonitor.loadingMediaFiles', { instanceId: instance.id }));
      
      // 並行獲取媒體文件、步驟執行信息和消息驗證記錄
      const [mediaFilesResponse, stepExecutionsResponse, messageValidationsResponse] = await Promise.all([
        fetch(`/api/workflowexecutions/${instance.id}/media-files`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }),
        fetch(`/api/workflowexecutions/${instance.id}/details`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }),
        fetch(`/api/workflowexecutions/${instance.id}/message-validations`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
      ]);
      
      // 處理媒體文件
      let mediaFiles = [];
      if (mediaFilesResponse.ok) {
        const mediaData = await mediaFilesResponse.json();
        mediaFiles = mediaData.data || [];
        console.log('🔵 [WorkflowMonitor] Raw media files loaded:', mediaFiles.length, mediaFiles);
      } else if (mediaFilesResponse.status === 404) {
        console.log(t('workflowMonitor.mediaFilesApiNotExists'));
        setMediaFiles([]);
        message.warning(t('workflowMonitor.mediaFilesApiNotImplemented'));
        return;
      } else {
        throw new Error(`HTTP ${mediaFilesResponse.status}: ${mediaFilesResponse.statusText}`);
      }
      
      // 處理步驟執行信息
      let stepExecutions = [];
      if (stepExecutionsResponse.ok) {
        const stepData = await stepExecutionsResponse.json();
        stepExecutions = stepData.stepExecutions || [];
      } else {
        console.warn('Failed to load step executions:', stepExecutionsResponse.status);
      }
      
      // 處理消息驗證記錄
      let messageValidations = [];
      if (messageValidationsResponse.ok) {
        const validationData = await messageValidationsResponse.json();
        messageValidations = validationData.data || [];
      } else {
        console.warn('Failed to load message validations:', messageValidationsResponse.status);
      }
      
      // 創建步驟索引到步驟名稱的映射
      const stepIndexToNameMap = {};
      stepExecutions.forEach(step => {
        stepIndexToNameMap[step.stepIndex] = step.stepName || step.stepType || `Step ${step.stepIndex}`;
      });
      
      console.log('[WorkflowMonitor] Step index to name map:', stepIndexToNameMap);
      
      // 創建媒體URL到步驟索引的映射（通過消息驗證記錄）
      // ✅ 現在 Meta Flows 的圖片也會創建 MessageValidation 記錄，所以統一通過這個映射匹配
      const mediaUrlToStepIndexMap = {};
      messageValidations.forEach(validation => {
        if (validation.mediaUrl) {
          // 標準化路徑格式以便匹配
          const normalizedUrl = validation.mediaUrl.replace(/\\/g, '/');
          mediaUrlToStepIndexMap[normalizedUrl] = validation.stepIndex;
        }
      });
      
      console.log('[WorkflowMonitor] Media URL to step index map:', mediaUrlToStepIndexMap);
      console.log('[WorkflowMonitor] Total media files to enrich:', mediaFiles.length);
      
      // 為每個媒體文件添加步驟信息
      const enrichedMediaFiles = mediaFiles.map((file, index) => {
        // 標準化文件路徑以便匹配
        const normalizedFilePath = file.filePath.replace(/\\/g, '/');
        // 移除前導斜線以便匹配
        const normalizedFilePathNoLeading = normalizedFilePath.startsWith('/') 
          ? normalizedFilePath.substring(1) 
          : normalizedFilePath;
        
        // 嘗試從消息驗證記錄中獲取步驟索引
        let stepIndex = null;
        let stepName = '未知步驟';
        
        console.log(`[WorkflowMonitor] Processing file ${index + 1}/${mediaFiles.length}:`, {
          fileName: file.fileName,
          filePath: file.filePath,
          normalizedFilePath,
          normalizedFilePathNoLeading
        });
        
        // 方法1: 通過完整的文件路徑匹配（帶前導斜線）
        if (mediaUrlToStepIndexMap[normalizedFilePath]) {
          stepIndex = mediaUrlToStepIndexMap[normalizedFilePath];
          console.log(`[WorkflowMonitor] Matched via method 1 (full path with leading slash): stepIndex=${stepIndex}`);
        }
        // 方法2: 通過完整的文件路徑匹配（不帶前導斜線）
        else if (mediaUrlToStepIndexMap[normalizedFilePathNoLeading]) {
          stepIndex = mediaUrlToStepIndexMap[normalizedFilePathNoLeading];
          console.log(`[WorkflowMonitor] Matched via method 2 (full path without leading slash): stepIndex=${stepIndex}`);
        }
        // 方法3: 通過文件名匹配（如果路徑不完整）
        if (stepIndex === null) {
          const fileName = file.fileName;
          console.log(`[WorkflowMonitor] Trying method 3 (filename matching) for: ${fileName}`);
          for (const [url, idx] of Object.entries(mediaUrlToStepIndexMap)) {
            // 標準化 URL 以便匹配
            const normalizedUrl = url.replace(/\\/g, '/');
            const normalizedUrlNoLeading = normalizedUrl.startsWith('/') 
              ? normalizedUrl.substring(1) 
              : normalizedUrl;
            
            // 檢查是否包含文件名
            if (normalizedUrl.includes(fileName) || normalizedUrlNoLeading.includes(fileName)) {
              stepIndex = idx;
              console.log(`[WorkflowMonitor] Matched via method 3a (URL contains filename): stepIndex=${stepIndex}, url=${url}`);
              break;
            }
            // 也檢查反向匹配（文件名包含 URL 的一部分）
            const urlFileName = normalizedUrl.split('/').pop() || normalizedUrlNoLeading.split('/').pop();
            if (urlFileName && (fileName.includes(urlFileName) || urlFileName.includes(fileName))) {
              stepIndex = idx;
              console.log(`[WorkflowMonitor] Matched via method 3b (filename contains URL filename): stepIndex=${stepIndex}, urlFileName=${urlFileName}`);
              break;
            }
          }
        }
        
        // 如果找到了步驟索引，獲取步驟名稱
        if (stepIndex !== null && stepIndexToNameMap[stepIndex]) {
          stepName = stepIndexToNameMap[stepIndex];
        }
        
        console.log('[WorkflowMonitor] Enriching media file result:', {
          fileName: file.fileName,
          filePath: file.filePath,
          stepIndex,
          stepName,
          availableStepIndices: Object.keys(stepIndexToNameMap),
          availableMediaUrls: Object.keys(mediaUrlToStepIndexMap)
        });
        
        return {
          ...file,
          stepIndex: stepIndex,
          stepName: stepName
        };
      });
      
      console.log('🔵 [WorkflowMonitor] Enriched media files:', enrichedMediaFiles);
      const grouped = enrichedMediaFiles.reduce((acc, file) => {
        const stepName = file.stepName || '未知步驟';
        if (!acc[stepName]) acc[stepName] = [];
        acc[stepName].push(file);
        return acc;
      }, {});
      console.log('🔵 [WorkflowMonitor] Grouped by step:', grouped);
      console.log('🔵 [WorkflowMonitor] Step names:', Object.keys(grouped));
      
      setMediaFiles(enrichedMediaFiles);
      console.log('🔵 [WorkflowMonitor] loadMediaFiles COMPLETED, setMediaFiles called with', enrichedMediaFiles.length, 'files');
    } catch (error) {
      console.error(t('workflowMonitor.loadMediaFilesFailed'), error);
      
      if (error.message.includes('404')) {
        message.error(t('workflowMonitor.mediaFilesApiNotExists'));
      } else if (error.message.includes('500')) {
        message.error(t('workflowMonitor.backendServerError'));
      } else {
        message.error(t('workflowMonitor.loadMediaFilesFailed') + ': ' + error.message);
      }
      
      setMediaFiles([]);
    } finally {
      setLoadingMediaFiles(false);
    }
  };

  const loadMessageValidations = async () => {
    try {
      setLoadingMessageValidations(true);
      console.log('加載消息驗證記錄...', instance.id);
      
      const response = await fetch(`/api/workflowexecutions/${instance.id}/message-validations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        console.log('消息驗證記錄 API 調用失敗:', response.status);
        setMessageValidations([]);
        return;
      }
      
      const data = await response.json();
      console.log('已載入消息驗證記錄:', data);
      setMessageValidations(data.data || []);
    } catch (error) {
      console.error('載入消息驗證記錄失敗:', error);
      setMessageValidations([]);
    } finally {
      setLoadingMessageValidations(false);
    }
  };


  const getEformStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'orange';
      case 'Approved': return 'green';
      case 'Rejected': return 'red';
      default: return 'default';
    }
  };

  const getEformStatusText = (status, eform = null) => {
    const approvalLabels = getEFormApprovalLabels(eform, t, {
      approve: 'workflowMonitor.eformStatusApproved',
      reject: 'workflowMonitor.eformStatusRejected',
    });
    switch (status) {
      case 'Pending': return t('workflowMonitor.eformStatusPending');
      case 'Approved': return approvalLabels.approve;
      case 'Rejected': return approvalLabels.reject;
      default: return status;
    }
  };

  const getSendReasonTag = (sendReason) => {
    const reasonConfig = {
      normal: { color: 'blue', text: t('workflowMonitor.sendReasonNormal') },
      retry: { color: 'orange', text: t('workflowMonitor.sendReasonRetry') },
      escalation: { color: 'red', text: t('workflowMonitor.sendReasonEscalation') },
      overdue: { color: 'purple', text: t('workflowMonitor.sendReasonOverdue') }
    };
    
    const config = reasonConfig[sendReason] || reasonConfig.normal;
    
    return (
      <Tag color={config.color}>
        {config.text}
      </Tag>
    );
  };

  const formatVariableValue = (value, dataType) => {
    if (value === null || value === undefined) {
      return '-';
    }

    switch (dataType.toLowerCase()) {
      case 'datetime':
        return TimezoneUtils.formatDateWithTimezone(value, userTimezoneOffset);
      case 'boolean':
        return value ? t('workflowMonitor.yes') : t('workflowMonitor.no');
      case 'json':
        try {
          const parsed = JSON.parse(value);
          // ✅ 如果是對象，使用 convertJsonToHtmlTable 轉換為 HTML 表格
          if (parsed && typeof parsed === 'object') {
            return <div dangerouslySetInnerHTML={{ __html: convertJsonToHtmlTable(parsed) }} />;
          }
          return JSON.stringify(parsed, null, 2);
        } catch {
          return value.toString();
        }
      case 'text':
        // ✅ 檢測 text 類型是否為 JSON 字符串
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length > 0) {
            try {
              const parsed = JSON.parse(value);
              // ✅ 如果是對象，使用 convertJsonToHtmlTable 轉換為 HTML 表格
              if (parsed && typeof parsed === 'object') {
                return <div dangerouslySetInnerHTML={{ __html: convertJsonToHtmlTable(parsed) }} />;
              }
              // 如果是數組或其他類型，格式化顯示
              return JSON.stringify(parsed, null, 2);
            } catch (e) {
              // 解析失敗，返回原始字符串
              return value.toString();
            }
          }
        }
        return value.toString();
      default:
        return value.toString();
    }
  };

  const getDataTypeColor = (dataType) => {
    switch (dataType.toLowerCase()) {
      case 'string': return 'blue';
      case 'int': 
      case 'decimal': return 'green';
      case 'datetime': return 'purple';
      case 'boolean': return 'orange';
      case 'text': return 'cyan';
      case 'json': return 'magenta';
      default: return 'default';
    }
  };

  // 媒體文件相關函數
  const getFileIcon = (fileName, file = null) => {
    if (!fileName) {
      return <FileOutlined style={{ color: '#8c8c8c', fontSize: '48px' }} />;
    }
    
    // 處理文件名，移除路徑，只保留文件名
    const fileNameOnly = fileName.split('/').pop().split('\\').pop();
    const fileNameLower = fileNameOnly.toLowerCase();
    
    // 提取擴展名
    let extension = '';
    if (fileNameOnly.includes('.')) {
      extension = fileNameOnly.split('.').pop().toLowerCase().trim();
    }
    
    // 檢查文件對象中的其他字段
    let fileType = null;
    let mimeType = null;
    let contentType = null;
    if (file) {
      fileType = file.fileType || file.type;
      mimeType = file.mimeType || file.mime;
      contentType = file.contentType || file.content_type;
    }
    
    wfmDebugLog('[WorkflowMonitor] getFileIcon:', { 
      fileName, 
      fileNameOnly, 
      fileNameLower, 
      extension,
      fileType,
      mimeType,
      contentType,
      file
    });
    
    // 優先根據 MIME 類型判斷
    if (mimeType || contentType) {
      const mime = (mimeType || contentType).toLowerCase();
      wfmDebugLog('[WorkflowMonitor] MIME type check:', mime);
      
      // PDF
      if (mime.includes('pdf') || mime === 'application/pdf') {
        return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '48px' }} />;
      }
      
      // Word 文檔
      if (mime.includes('word') || 
          mime.includes('msword') || 
          mime.includes('document.wordprocessingml') ||
          mime.includes('application/msword') ||
          mime.includes('application/vnd.openxmlformats-officedocument.wordprocessingml')) {
        return <FileWordOutlined style={{ color: '#1890ff', fontSize: '48px' }} />;
      }
      
      // Excel 表格
      if (mime.includes('excel') || 
          mime.includes('spreadsheet') || 
          mime.includes('ms-excel') ||
          mime.includes('spreadsheetml') ||
          mime.includes('application/vnd.ms-excel') ||
          mime.includes('application/vnd.openxmlformats-officedocument.spreadsheetml')) {
        return <FileExcelOutlined style={{ color: '#52c41a', fontSize: '48px' }} />;
      }
      
      // PowerPoint 簡報
      if (mime.includes('powerpoint') || 
          mime.includes('presentation') || 
          mime.includes('ms-powerpoint') ||
          mime.includes('presentationml') ||
          mime.includes('application/vnd.ms-powerpoint') ||
          mime.includes('application/vnd.openxmlformats-officedocument.presentationml')) {
        return <FilePptOutlined style={{ color: '#fa8c16', fontSize: '48px' }} />;
      }
    }
    
    // 根據擴展名判斷
    if (extension) {
      switch (extension) {
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
        case 'webp':
        case 'svg':
        case 'tiff':
        case 'ico':
          return <FileImageOutlined style={{ color: '#52c41a', fontSize: '48px' }} />;
        case 'mp4':
        case 'avi':
        case 'mov':
        case 'wmv':
        case 'flv':
        case 'webm':
        case 'mkv':
        case 'm4v':
        case '3gp':
          return <VideoCameraOutlined style={{ color: '#1890ff', fontSize: '48px' }} />;
        case 'mp3':
        case 'wav':
        case 'ogg':
        case 'aac':
        case 'flac':
        case 'm4a':
        case 'wma':
          return <FileOutlined style={{ color: '#fa8c16', fontSize: '48px' }} />;
        case 'pdf':
          return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '48px' }} />;
        case 'doc':
        case 'docx':
          return <FileWordOutlined style={{ color: '#1890ff', fontSize: '48px' }} />;
        case 'xls':
        case 'xlsx':
          return <FileExcelOutlined style={{ color: '#52c41a', fontSize: '48px' }} />;
        case 'ppt':
        case 'pptx':
          return <FilePptOutlined style={{ color: '#fa8c16', fontSize: '48px' }} />;
      }
    }
    
    // 如果沒有擴展名，根據文件名關鍵字判斷
    // 優先檢查具體的文件類型關鍵字
    if (fileNameLower.includes('pdf') || fileNameLower.endsWith('.pdf')) {
      return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '48px' }} />;
    }
    if (fileNameLower.includes('word') || fileNameLower.includes('doc') || fileNameLower.endsWith('.doc') || fileNameLower.endsWith('.docx')) {
      return <FileWordOutlined style={{ color: '#1890ff', fontSize: '48px' }} />;
    }
    if (fileNameLower.includes('excel') || fileNameLower.includes('xls') || fileNameLower.endsWith('.xls') || fileNameLower.endsWith('.xlsx')) {
      return <FileExcelOutlined style={{ color: '#52c41a', fontSize: '48px' }} />;
    }
    if (fileNameLower.includes('powerpoint') || fileNameLower.includes('ppt') || fileNameLower.endsWith('.ppt') || fileNameLower.endsWith('.pptx')) {
      return <FilePptOutlined style={{ color: '#fa8c16', fontSize: '48px' }} />;
    }
    
    // 檢查文件對象中的其他字段
    if (file) {
      // 檢查 fileType 字段
      const fileTypeStr = (file.fileType || file.type || '').toLowerCase();
      if (fileTypeStr.includes('pdf')) {
        return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '48px' }} />;
      }
      if (fileTypeStr.includes('word') || fileTypeStr.includes('doc')) {
        return <FileWordOutlined style={{ color: '#1890ff', fontSize: '48px' }} />;
      }
      if (fileTypeStr.includes('excel') || fileTypeStr.includes('xls')) {
        return <FileExcelOutlined style={{ color: '#52c41a', fontSize: '48px' }} />;
      }
      if (fileTypeStr.includes('powerpoint') || fileTypeStr.includes('ppt')) {
        return <FilePptOutlined style={{ color: '#fa8c16', fontSize: '48px' }} />;
      }
      
      // 檢查文件名中的其他字段（如 originalFileName）
      if (file.originalFileName) {
        const originalLower = file.originalFileName.toLowerCase();
        if (originalLower.includes('pdf') || originalLower.endsWith('.pdf')) {
          return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '48px' }} />;
        }
        if (originalLower.includes('word') || originalLower.includes('doc') || originalLower.endsWith('.doc') || originalLower.endsWith('.docx')) {
          return <FileWordOutlined style={{ color: '#1890ff', fontSize: '48px' }} />;
        }
        if (originalLower.includes('excel') || originalLower.includes('xls') || originalLower.endsWith('.xls') || originalLower.endsWith('.xlsx')) {
          return <FileExcelOutlined style={{ color: '#52c41a', fontSize: '48px' }} />;
        }
        if (originalLower.includes('powerpoint') || originalLower.includes('ppt') || originalLower.endsWith('.ppt') || originalLower.endsWith('.pptx')) {
          return <FilePptOutlined style={{ color: '#fa8c16', fontSize: '48px' }} />;
        }
      }
    }
    
    // 檢查其他媒體類型關鍵字
    if (fileNameLower.includes('image') || fileNameLower.includes('img') || fileNameLower.includes('photo') || fileNameLower.includes('picture')) {
      return <FileImageOutlined style={{ color: '#52c41a', fontSize: '48px' }} />;
    }
    if (fileNameLower.includes('video') || fileNameLower.includes('movie') || fileNameLower.includes('film')) {
      return <VideoCameraOutlined style={{ color: '#1890ff', fontSize: '48px' }} />;
    }
    if (fileNameLower.includes('audio') || fileNameLower.includes('sound') || fileNameLower.includes('music')) {
      return <FileOutlined style={{ color: '#fa8c16', fontSize: '48px' }} />;
    }
    
    // 對於 "document" 關鍵字，如果沒有其他信息，默認顯示 PDF 圖標（因為 WhatsApp 中 document 通常是 PDF）
    if (fileNameLower.includes('document') && !fileNameLower.includes('word') && !fileNameLower.includes('excel') && !fileNameLower.includes('powerpoint')) {
      return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '48px' }} />;
    }
    
    // 默認返回通用文件圖標
    return <FileOutlined style={{ color: '#8c8c8c', fontSize: '48px' }} />;
  };

  const getFileType = (fileName) => {
    if (!fileName) {
      return 'document';
    }
    
    // 處理文件名，移除路徑，只保留文件名
    const fileNameOnly = fileName.split('/').pop().split('\\').pop();
    const extension = fileNameOnly.includes('.') 
      ? fileNameOnly.split('.').pop().toLowerCase().trim()
      : '';
    
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
      case 'svg':
      case 'tiff':
      case 'ico':
        return 'image';
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv':
      case 'webm':
      case 'mkv':
      case 'm4v':
      case '3gp':
        return 'video';
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'aac':
      case 'flac':
      case 'm4a':
      case 'wma':
        return 'audio';
      default:
        return 'document';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) return '—';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const groupFilesByFolder = (files) => {
    const grouped = {};
    files.forEach(file => {
      const folder = file.folderPath || 'root';
      if (!grouped[folder]) {
        grouped[folder] = [];
      }
      grouped[folder].push(file);
    });
    return grouped;
  };

  // Lightbox 相關函數
  const openLightbox = (file, allFiles = []) => {
    const imageVideoFiles = allFiles.filter(f => {
      const fileType = getFileType(f.fileName);
      return fileType === 'image' || fileType === 'video';
    });
    
    const currentIndex = imageVideoFiles.findIndex(f => f.id === file.id);
    
    setLightboxFiles(imageVideoFiles);
    setLightboxFile(file);
    setLightboxCurrentIndex(currentIndex >= 0 ? currentIndex : 0);
    setLightboxTransform({
      rotate: 0,
      scale: 1,
      flipH: false,
      flipV: false
    });
    setLightboxVisible(true);
  };

  const closeLightbox = () => {
    setLightboxVisible(false);
    setLightboxFile(null);
    setLightboxFiles([]);
    setLightboxCurrentIndex(0);
    setLightboxTransform({
      rotate: 0,
      scale: 1,
      flipH: false,
      flipV: false
    });
  };

  const goToPrevious = () => {
    if (lightboxFiles.length > 0) {
      const newIndex = lightboxCurrentIndex > 0 ? lightboxCurrentIndex - 1 : lightboxFiles.length - 1;
      setLightboxCurrentIndex(newIndex);
      setLightboxFile(lightboxFiles[newIndex]);
      setLightboxTransform({
        rotate: 0,
        scale: 1,
        flipH: false,
        flipV: false
      });
    }
  };

  const goToNext = () => {
    if (lightboxFiles.length > 0) {
      const newIndex = lightboxCurrentIndex < lightboxFiles.length - 1 ? lightboxCurrentIndex + 1 : 0;
      setLightboxCurrentIndex(newIndex);
      setLightboxFile(lightboxFiles[newIndex]);
      setLightboxTransform({
        rotate: 0,
        scale: 1,
        flipH: false,
        flipV: false
      });
    }
  };

  const rotateImage = (direction) => {
    setLightboxTransform(prev => ({
      ...prev,
      rotate: prev.rotate + (direction === 'left' ? -90 : 90)
    }));
  };

  const flipImage = (direction) => {
    setLightboxTransform(prev => ({
      ...prev,
      flipH: direction === 'horizontal' ? !prev.flipH : prev.flipH,
      flipV: direction === 'vertical' ? !prev.flipV : prev.flipV
    }));
  };

  const zoomImage = (direction) => {
    setLightboxTransform(prev => ({
      ...prev,
      scale: direction === 'in' 
        ? Math.min(prev.scale * 1.2, 5) 
        : Math.max(prev.scale / 1.2, 0.1)
    }));
  };

  const resetTransform = () => {
    setLightboxTransform({
      rotate: 0,
      scale: 1,
      flipH: false,
      flipV: false
    });
  };


  return (
    <>
      <div className="workflow-monitor-instance-detail-root">
      <style>
        {`
        .workflow-monitor-instance-detail-root .ant-tabs-nav {
          padding: 8px 16px 0;
          background: #fff;
          position: sticky;
          top: 0;
          z-index: 2;
        }
        .workflow-monitor-instance-detail-root .ant-tabs-tabpane {
          padding: 16px 16px 24px;
        }
        .workflow-monitor-trigger-source-kv-table table {
          border: 1px solid #f0f0f0;
          border-radius: 8px;
          overflow: hidden;
        }
        .workflow-monitor-trigger-source-kv-table a {
          color: #1677ff;
        }
        `}
      </style>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={t('workflowMonitor.executionHistory')} key="history">
          <Timeline>
            <Timeline.Item color="green">
              <p>{t('workflowMonitor.workflowStarted')}</p>
              <p>{TimezoneUtils.formatDateWithTimezone(instance.startedAt, userTimezoneOffset)}</p>
            </Timeline.Item>
            {instance.stepExecutions && instance.stepExecutions.length > 0 ? (
              instance.stepExecutions.map((step, index) => {
                // 調試信息：檢查步驟數據結構（僅 WORKFLOW_MONITOR_DEBUG 開啟時輸出，
                // 否則每次 render 對每個 step 輸出十多行 log 會嚴重拖慢頁面）
                wfmDebugLog(t('workflowMonitor.stepData', { stepNumber: index + 1 }), step);
                wfmDebugLog(t('workflowMonitor.stepAvailableFields', { stepNumber: index + 1 }), Object.keys(step));
                wfmDebugLog(t('workflowMonitor.stepOutputJson', { stepNumber: index + 1 }), step.outputJson);
                wfmDebugLog(t('workflowMonitor.stepErrorMessage', { stepNumber: index + 1 }), step.errorMessage);
                
                // 解析 OutputJson 來判斷是否為錯誤
                let outputData = null;
                let isError = false;
                let displayMessage = '';
                
                // 嘗試多個可能的字段名稱
                const jsonContent = step.outputJson || step.OutputJson || step.output;
                
                if (jsonContent) {
                  try {
                    outputData = JSON.parse(jsonContent);
                    wfmDebugLog(t('workflowMonitor.stepParsedData', { stepNumber: index + 1 }), outputData);
                    
                    // 優先檢查 success 字段
                    if (outputData.success === true) {
                      isError = false;
                      displayMessage = outputData.message || t('workflowMonitor.operationSuccess');
                      wfmDebugLog(t('workflowMonitor.stepDetectedSuccess', { stepNumber: index + 1 }));
                    }
                    // 檢查是否包含錯誤信息
                    else if (outputData.error) {
                      isError = true;
                      displayMessage = outputData.error;
                      wfmDebugLog(t('workflowMonitor.stepDetectedError', { stepNumber: index + 1 }));
                    } 
                    // 檢查 message 字段
                    else if (outputData.message) {
                      // 檢查是否為成功的狀態更新消息
                      if (outputData.message.includes("User replied, continuing workflow") || 
                          outputData.message.includes("EForm sent successfully") ||
                          outputData.message.includes("Form already processed") ||
                          outputData.message.includes("waiting for approval") ||
                          outputData.message.includes("Waiting for user reply")) {
                        isError = false;
                        displayMessage = outputData.message;
                        wfmDebugLog(t('workflowMonitor.stepDetectedSuccessMessage', { stepNumber: index + 1 }));
                      } else {
                        // 默認情況下，message 字段通常表示信息，不是錯誤
                        isError = false;
                        displayMessage = outputData.message;
                        wfmDebugLog(t('workflowMonitor.stepDetectedNormalMessage', { stepNumber: index + 1 }));
                      }
                    }
                    // switch 節點的正常輸出由專屬 GUI 區塊渲染，不再顯示 raw JSON
                    else if (outputData.selectedPaths || outputData.selectedPath || outputData.evaluatedAt) {
                      isError = false;
                      displayMessage = '';
                      wfmDebugLog(t('workflowMonitor.stepDetectedSwitchOutput', { stepNumber: index + 1 }));
                    }
                    // 如果沒有明確的字段，檢查整個 JSON 內容
                    else {
                      // 如果沒有明確的錯誤標識，通常不是錯誤
                      isError = false;
                      displayMessage = JSON.stringify(outputData, null, 2);
                      wfmDebugLog(t('workflowMonitor.stepNoClearFields', { stepNumber: index + 1 }));
                    }
                  } catch (parseError) {
                    console.error(t('workflowMonitor.stepParseJsonFailed', { stepNumber: index + 1 }), parseError);
                    // 如果解析失敗，將原始內容作為普通信息顯示
                    displayMessage = jsonContent;
                    isError = false; // 解析失敗不一定是錯誤
                  }
                } else {
                  wfmDebugLog(t('workflowMonitor.stepNoJsonContentField', { stepNumber: index + 1 }));
                }
                
                wfmDebugLog(t('workflowMonitor.stepFinalResult', { stepNumber: index + 1 }), { isError, displayMessage });
                
                // 檢查是否為發送消息的節點
                const isMessageSendNode = (step.stepName && (
                  step.stepName.includes('sendWhatsApp') || 
                  step.stepName.includes('sendWhatsAppTemplate') ||
                  step.stepName.includes('sendEForm')
                )) || (step.stepType && (
                  step.stepType.includes('sendWhatsApp') || 
                  step.stepType.includes('sendWhatsAppTemplate') ||
                  step.stepType.includes('sendEForm')
                ));

                // 優先使用 taskName，如果沒有則使用 stepName
                const displayName = step.taskName || step.stepName || `${t('workflowMonitor.step')} ${index + 1}`;
                const nodeType = step.stepType || step.nodeType || step.type;
                
                // 檢查是否為 dataSetQuery 節點
                const isDataSetQueryNode = (step.stepName && step.stepName.includes('dataSetQuery')) || 
                                         (step.stepType && step.stepType.includes('dataSetQuery')) ||
                                         (nodeType === 'dataSetQuery');
                
                // 調試 dataSetQuery 節點
                if (isDataSetQueryNode) {
                  wfmDebugLog('🔍 dataSetQuery 節點檢測:', {
                    stepName: step.stepName,
                    stepType: step.stepType,
                    nodeType: nodeType,
                    outputData: outputData,
                    hasQueryResult: outputData && outputData.queryResult,
                    hasQueryResultId: outputData && outputData.queryResultId,
                    stepId: step.id
                  });
                }
                
                // 調試信息
                if (step.stepName && step.stepName.includes('sendWhatsApp')) {
                  wfmDebugLog(t('workflowMonitor.stepIsSendWhatsAppNode', { stepNumber: index + 1, stepName: step.stepName }), {
                    stepName: step.stepName,
                    stepType: step.stepType,
                    status: step.status,
                    isMessageSendNode: isMessageSendNode,
                    outputData: outputData,
                    hasMessageSendId: outputData && outputData.messageSendId
                  });
                }

                // 查找該步驟的用戶回覆（waitReply 或 waitForQRCode）
                // 使用 step.stepIndex 而不是數組索引 index
                const stepValidations = messageValidations.filter(mv => mv.stepIndex === step.stepIndex);
                const isWaitForLocationNode = nodeType === 'waitForLocation' || nodeType === 'waitforlocation';
                const locationPoint = isWaitForLocationNode ? pickLocationFromValidations(stepValidations) : null;
                const isWaitNode = nodeType === 'waitReply' || nodeType === 'waitForQRCode' || nodeType === 'waitforqrcode';
                
                // 調試日誌
                if (isWaitNode) {
                  wfmDebugLog(`🔍 等待節點 "${displayName}" (stepIndex: ${step.stepIndex}):`, {
                    nodeType,
                    stepIndex: step.stepIndex,
                    totalValidations: messageValidations.length,
                    matchedValidations: stepValidations.length,
                    validations: stepValidations
                  });
                }

                return (
                  <Timeline.Item 
                    key={step.id} 
                    color={(step.status === 'Completed' || step.status === 'completed') ? 'green' : (step.status === 'Failed' || step.status === 'failed') ? 'red' : 'blue'}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <Text strong style={{ fontSize: '15px' }}>
                          {displayName}
                        </Text>
                        {nodeType && (
                          <Tag color="blue">
                            {getNodeTypeLabel(nodeType, t)}
                          </Tag>
                        )}
                        {(() => {
                          const lowerNodeType = String(nodeType || '').toLowerCase();
                          const isChatBot = lowerNodeType === 'chatbot';
                          const isOrderingCart = lowerNodeType === 'chatorderingcart';
                          if (!isChatBot && !isOrderingCart) return null;
                          const labelKey = isOrderingCart
                            ? 'workflowMonitor.viewChatOrderingCartConversation'
                            : 'workflowMonitor.viewChatBotConversation';
                          return (
                            <Button
                              size="small"
                              type="link"
                              icon={<MessageOutlined />}
                              onClick={() => {
                                setChatBotViewer({
                                  open: true,
                                  stepExecutionId: step.id,
                                  taskName: step.taskName || step.stepName || displayName,
                                  nodeKind: isOrderingCart ? 'chatOrderingCart' : 'chatBot'
                                });
                              }}
                              style={{ padding: 0, height: 22, fontSize: 12 }}
                            >
                              {t(labelKey) || (isOrderingCart ? '查看購物車對話' : '查看對話')}
                            </Button>
                          );
                        })()}
                      </div>
                      <p>{t('workflowMonitor.stepStatus')}: {step.status}</p>
                      <p>{t('workflowMonitor.stepStartTime')}: {step.startedAt ? TimezoneUtils.formatDateWithTimezone(step.startedAt, userTimezoneOffset) : '-'}</p>
                      {step.endedAt && (
                        <p>{t('workflowMonitor.stepEndTime')}: {TimezoneUtils.formatDateWithTimezone(step.endedAt, userTimezoneOffset)}</p>
                      )}
                        
                        {/* 顯示用戶回覆（waitReply 或 waitForQRCode 節點） */}
                        {isWaitNode && stepValidations.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <Text strong style={{ color: '#1890ff' }}>{t('workflowMonitor.userReplies')}:</Text>
                            
                            {/* 按驗證狀態分組顯示 */}
                            {(() => {
                              // 分組：有效的和無效的
                              const validValidations = stepValidations.filter(v => v.isValid);
                              const invalidValidations = stepValidations.filter(v => !v.isValid);
                              
                              const renderValidationGroup = (validations, isValid, nodeType) => {
                                if (validations.length === 0) return null;
                                
                                // 分離文本消息和圖片消息
                                const textValidations = validations.filter(v => v.messageType === 'text');
                                const imageValidations = validations.filter(v => v.messageType === 'image');
                                
                                // 判斷是否為 QR Code 節點
                                const isQRCodeNode = nodeType === 'waitForQRCode' || nodeType === 'waitforqrcode';
                                
                                // 為 QR Code 節點的 invalid 使用不同的樣式（橙色）
                                const bgColor = isValid ? '#f6ffed' : (isQRCodeNode ? '#fff7e6' : '#fff2f0');
                                const borderColor = isValid ? '#b7eb8f' : (isQRCodeNode ? '#ffd591' : '#ffccc7');
                                
                                return (
                                  <div 
                                    style={{
                                      marginTop: '8px',
                                      padding: '12px',
                                      backgroundColor: bgColor,
                                      border: `1px solid ${borderColor}`,
                                      borderRadius: '6px'
                                    }}
                                  >
                                    {/* 文本消息 */}
                                    {textValidations.map((validation, idx) => (
                                      <div key={validation.id} style={{ marginBottom: idx < textValidations.length - 1 ? '8px' : '0' }}>
                                        <Text>{validation.userMessage}</Text>
                                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                                          {TimezoneUtils.formatDateWithTimezone(validation.createdAt, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss')}
                      </div>
                                      </div>
                                    ))}
                                    
                                    {/* 圖片消息 - 網格顯示 */}
                                    {imageValidations.length > 0 && (
                                      <div style={{ marginTop: textValidations.length > 0 ? '12px' : '0' }}>
                                        {/* 顯示所有 QR Code 結果和 Caption */}
                                        <div style={{ marginBottom: '8px' }}>
                                          {imageValidations.map((validation, idx) => {
                                            try {
                                              const processedData = validation.processedData ? JSON.parse(validation.processedData) : null;
                                              
                                              // waitForQRCode 節點：userMessage = QR Code 值，caption 在 processedData 中
                                              // waitReply 節點：userMessage = caption（圖片文字說明），processedData 可能沒有 caption
                                              let qrCodeValue = null;
                                              let caption = null;
                                              
                                              if (isQRCodeNode) {
                                                // QR Code 節點
                                                qrCodeValue = validation.userMessage;
                                                caption = processedData?.caption || '';
                                              } else {
                                                // waitReply 節點：直接使用 userMessage 作為文字說明
                                                caption = validation.userMessage || '';
                                              }
                                              
                                              return (
                                                <div key={validation.id} style={{ marginBottom: '4px' }}>
                                                  {/* 只在 waitForQRCode 節點顯示 QR Code 標籤 */}
                                                  {isQRCodeNode && qrCodeValue && (
                                                    <Tag color="green">QR Code: {qrCodeValue}</Tag>
                                                  )}
                                                  
                                                  {/* 顯示 Caption（圖片文字說明） */}
                                                  {caption && (
                                                    <Text style={{ marginLeft: (isQRCodeNode && qrCodeValue) ? '8px' : '0' }}>
                                                      <strong>{t('workflowMonitor.caption')}</strong>
                                                      {caption}
                                                    </Text>
                                                  )}
                                                  
                                                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                                                    {TimezoneUtils.formatDateWithTimezone(validation.createdAt, userTimezoneOffset, 'HH:mm:ss')}
                                                  </span>
                                                </div>
                                              );
                                            } catch (e) {
                                              // 解析失敗時的後備顯示
                                              const displayText = validation.userMessage;
                                              return displayText ? (
                                                <div key={validation.id} style={{ marginBottom: '4px' }}>
                                                  {/* waitReply 節點直接顯示文字，不加標籤 */}
                                                  {isQRCodeNode ? (
                                                    <Tag color="green">QR Code: {displayText}</Tag>
                                                  ) : (
                                                    <Text>{displayText}</Text>
                                                  )}
                                                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                                                    {TimezoneUtils.formatDateWithTimezone(validation.createdAt, userTimezoneOffset, 'HH:mm:ss')}
                                                  </span>
                                                </div>
                                              ) : null;
                                            }
                                          })}
                                        </div>
                                        
                                        {/* 圖片網格 - 響應式布局 */}
                                        <div style={{ 
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          gap: '8px',
                                          marginTop: '8px'
                                        }}>
                                          {imageValidations.map((validation) => (
                                            <div
                                              key={validation.id}
                                              style={{
                                                width: '100px',
                                                height: '100px',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '4px',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s, box-shadow 0.2s'
                                              }}
                                              onClick={() => {
                                                // 點擊打開 Lightbox，顯示所有圖片
                                                const allImages = imageValidations.map(v => ({
                                                  id: v.id,
                                                  fileName: `reply_${v.id}.jpg`,
                                                  filePath: v.mediaUrl,
                                                  fileSize: 0,
                                                  createdAt: v.createdAt
                                                }));
                                                const currentIndex = imageValidations.findIndex(v => v.id === validation.id);
                                                openLightbox(allImages[currentIndex], allImages);
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'scale(1.05)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'scale(1)';
                                                e.currentTarget.style.boxShadow = 'none';
                                              }}
                                            >
                                              <img
                                                src={validation.mediaUrl}
                                                alt="User reply"
                                                style={{
                                                  width: '100%',
                                                  height: '100%',
                                                  objectFit: 'cover'
                                                }}
                                                onError={(e) => {
                                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIj5JbWFnZTwvdGV4dD48L3N2Zz4=';
                                                }}
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* 驗證狀態標籤 */}
                                    <div style={{ 
                                      marginTop: '12px',
                                      paddingTop: '8px',
                                      borderTop: '1px solid ' + (isValid ? '#d9f7be' : (isQRCodeNode ? '#ffe7ba' : '#ffccc7'))
                                    }}>
                                      {/* 根據節點類型顯示不同的標籤 */}
                                      {isValid ? (
                                        <Tag color="success">
                                          {t('workflowMonitor.validationPassed')}
                                        </Tag>
                                      ) : (
                                        <Tag color={isQRCodeNode ? 'orange' : 'error'}>
                                          {isQRCodeNode ? t('workflowMonitor.others') : t('workflowMonitor.validationFailed')}
                                        </Tag>
                                      )}
                                      <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                                        {validations.length} {validations.length === 1 ? t('workflowMonitor.reply') : t('workflowMonitor.replies')}
                                      </span>
                                    </div>
                                  </div>
                                );
                              };
                              
                              return (
                                <>
                                  {renderValidationGroup(validValidations, true, nodeType)}
                                  {renderValidationGroup(invalidValidations, false, nodeType)}
                                </>
                              );
                            })()}
                          </div>
                        )}
                    
                    {/* switch 節點：以 GUI 形式顯示條件分支結果 */}
                    {outputData && nodeType === 'switch' && (outputData.selectedPaths || outputData.selectedPath) && (
                      <div style={{
                        marginTop: '8px',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        backgroundColor: '#f0f5ff',
                        border: '1px solid #adc6ff'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                          <BranchesOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                          <Text strong style={{ color: '#1890ff' }}>{t('workflowMonitor.switchEvaluationResult')}</Text>
                        </div>

                        {outputData.evaluatedAt && (
                          <div style={{ marginBottom: '8px', fontSize: '13px', color: '#666' }}>
                            {t('workflowMonitor.evaluatedAt')}: {TimezoneUtils.formatDateWithTimezone(outputData.evaluatedAt, userTimezoneOffset)}
                          </div>
                        )}

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                          {outputData.conditionGroupsCount != null && (
                            <Tag color="purple">{t('workflowMonitor.conditionGroupsCount')}: {outputData.conditionGroupsCount}</Tag>
                          )}
                          {outputData.defaultPathUsed ? (
                            <Tag color="orange">{t('workflowMonitor.defaultPathUsed')}</Tag>
                          ) : (
                            <Tag color="green"><CheckCircleOutlined style={{ marginRight: 4 }} />{t('workflowMonitor.conditionMatched')}</Tag>
                          )}
                        </div>

                        {(() => {
                          const paths = outputData.selectedPaths || (outputData.selectedPath ? [outputData.selectedPath] : []);
                          if (paths.length === 0) return null;
                          return paths.map((pathEdge, pi) => {
                            const target = parseSwitchPathTarget(pathEdge);
                            // 優先以 nodeId 對齊（同類型多節點時不可只用 stepType，否則會誤顯示第一個 sendWhatsApp 等）
                            const targetStep = target && instance.stepExecutions
                              ? (instance.stepExecutions.find(s => target.nodeId && s.nodeId === target.nodeId)
                                || instance.stepExecutions.find(s =>
                                  s.stepType === target.nodeType || s.nodeType === target.nodeType))
                              : null;
                            const targetName = targetStep ? (targetStep.taskName || targetStep.stepName) : null;
                            return (
                              <div key={pi} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 10px',
                                backgroundColor: '#fff',
                                borderRadius: '6px',
                                border: '1px solid #d9d9d9',
                                marginTop: pi > 0 ? '6px' : 0
                              }}>
                                <ArrowRightOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
                                <Text style={{ fontSize: '13px' }}>{t('workflowMonitor.selectedPathTarget')}:</Text>
                                {target && target.nodeType && (
                                  <Tag color="blue" style={{ margin: 0 }}>{getNodeTypeLabel(target.nodeType, t)}</Tag>
                                )}
                                {targetName && (
                                  <Text style={{ fontSize: '13px', color: '#333' }}>"{targetName}"</Text>
                                )}
                                {!target && (
                                  <Text style={{ fontSize: '12px', color: '#999' }}>{pathEdge}</Text>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}

                    {/* 顯示輸出信息，正確區分錯誤和正常信息 */}
                    {displayMessage && (
                      <div style={{ 
                        marginTop: '8px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        backgroundColor: isError ? '#fff2f0' : '#f6ffed',
                        border: `1px solid ${isError ? '#ffccc7' : '#b7eb8f'}`,
                        color: isError ? '#cf1322' : '#389e0d'
                      }}>
                        <strong>{isError ? t('workflowMonitor.error') + ': ' : t('workflowMonitor.information') + ': '}</strong>
                        {translateEngineOutputMessage(displayMessage, t)}
                        
                        {/* 如果有額外的輸出數據，顯示更多信息 */}
                        {outputData && outputData.timestamp && (
                          <div style={{ 
                            marginTop: '4px', 
                            fontSize: '12px', 
                            opacity: 0.7 
                          }}>
                            {t('workflowMonitor.time')}: {TimezoneUtils.formatDateWithTimezone(outputData.timestamp, userTimezoneOffset)}
                          </div>
                        )}
                        
                        {outputData && outputData.userResponse && (
                          <div style={{ 
                            marginTop: '8px', 
                            fontSize: '12px', 
                            opacity: 0.95,
                            color: isError ? undefined : '#389e0d',
                          }}>
                            {isWaitForLocationNode && locationPoint ? (
                              <>
                                <div style={{ marginBottom: '6px', fontWeight: 600 }}>{t('workflowMonitor.userResponse')}:</div>
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setLocationMapModal({
                                    open: true,
                                    lat: locationPoint.lat,
                                    lng: locationPoint.lng,
                                    name: locationPoint.name || '',
                                    address: locationPoint.address || '',
                                  })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setLocationMapModal({
                                        open: true,
                                        lat: locationPoint.lat,
                                        lng: locationPoint.lng,
                                        name: locationPoint.name || '',
                                        address: locationPoint.address || '',
                                      });
                                    }
                                  }}
                                  style={{
                                    position: 'relative',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    cursor: 'zoom-in',
                                    outline: 'none',
                                    border: '1px solid #95de64',
                                  }}
                                  aria-label={t('workflowMonitor.locationMapClickToEnlarge')}
                                >
                                  <WorkflowMonitorLocationMap lat={locationPoint.lat} lng={locationPoint.lng} height={200} zoom={15} />
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '8px',
                                      right: '8px',
                                      background: 'rgba(0,0,0,0.55)',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      pointerEvents: 'none',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                    }}
                                  >
                                    <ZoomInOutlined />
                                    {t('workflowMonitor.locationMapClickToEnlarge')}
                                  </div>
                                </div>
                                {(locationPoint.address || locationPoint.name) && (
                                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666', lineHeight: 1.5 }}>
                                    {locationPoint.name ? (
                                      <div><strong>{t('workflowMonitor.locationName')}:</strong> {locationPoint.name}</div>
                                    ) : null}
                                    {locationPoint.address ? (
                                      <div><strong>{t('workflowMonitor.locationAddress')}:</strong> {locationPoint.address}</div>
                                    ) : null}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div>
                                {t('workflowMonitor.userResponse')}: {translateEngineOutputMessage(outputData.userResponse, t)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {outputData && (outputData.reportUrl || outputData.docUrl || outputData.formInstanceId || (Array.isArray(outputData.instances) && outputData.instances.length > 0)) && (
                      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {outputData.formInstanceId && (
                          <Button size="small" icon={<FileTextOutlined />} onClick={() => onViewFormInstance && onViewFormInstance(outputData.formInstanceId)}>
                            {t('workflowMonitor.viewFormInstance')}
                          </Button>
                        )}
                        {outputData.reportUrl && (
                          <Button size="small" icon={<FileTextOutlined />} onClick={() => onOpenArtifact && onOpenArtifact({ kind: 'report', url: outputData.reportUrl, title: getStepPreviewTitle(step, t('workflowMonitor.htmlReport')) })}>
                            {t('workflowMonitor.openHtmlReport')}
                          </Button>
                        )}
                        {outputData.docUrl && (
                          <Button size="small" icon={<FileOutlined />} onClick={() => onOpenArtifact && onOpenArtifact({ kind: 'doc', url: outputData.docUrl, title: getStepPreviewTitle(step, t('workflowMonitor.generatedDocument')) })}>
                            {t('workflowMonitor.openGeneratedDocument')}
                          </Button>
                        )}
                        {Array.isArray(outputData.instances) && outputData.instances.map((doc, docIndex) => {
                          const docUrl = doc?.docUrl || (doc?.id ? `/doc-instance/${doc.id}` : doc?.relativePath);
                          if (!docUrl) return null;
                          return (
                            <Button
                              key={`${docUrl}-${docIndex}`}
                              size="small"
                              icon={<FileOutlined />}
                              onClick={() => onOpenArtifact && onOpenArtifact({ kind: 'doc', url: docUrl, title: getStepPreviewTitle(step, getDisplayFileName(doc?.fileName, docUrl) || `${t('workflowMonitor.generatedDocument')} ${docIndex + 1}`) })}
                            >
                              {getDisplayFileName(doc?.fileName, docUrl) || `${t('workflowMonitor.generatedDocument')} ${docIndex + 1}`}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* 智能處理 errorMessage 字段，只顯示真正的錯誤信息 */}
                    {step.errorMessage && (
                      (() => {
                        // 檢查 errorMessage 是否與 outputJson 內容相同，如果相同則不顯示（避免重複）
                        const jsonContent = step.outputJson || step.OutputJson || step.output;
                        if (jsonContent && step.errorMessage === jsonContent) {
                          wfmDebugLog(t('workflowMonitor.stepErrorMessageSameAsOutputJson', { stepNumber: index + 1 }));
                          return null; // 不顯示重複內容
                        }
                        
                        // 檢查 errorMessage 是否包含成功的狀態更新消息
                        try {
                          const errorData = JSON.parse(step.errorMessage);
                          // 如果 errorMessage 包含 success: true 或特定的成功消息，則不顯示
                          if (errorData.success === true || 
                              (errorData.message && (
                                errorData.message.includes("User replied, continuing workflow") ||
                                errorData.message.includes("EForm sent successfully") ||
                                errorData.message.includes("Form already processed") ||
                                errorData.message.includes("waiting for approval") ||
                                errorData.message.includes("Waiting for user reply")
                              )) ||
                              // 檢查是否為 switch 節點的正常輸出
                              errorData.selectedPaths || errorData.selectedPath || errorData.evaluatedAt) {
                            wfmDebugLog(t('workflowMonitor.stepErrorMessageContainsSuccess', { stepNumber: index + 1 }));
                            return null; // 不顯示
                          }
                        } catch (parseError) {
                          // 如果解析失敗，可能是純文本錯誤信息，正常顯示
                          wfmDebugLog(t('workflowMonitor.stepErrorMessageParseFailed', { stepNumber: index + 1 }));
                        }
                        
                        // 顯示真正的錯誤信息
                        return (
                          <p style={{ color: 'red' }}>{t('workflowMonitor.error')}: {step.errorMessage}</p>
                        );
                      })()
                    )}
                    
                    {/* ✅ 操作按鈕區域 - 放在最底部 */}
                    {((isMessageSendNode || isWaitNode || isWaitForLocationNode) || (isDataSetQueryNode && outputData && outputData.queryResultId)) && (
                      <div style={{ 
                        marginTop: '16px', 
                        paddingTop: '12px', 
                        borderTop: '1px solid #f0f0f0',
                        display: 'flex',
                        gap: '8px'
                      }}>
                        {/* sendWhatsApp、waitReply、waitForQRCode、sendEForm 節點按鈕 */}
                        {(isMessageSendNode || isWaitNode || isWaitForLocationNode) && (
                          <>
                            <Button 
                              type="default" 
                              size="small" 
                              icon={<BarChartOutlined />}
                              onClick={async () => {
                                try {
                                  let messageSendId = null;
                                  
                                  // 對於所有消息發送節點，優先從 outputData 獲取
                                  if (isMessageSendNode && outputData && outputData.messageSendId) {
                                    messageSendId = outputData.messageSendId;
                                    console.log('從 outputData 獲取 messageSendId:', messageSendId);
                                  } else {
                                    // ✅ 對於所有節點，使用 stepExecutionId 查找
                                    console.log('📞 使用 stepExecutionId 查詢 messageSendId:', step.id);
                                    
                                    const response = await fetch(`/api/workflowexecutions/step/${step.id}/message-send-id`, {
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                      }
                                    });
                                    
                                    if (response.ok) {
                                      const data = await response.json();
                                      messageSendId = data.messageSendId;
                                      console.log('✅ 從 API 獲取 messageSendId:', messageSendId);
                                    } else {
                                      console.warn('❌ 找不到消息發送記錄，stepExecutionId:', step.id);
                                      message.warning(t('workflowMonitor.cannotFindMessageSendId'));
                                      return;
                                    }
                                  }
                                  
                                  // 打開消息發送狀態模態框（傳 step.id 以合併 normal / retry / escalation 記錄）
                                  if (messageSendId) {
                                    onViewMessageSendDetail(messageSendId, step.id);
                                  } else {
                                    message.warning(t('workflowMonitor.cannotFindMessageSendId'));
                                  }
                                } catch (error) {
                                  console.error('查詢消息發送記錄時發生錯誤:', error);
                                  message.error('查詢消息發送記錄失敗');
                                }
                              }}
                            >
                              {t('workflowMonitor.viewMessageSendStatus')}
                            </Button>
                            
                            {/* sendEForm 節點額外顯示查看表單實例按鈕 */}
                            {nodeType === 'sendEForm' && outputData && outputData.formInstanceId && (
                              <Button 
                                type="default" 
                                size="small" 
                                icon={<FileTextOutlined />}
                                onClick={() => {
                                  // 調用父組件傳入的函數來顯示內嵌表單
                                  if (onViewFormInstance) {
                                    onViewFormInstance(outputData.formInstanceId);
                                  }
                                }}
                              >
                                {t('workflowMonitor.viewFormInstance')}
                              </Button>
                            )}
                          </>
                        )}
                        
                        {/* dataSetQuery 節點按鈕 */}
                        {isDataSetQueryNode && outputData && outputData.queryResultId && (
                          <Button 
                            type="default" 
                            size="small" 
                            icon={<BarChartOutlined />}
                            onClick={async () => {
                              try {
                                // 獲取查詢結果詳情
                                const response = await fetch(`/api/workflowexecutions/step/${step.id}/data-set-query-result`, {
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                  }
                                });
                                
                                if (response.ok) {
                                  const data = await response.json();
                                  // 在同一界面中顯示數據集查詢結果
                                  onViewDataSetQuery(data);
                                } else {
                                  message.error('無法獲取查詢結果詳情');
                                }
                              } catch (error) {
                                console.error('獲取數據集查詢結果時發生錯誤:', error);
                                message.error('獲取查詢結果失敗');
                              }
                            }}
                          >
                            {t('workflowMonitor.viewDataSet')}
                          </Button>
                        )}
                      </div>
                    )}
                    </div>
                  </Timeline.Item>
                );
              })
            ) : (
              <Timeline.Item color="blue">
                <p>{t('workflowMonitor.noStepExecutionRecords')}</p>
              </Timeline.Item>
            )}
            {instance.status === 'completed' && (
              <Timeline.Item color="green">
                <p>{t('workflowMonitor.workflowCompleted')}</p>
                <p>{TimezoneUtils.formatDateWithTimezone(instance.endedAt, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss')}</p>
              </Timeline.Item>
            )}
          </Timeline>
        </TabPane>

        <TabPane tab={t('workflowMonitor.triggerSource')} key="triggerSource">
          <TriggerSourceContent instance={instance} userTimezoneOffset={userTimezoneOffset} />
        </TabPane>
        
        <TabPane tab={t('workflowMonitor.processVariables')} key="variables">
          {loadingProcessVariables ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>{t('workflowMonitor.loadingProcessVariables')}</p>
            </div>
          ) : processVariables.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {processVariables.map((variable) => (
                <Card 
                  key={variable.variableName}
                  size="small"
                  style={{ 
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    gap: '16px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        marginBottom: '12px' 
                      }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                          {variable.displayName || variable.variableName}
                        </h4>
                        <Tag color={getDataTypeColor(variable.dataType)}>
                          {variable.dataType}
                        </Tag>
                        {variable.isRequired && (
                          <Tag color="red">{t('workflowMonitor.required')}</Tag>
                        )}
                        {variable.hasValue ? (
                          <Tag color="green">{t('workflowMonitor.hasValue')}</Tag>
                        ) : (
                          <Tag color="default">{t('workflowMonitor.noValue')}</Tag>
                        )}
                      </div>
                      
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '12px',
                        fontSize: '14px'
                      }}>
                        <div>
                          <strong style={{ color: '#595959' }}>{t('workflowMonitor.variableName')}:</strong>
                          <div style={{ 
                            marginTop: '4px',
                            padding: '4px 8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid #e8e8e8'
                          }}>
                            {variable.variableName}
                          </div>
                        </div>
                        
                        <div>
                          <strong style={{ color: '#595959' }}>{t('workflowMonitor.dataType')}:</strong>
                          <div style={{ 
                            marginTop: '4px',
                            padding: '4px 8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid #e8e8e8'
                          }}>
                            {variable.dataType}
                          </div>
                        </div>
                        
                        {variable.description && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <strong style={{ color: '#595959' }}>{t('workflowMonitor.description')}:</strong>
                            <div style={{ 
                              marginTop: '4px',
                              padding: '8px 12px',
                              backgroundColor: '#f6ffed',
                              border: '1px solid #b7eb8f',
                              borderRadius: '6px',
                              fontSize: '14px',
                              lineHeight: '1.5'
                            }}>
                              {variable.description}
                            </div>
                          </div>
                        )}
                        
                        <div>
                          <strong style={{ color: '#595959' }}>{t('workflowMonitor.currentValue')}:</strong>
                          <div style={{ 
                            marginTop: '4px',
                            padding: '8px 12px',
                            backgroundColor: variable.hasValue ? '#f6ffed' : '#fff7e6',
                            border: `1px solid ${variable.hasValue ? '#b7eb8f' : '#ffd591'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            wordBreak: 'break-all'
                          }}>
                            {formatVariableValue(variable.value, variable.dataType)}
                          </div>
                        </div>
                        
                        {variable.defaultValue && (
                          <div>
                            <strong style={{ color: '#595959' }}>{t('workflowMonitor.defaultValue')}:</strong>
                            <div style={{ 
                              marginTop: '4px',
                              padding: '4px 8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e8e8e8'
                            }}>
                              {variable.defaultValue}
                            </div>
                          </div>
                        )}
                        
                        {variable.setAt && (
                          <div>
                            <strong style={{ color: '#595959' }}>{t('workflowMonitor.setAt')}:</strong>
                            <div style={{ 
                              marginTop: '4px',
                              padding: '4px 8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e8e8e8'
                            }}>
                              {TimezoneUtils.formatDateWithTimezone(variable.setAt, userTimezoneOffset)}
                            </div>
                          </div>
                        )}
                        
                        {variable.setBy && (
                          <div>
                            <strong style={{ color: '#595959' }}>{t('workflowMonitor.setBy')}:</strong>
                            <div style={{ 
                              marginTop: '4px',
                              padding: '4px 8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e8e8e8'
                            }}>
                              {variable.setBy}
                            </div>
                          </div>
                        )}
                        
                        {variable.sourceType && (
                          <div>
                            <strong style={{ color: '#595959' }}>{t('workflowMonitor.sourceType')}:</strong>
                            <div style={{ 
                              marginTop: '4px',
                              padding: '4px 8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e8e8e8'
                            }}>
                              {variable.sourceType}
                            </div>
                          </div>
                        )}
                        
                        {variable.sourceReference && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <strong style={{ color: '#595959' }}>{t('workflowMonitor.sourceReference')}:</strong>
                            <div style={{ 
                              marginTop: '4px',
                              padding: '4px 8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e8e8e8',
                              wordBreak: 'break-all'
                            }}>
                              {variable.sourceReference}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Empty 
              description={t('workflowMonitor.noProcessVariables')} 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: '40px 0' }}
            />
          )}
        </TabPane>
        
        <TabPane tab={t('workflowMonitor.receivedMedia')} key="media">
          {loadingMediaFiles ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>{t('workflowMonitor.loadingMediaFiles')}</p>
            </div>
          ) : mediaFiles.length > 0 ? (
            <div>
              <div style={{ 
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <Text strong style={{ fontSize: '16px' }}>
                  {t('workflowMonitor.totalFiles')}: {mediaFiles.length}
                </Text>
              </div>
              
              {/* 按步驟分組顯示 */}
              {(() => {
                // 按步驟名稱分組
                const groupedByStep = mediaFiles.reduce((acc, file) => {
                  const stepName = file.stepName || '未知步驟';
                  if (!acc[stepName]) {
                    acc[stepName] = [];
                  }
                  acc[stepName].push(file);
                  return acc;
                }, {});
                
                // 獲取所有步驟名稱並排序
                const stepNames = Object.keys(groupedByStep).sort();
                
                wfmDebugLog('[WorkflowMonitor] Grouping media files:', {
                  totalFiles: mediaFiles.length,
                  filesWithStepName: mediaFiles.filter(f => f.stepName && f.stepName !== '未知步驟').length,
                  filesWithoutStepName: mediaFiles.filter(f => !f.stepName || f.stepName === '未知步驟').length,
                  groupedByStep,
                  stepNames
                });
                
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {stepNames.map((stepName) => {
                      const files = groupedByStep[stepName];
                      wfmDebugLog(`[WorkflowMonitor] Rendering step group "${stepName}" with ${files.length} files`);
                      return (
                        <div key={stepName} style={{ marginBottom: '8px' }}>
                          {/* 步驟標題 */}
                          <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#f0f8ff',
                            border: '1px solid #91d5ff',
                            borderRadius: '4px',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}>
                            <Text strong style={{ fontSize: '13px', color: '#1890ff' }}>
                              {stepName}
                            </Text>
                            <Tag color="blue" style={{ fontSize: '10px', margin: 0 }}>
                              {files.length} 個文件
                            </Tag>
                          </div>
                          
                          {/* 該步驟的文件列表 */}
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '16px'
                          }}>
                            {files.map((file) => {
                              wfmDebugLog('[WorkflowMonitor] Rendering media file:', { 
                                fileName: file.fileName, 
                                filePath: file.filePath,
                                originalFileName: file.originalFileName,
                                name: file.name,
                                fileType: file.fileType,
                                mimeType: file.mimeType,
                                contentType: file.contentType,
                                stepName: file.stepName,
                                stepIndex: file.stepIndex,
                                file: file 
                              });
                              // 使用原始文件名或文件名
                              const displayFileName = file.originalFileName || file.name || file.fileName || '';
                              const fileType = getFileType(displayFileName);
                              const isImage = fileType === 'image';
                              const isVideo = fileType === 'video';
                              const isAudio = fileType === 'audio';
                              const isDocument = fileType === 'document';
                              
                              return (
                                <Card
                                  key={file.id}
                                  size="small"
                                  hoverable
                                  style={{ 
                                    border: '1px solid #e8e8e8',
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                  }}
                                  bodyStyle={{ padding: '8px' }}
                                >
                                  <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center'
                                  }}>
                                    {/* 文件預覽 */}
                                    <div 
                                      style={{ 
                                        width: '100%', 
                                        height: '120px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '6px',
                                        marginBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        cursor: (isImage || isVideo) ? 'pointer' : 'default'
                                      }}
                                      onClick={() => {
                                        if (isImage || isVideo) {
                                          openLightbox(file, mediaFiles);
                                        }
                                      }}
                                    >
                                      {isImage ? (
                                        <img
                                          src={file.filePath}
                                          alt={file.fileName}
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: '4px'
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                      ) : isVideo ? (
                                        <video
                                          src={file.filePath}
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: '4px'
                                          }}
                                          controls={false}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                      ) : null}
                                      
                                      {/* 備用圖標 */}
                                      <div style={{ 
                                        display: isImage || isVideo ? 'none' : 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: '#f0f0f0'
                                      }}>
                                        {getFileIcon(displayFileName, file)}
                                      </div>
                                    </div>
                                    
                                    {/* 文件信息 */}
                                    <div style={{ width: '100%' }}>
                                      <Text 
                                        strong 
                                        style={{ 
                                          fontSize: '12px',
                                          display: 'block',
                                          marginBottom: '4px',
                                          wordBreak: 'break-all',
                                          lineHeight: '1.2'
                                        }}
                                        title={displayFileName}
                                      >
                                        {displayFileName.length > 20 ? 
                                          displayFileName.substring(0, 20) + '...' : 
                                          displayFileName
                                        }
                                      </Text>
                                      
                                      <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontSize: '11px',
                                        color: '#666'
                                      }}>
                                        <span>{formatFileSize(file.fileSize)}</span>
                                        <Tag 
                                          color={isImage ? 'green' : isVideo ? 'blue' : isAudio ? 'orange' : 'default'}
                                          style={{ fontSize: '10px', margin: 0 }}
                                        >
                                          {isImage ? t('workflowMonitor.image') : 
                                           isVideo ? t('workflowMonitor.video') : 
                                           isAudio ? t('workflowMonitor.audio') :
                                           t('workflowMonitor.document')}
                                        </Tag>
                                      </div>
                                      
                                      {file.createdAt && (
                                        <div style={{ 
                                          fontSize: '10px', 
                                          color: '#999',
                                          marginTop: '4px'
                                        }}>
                                          {new Date(file.createdAt).toLocaleDateString('zh-TW')}
                                        </div>
                                      )}
                                      
                                      {/* 操作按鈕 */}
                                      <div style={{ 
                                        marginTop: '8px',
                                        display: 'flex',
                                        gap: '4px',
                                        justifyContent: 'center'
                                      }}>
                                        <Button 
                                          type="text" 
                                          size="small"
                                          icon={<EyeOutlined />}
                                          onClick={() => {
                                            if (isImage || isVideo) {
                                              openLightbox(file, mediaFiles);
                                            } else {
                                              onOpenArtifact && onOpenArtifact({
                                                kind: 'media',
                                                url: file.filePath,
                                                title: getDisplayFileName(file.originalFileName, file.fileName, file.filePath) || t('workflowMonitor.file')
                                              });
                                            }
                                          }}
                                          style={{ fontSize: '10px', padding: '2px 6px' }}
                                        >
                                          {t('workflowMonitor.view')}
                                        </Button>
                                        <Button 
                                          type="text" 
                                          size="small"
                                          icon={<DownloadOutlined />}
                                          onClick={() => {
                                            // 下載文件
                                            const link = document.createElement('a');
                                            link.href = file.filePath;
                                            link.download = file.fileName;
                                            link.click();
                                          }}
                                          style={{ fontSize: '10px', padding: '2px 6px' }}
                                        >
                                          {t('workflowMonitor.download')}
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </Card>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ) : (
            <Empty 
              description={t('workflowMonitor.noMediaFiles')} 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: '40px 0' }}
            />
          )}
        </TabPane>
        
        <TabPane tab={t('workflowMonitor.formInstances')} key="forms">
          {loadingEforms ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>{t('workflowMonitor.loadingEformInstances')}</p>
            </div>
          ) : eformInstances.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {eformInstances.map((eform) => (
                <Card 
                  key={eform.id}
                  size="small"
                  style={{ 
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <div>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <Button 
                            type="primary" 
                            size="small"
                            onClick={() => {
                              if (onViewFormInstance) {
                                onViewFormInstance(eform.id);
                              }
                            }}
                            style={{ 
                              backgroundColor: '#1890ff',
                              borderColor: '#1890ff'
                            }}
                          >
                            {t('workflowMonitor.viewEmbedded')}
                          </Button>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '12px'
                        }}>
                          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                            {eform.formName || t('workflowMonitor.unnamedForm')}
                          </h4>
                        <Tag color={getEformStatusColor(eform.status)}>
                          {getEformStatusText(eform.status, eform)}
                        </Tag>
                        {eform.fillType && (
                          <Tag color={
                            eform.fillType === 'Manual' ? 'blue' : 
                            eform.fillType === 'AI' ? 'green' : 
                            eform.fillType === 'MetaFlows' ? 'purple' : 
                            'orange'
                          }>
                            {eform.fillType}
                          </Tag>
                        )}
                        </div>
                      </div>
                      
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '12px',
                        fontSize: '14px'
                      }}>
                        <div>
                          <strong style={{ color: '#595959' }}>{t('workflowMonitor.instanceName')}:</strong>
                          <div style={{ 
                            marginTop: '4px',
                            padding: '4px 8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid #e8e8e8'
                          }}>
                            {eform.instanceName || '-'}
                          </div>
                        </div>
                        
                        <div>
                          <strong style={{ color: '#595959' }}>{t('workflowMonitor.createdAt')}:</strong>
                          <div style={{ 
                            marginTop: '4px',
                            padding: '4px 8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid #e8e8e8'
                          }}>
                            {eform.createdAt ? TimezoneUtils.formatDateWithTimezone(eform.createdAt, userTimezoneOffset) : '-'}
                          </div>
                        </div>
                        
                        {eform.userMessage && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <strong style={{ color: '#595959' }}>{t('workflowMonitor.userInput')}:</strong>
                            <div style={{ 
                              marginTop: '4px',
                              padding: '8px 12px',
                              backgroundColor: '#f6ffed',
                              border: '1px solid #b7eb8f',
                              borderRadius: '6px',
                              fontSize: '14px',
                              lineHeight: '1.5'
                            }}>
                              {(() => {
                                const result = extractAiAnalysisResult(eform.userMessage);
                                // 如果結果包含 HTML 標籤，使用 dangerouslySetInnerHTML
                                if (typeof result === 'string' && result.includes('<table')) {
                                  return <div dangerouslySetInnerHTML={{ __html: result }} />;
                                }
                                return result;
                              })()}
                            </div>
                          </div>
                        )}
                        
                        {eform.approvalBy && (
                          <div>
                            <strong style={{ color: '#595959' }}>{t('workflowMonitor.approvalBy')}:</strong>
                            <div style={{ 
                              marginTop: '4px',
                              padding: '4px 8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e8e8e8'
                            }}>
                              {eform.approvalBy}
                            </div>
                          </div>
                        )}
                        
                        {eform.approvalAt && (
                          <div>
                            <strong style={{ color: '#595959' }}>{t('workflowMonitor.approvalAt')}:</strong>
                            <div style={{ 
                              marginTop: '4px',
                              padding: '4px 8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e8e8e8'
                            }}>
                              {TimezoneUtils.formatDateWithTimezone(eform.approvalAt, userTimezoneOffset)}
                            </div>
                          </div>
                        )}
                        
                        {eform.approvalNote && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <strong style={{ color: '#595959' }}>{t('workflowMonitor.approvalNote')}:</strong>
                            <div style={{ 
                              marginTop: '4px',
                              padding: '8px 12px',
                              backgroundColor: '#fff7e6',
                              border: '1px solid #ffd591',
                              borderRadius: '6px',
                              fontSize: '14px',
                              lineHeight: '1.5'
                            }}>
                              {eform.approvalNote}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Empty 
              description={t('workflowMonitor.noEformInstances')} 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: '40px 0' }}
            />
          )}
        </TabPane>

        <TabPane tab={t('workflowMonitor.htmlReportInstances')} key="reports">
          {loadingReportInstances ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>{t('workflowMonitor.loadingReportInstances')}</p>
            </div>
          ) : reportInstances.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {reportInstances.map((report) => (
                <Card key={report.id} size="small" style={{ border: '1px solid #e8e8e8', borderRadius: 8 }}>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Space wrap>
                      <Text strong>{report.instanceName || t('workflowMonitor.htmlReport')}</Text>
                      <Tag color="green">{report.status || '-'}</Tag>
                    </Space>
                    <div style={{ fontSize: 13, color: '#666' }}>
                      <div>{t('workflowMonitor.relatedStepExecutionId')}: {report.workflowStepExecutionId || '-'}</div>
                      <div>{t('workflowMonitor.createdAt')}: {report.createdAt ? TimezoneUtils.formatDateWithTimezone(report.createdAt, userTimezoneOffset) : '-'}</div>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      icon={<EyeOutlined />}
                      disabled={!report.reportUrl && !report.id}
                      onClick={() => onOpenArtifact && onOpenArtifact({
                        kind: 'report',
                        url: report.reportUrl || `/report-instance/${report.id}`,
                        title: getStepPreviewTitleByExecutionId(report.workflowStepExecutionId, report.instanceName || t('workflowMonitor.htmlReport'))
                      })}
                    >
                      {t('workflowMonitor.openHtmlReport')}
                    </Button>
                  </Space>
                </Card>
              ))}
            </div>
          ) : (
            <Empty description={t('workflowMonitor.noReportInstances')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '40px 0' }} />
          )}
        </TabPane>

        <TabPane tab={t('workflowMonitor.generatedDocumentInstances')} key="documents">
          {loadingDocInstances ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>{t('workflowMonitor.loadingDocInstances')}</p>
            </div>
          ) : docInstances.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {docInstances.map((doc) => {
                const docUrl = doc.docUrl || (doc.id ? `/doc-instance/${doc.id}` : doc.publicUrl);
                const docTitle = getDisplayFileName(doc.fileName, doc.docUrl, doc.publicUrl) || doc.instanceName || t('workflowMonitor.generatedDocument');
                return (
                  <Card key={doc.id} size="small" style={{ border: '1px solid #e8e8e8', borderRadius: 8 }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      <Space wrap>
                        <Text strong>{docTitle}</Text>
                        <Tag color="blue">{doc.outputFormat || '-'}</Tag>
                        <Tag color="green">{doc.status || '-'}</Tag>
                      </Space>
                      <div style={{ fontSize: 13, color: '#666' }}>
                        <div>{t('workflowMonitor.relatedStepExecutionId')}: {doc.workflowStepExecutionId || '-'}</div>
                        <div>{t('workflowMonitor.createdAt')}: {doc.createdAt ? TimezoneUtils.formatDateWithTimezone(doc.createdAt, userTimezoneOffset) : '-'}</div>
                      </div>
                      <Space>
                        <Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => onOpenArtifact && onOpenArtifact({ kind: 'doc', url: docUrl, title: getStepPreviewTitleByExecutionId(doc.workflowStepExecutionId, docTitle) })}>
                          {t('workflowMonitor.openGeneratedDocument')}
                        </Button>
                        {doc.id && doc.outputFormat === 'pdf' && (
                          <Button size="small" icon={<EditOutlined />} onClick={() => setOverlayEditorDocId(doc.id)}>
                            編輯填表內容
                          </Button>
                        )}
                      </Space>
                    </Space>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Empty description={t('workflowMonitor.noDocInstances')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '40px 0' }} />
          )}
        </TabPane>
      </Tabs>
      </div>

      {/* PDF Overlay Editor Drawer（全螢幕） */}
      <Drawer
        title="編輯填表內容"
        placement="right"
        width="100vw"
        styles={{ body: { padding: 0, overflow: 'hidden', height: '100%' } }}
        open={!!overlayEditorDocId}
        onClose={() => setOverlayEditorDocId(null)}
        destroyOnClose
      >
        {overlayEditorDocId && (
          <PdfOverlayEditor
            docInstanceId={overlayEditorDocId}
            onSaveSuccess={() => setOverlayEditorDocId(null)}
            onClose={() => setOverlayEditorDocId(null)}
          />
        )}
      </Drawer>

      <Modal
        title={t('workflowMonitor.locationMapTitle')}
        open={locationMapModal.open}
        onCancel={() => setLocationMapModal((s) => ({ ...s, open: false }))}
        footer={null}
        width={900}
        destroyOnClose
        styles={{ body: { paddingTop: 8 } }}
      >
        {locationMapModal.open && locationMapModal.lat != null && locationMapModal.lng != null && (
          <>
            <WorkflowMonitorLocationMap
              lat={locationMapModal.lat}
              lng={locationMapModal.lng}
              height={420}
              zoom={16}
            />
            {(locationMapModal.name || locationMapModal.address) && (
              <div style={{ marginTop: 12, fontSize: 13, color: '#595959', lineHeight: 1.6 }}>
                {locationMapModal.name ? (
                  <div><strong>{t('workflowMonitor.locationName')}:</strong> {locationMapModal.name}</div>
                ) : null}
                {locationMapModal.address ? (
                  <div><strong>{t('workflowMonitor.locationAddress')}:</strong> {locationMapModal.address}</div>
                ) : null}
              </div>
            )}
          </>
        )}
      </Modal>
      
      {/* Lightbox 組件 */}
      <Modal
        title={lightboxFile ? lightboxFile.fileName : ''}
        visible={lightboxVisible}
        onCancel={closeLightbox}
        footer={null}
        width="95%"
        style={{ top: 10 }}
        bodyStyle={{ 
          padding: 0, 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '85vh',
          backgroundColor: '#000',
          position: 'relative'
        }}
        closable={false}
      >
        {lightboxFile && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center',
            width: '100%',
            height: '100%',
            position: 'relative'
          }}>
            {/* 關閉按鈕 */}
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={closeLightbox}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 1000,
                color: '#fff',
                fontSize: '20px',
                width: '40px',
                height: '40px',
                backgroundColor: 'rgba(0,0,0,0.5)',
                border: 'none'
              }}
            />
            
            {/* 導航按鈕 */}
            {lightboxFiles.length > 1 && (
              <>
                <Button
                  type="text"
                  icon={<LeftOutlined />}
                  onClick={goToPrevious}
                  style={{
                    position: 'absolute',
                    left: 20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1000,
                    color: '#fff',
                    fontSize: '24px',
                    width: '50px',
                    height: '50px',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    border: 'none'
                  }}
                />
                <Button
                  type="text"
                  icon={<RightOutlined />}
                  onClick={goToNext}
                  style={{
                    position: 'absolute',
                    right: 20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1000,
                    color: '#fff',
                    fontSize: '24px',
                    width: '50px',
                    height: '50px',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    border: 'none'
                  }}
                />
              </>
            )}
            
            {/* 媒體內容 */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              transform: `
                rotate(${lightboxTransform.rotate}deg) 
                scale(${lightboxTransform.scale}) 
                scaleX(${lightboxTransform.flipH ? -1 : 1}) 
                scaleY(${lightboxTransform.flipV ? -1 : 1})
              `,
              transition: 'transform 0.3s ease'
            }}>
              {getFileType(lightboxFile.fileName) === 'image' ? (
                <img
                  src={lightboxFile.filePath || lightboxFile.dataUrl}
                  alt={lightboxFile.fileName}
                  style={{
                    maxWidth: '90%',
                    maxHeight: '80vh',
                    objectFit: 'contain'
                  }}
                />
              ) : getFileType(lightboxFile.fileName) === 'video' ? (
                <video
                  src={lightboxFile.filePath || lightboxFile.dataUrl}
                  controls
                  style={{
                    maxWidth: '90%',
                    maxHeight: '80vh'
                  }}
                />
              ) : null}
            </div>
            
            {/* 工具欄 */}
            {getFileType(lightboxFile.fileName) === 'image' && (
              <div style={{
                position: 'absolute',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '8px',
                backgroundColor: 'rgba(0,0,0,0.7)',
                padding: '8px 16px',
                borderRadius: '8px',
                zIndex: 1000
              }}>
                <Button
                  type="text"
                  icon={<RotateLeftOutlined />}
                  onClick={() => rotateImage('left')}
                  style={{ color: '#fff' }}
                  title={t('workflowMonitor.rotateLeft')}
                />
                <Button
                  type="text"
                  icon={<RotateRightOutlined />}
                  onClick={() => rotateImage('right')}
                  style={{ color: '#fff' }}
                  title={t('workflowMonitor.rotateRight')}
                />
                <Button
                  type="text"
                  icon={<SwapOutlined />}
                  onClick={() => flipImage('horizontal')}
                  style={{ 
                    color: '#fff',
                    transform: lightboxTransform.flipH ? 'scaleX(-1)' : 'none'
                  }}
                  title={t('workflowMonitor.flipHorizontal')}
                />
                <Button
                  type="text"
                  icon={<SwapOutlined />}
                  onClick={() => flipImage('vertical')}
                  style={{ 
                    color: '#fff',
                    transform: lightboxTransform.flipV ? 'scaleY(-1)' : 'none'
                  }}
                  title={t('workflowMonitor.flipVertical')}
                />
                <Button
                  type="text"
                  icon={<ZoomInOutlined />}
                  onClick={() => zoomImage('in')}
                  style={{ color: '#fff' }}
                  title={t('workflowMonitor.zoomIn')}
                />
                <Button
                  type="text"
                  icon={<ZoomOutOutlined />}
                  onClick={() => zoomImage('out')}
                  style={{ color: '#fff' }}
                  title={t('workflowMonitor.zoomOut')}
                />
                <Button
                  type="text"
                  icon={<ResetOutlined />}
                  onClick={resetTransform}
                  style={{ color: '#fff' }}
                  title={t('workflowMonitor.reset')}
                />
              </div>
            )}
            
            {/* 文件信息 */}
            <div style={{
              position: 'absolute',
              bottom: 20,
              left: 20,
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              zIndex: 1000
            }}>
              {lightboxFiles.length > 1 && (
                <div>{lightboxCurrentIndex + 1} / {lightboxFiles.length}</div>
              )}
              <div>{formatFileSize(lightboxFile.fileSize)}</div>
            </div>
          </div>
        )}
      </Modal>

      {/* 聊天類節點對話檢視彈窗（實例詳情用，chatBot / chatOrderingCart 共用） */}
      <ChatBotConversationModal
        visible={chatBotViewer.open}
        stepExecutionId={chatBotViewer.stepExecutionId}
        taskName={chatBotViewer.taskName}
        nodeKind={chatBotViewer.nodeKind || 'chatBot'}
        onClose={() => setChatBotViewer({ open: false, stepExecutionId: null, taskName: '', nodeKind: 'chatBot' })}
      />
    </>
  );
};

// 消息發送詳情組件
const MessageSendDetailModal = ({ messageSend, onClose, userTimezoneOffset }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('basic');
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  // 載入收件人數據
  useEffect(() => {
    if (messageSend && messageSend.recipients) {
      setRecipients(messageSend.recipients);
    }
  }, [messageSend]);

  const getStatusTag = (status) => {
    const statusConfig = {
      Pending: { color: 'default', text: t('workflowMonitor.statusPending') },
      InProgress: { color: 'processing', text: t('workflowMonitor.statusInProgress') },
      Completed: { color: 'success', text: t('workflowMonitor.statusCompleted') },
      Failed: { color: 'error', text: t('workflowMonitor.statusFailed') },
      PartiallyFailed: { color: 'warning', text: t('workflowMonitor.statusPartiallyFailed') }
    };
    
    const config = statusConfig[status] || statusConfig.Pending;
    
    return (
      <Tag color={config.color}>
        {config.text}
      </Tag>
    );
  };

  const getRecipientStatusTag = (status) => {
    const statusConfig = {
      Pending: { color: 'default', text: t('workflowMonitor.statusPending') },
      Sent: { color: 'processing', text: t('workflowMonitor.statusSent') },
      Delivered: { color: 'success', text: t('workflowMonitor.statusDelivered') },
      Read: { color: 'success', text: t('workflowMonitor.statusRead') },
      Failed: { color: 'error', text: t('workflowMonitor.statusFailed') },
      Retrying: { color: 'warning', text: t('workflowMonitor.statusRetrying') }
    };
    
    const config = statusConfig[status] || statusConfig.Pending;
    
    return (
      <Tag color={config.color}>
        {config.text}
      </Tag>
    );
  };

  const getRecipientTypeTag = (type) => {
    const typeConfig = {
      User: { color: 'blue', text: t('workflowMonitor.recipientTypeUser') },
      Contact: { color: 'green', text: t('workflowMonitor.recipientTypeContact') },
      Group: { color: 'orange', text: t('workflowMonitor.recipientTypeGroup') },
      Hashtag: { color: 'purple', text: t('workflowMonitor.recipientTypeHashtag') },
      Initiator: { color: 'red', text: t('workflowMonitor.recipientTypeInitiator') }
    };
    
    const config = typeConfig[type] || typeConfig.User;
    
    return (
      <Tag color={config.color}>
        {config.text}
      </Tag>
    );
  };

  const getSendReasonTag = (sendReason) => {
    const reasonConfig = {
      normal: { color: 'blue', text: t('workflowMonitor.sendReasonNormal') },
      retry: { color: 'orange', text: t('workflowMonitor.sendReasonRetry') },
      escalation: { color: 'red', text: t('workflowMonitor.sendReasonEscalation') },
      overdue: { color: 'purple', text: t('workflowMonitor.sendReasonOverdue') }
    };
    
    const config = reasonConfig[sendReason] || reasonConfig.normal;
    
    return (
      <Tag color={config.color}>
        {config.text}
      </Tag>
    );
  };

  return (
    <div>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={t('workflowMonitor.basicInfo')} key="basic">
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('workflowMonitor.messageSendId')}>{messageSend.id}</Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.workflowExecutionId')}>{messageSend.workflowExecutionId}</Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.nodeId')}>{messageSend.nodeId}</Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.nodeType')}>{getNodeTypeLabel(messageSend.nodeType, t)}</Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.messageType')}>{messageSend.messageType}</Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.status')}>{getStatusTag(messageSend.status)}</Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.totalRecipients')}>{messageSend.totalRecipients}</Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.successCount')}>{messageSend.successCount}</Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.failedCount')}>{messageSend.failedCount}</Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.startedAt')}>
              {TimezoneUtils.formatDateWithTimezone(messageSend.startedAt, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.completedAt')}>
              {messageSend.completedAt ? TimezoneUtils.formatDateWithTimezone(messageSend.completedAt, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.createdBy')}>{messageSend.createdBy}</Descriptions.Item>
            <Descriptions.Item label={t('workflowMonitor.sendReason')}>{getSendReasonTag(messageSend.sendReason)}</Descriptions.Item>
            {messageSend.relatedStepExecutionId && (
              <Descriptions.Item label={t('workflowMonitor.relatedStepExecutionId')}>{messageSend.relatedStepExecutionId}</Descriptions.Item>
            )}
          </Descriptions>
          
          {messageSend.messageContent && (
            <div style={{ marginTop: 16 }}>
              <Text strong>{t('workflowMonitor.messageContent')}:</Text>
              <div style={{ 
                marginTop: 8,
                padding: 12,
                backgroundColor: '#f5f5f5',
                borderRadius: 6,
                border: '1px solid #d9d9d9',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {messageSend.messageContent}
              </div>
            </div>
          )}
          
          {messageSend.errorMessage && (
            <Alert
              message={t('workflowMonitor.errorMessage')}
              description={messageSend.errorMessage}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </TabPane>
        
        <TabPane tab={t('workflowMonitor.recipientDetails')} key="recipients">
          {loadingRecipients ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>{t('workflowMonitor.loadingRecipientDetails')}</p>
            </div>
          ) : recipients.length > 0 ? (
            <>
              <Table
                dataSource={recipients}
                rowKey="id"
                pagination={false}
                scroll={{ x: 800 }}
              columns={[
                {
                  title: t('workflowMonitor.recipient'),
                  dataIndex: 'recipientName',
                  key: 'recipientName',
                  width: 200,
                  render: (text, record) => (
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{text}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{record.phoneNumber}</div>
                    </div>
                  )
                },
                {
                  title: t('workflowMonitor.type'),
                  dataIndex: 'recipientType',
                  key: 'recipientType',
                  width: 100,
                  render: (type) => getRecipientTypeTag(type)
                },
                {
                  title: t('workflowMonitor.status'),
                  dataIndex: 'status',
                  key: 'status',
                  width: 100,
                  render: (status) => getRecipientStatusTag(status)
                },
                {
                  title: t('workflowMonitor.whatsAppMessageId'),
                  dataIndex: 'whatsAppMessageId',
                  key: 'whatsAppMessageId',
                  width: 200,
                  ellipsis: true,
                  render: (text) => text || '-'
                },
                {
                  title: t('workflowMonitor.sentAt'),
                  dataIndex: 'sentAt',
                  key: 'sentAt',
                  width: 150,
                  render: (date) => date ? TimezoneUtils.formatDateWithTimezone(date, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-'
                },
                {
                  title: t('workflowMonitor.deliveredAt'),
                  dataIndex: 'deliveredAt',
                  key: 'deliveredAt',
                  width: 150,
                  render: (date) => date ? TimezoneUtils.formatDateWithTimezone(date, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-'
                },
                {
                  title: t('workflowMonitor.readAt'),
                  dataIndex: 'readAt',
                  key: 'readAt',
                  width: 150,
                  render: (date) => date ? TimezoneUtils.formatDateWithTimezone(date, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-'
                },
                {
                  title: t('workflowMonitor.errorMessage'),
                  dataIndex: 'errorMessage',
                  key: 'errorMessage',
                  width: 200,
                  ellipsis: true,
                  render: (text) => text || '-'
                }
              ]}
            />
            <div style={{ marginTop: 16, textAlign: 'left' }}>
              <Pagination
                current={1}
                pageSize={10}
                total={recipients.length}
                showSizeChanger
                showQuickJumper
                pageSizeOptions={['5', '10', '20', '50']}
                showTotal={(total, range) => 
                  `第 ${range[0]}-${range[1]} 條，共 ${total} 條記錄`
                }
                onChange={(page, pageSize) => {
                  // 處理分頁變更
                }}
                onShowSizeChange={(current, size) => {
                  // 處理每頁條數變更
                }}
              />
            </div>
            </>
          ) : (
            <Empty 
              description={t('workflowMonitor.noRecipientRecords')} 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: '40px 0' }}
            />
          )}
        </TabPane>
      </Tabs>
    </div>
  );
};

// 消息發送詳細狀態組件
const MessageSendStatusDetailModal = ({ messageSend, onClose, onViewMessageSend, onViewMessageSendDetail, userTimezoneOffset }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [statistics, setStatistics] = useState({
    total: 0,
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    pending: 0
  });

  // 載入收件人數據
  useEffect(() => {
    if (messageSend && messageSend.recipients) {
      setRecipients(messageSend.recipients);
      calculateStatistics(messageSend.recipients);
    }
  }, [messageSend]);

  const calculateStatistics = (recipientsData) => {
    const stats = {
      total: recipientsData.length,
      sent: 0,
      delivered: 0,
      read: 0,
      failed: 0,
      pending: 0
    };

    recipientsData.forEach(recipient => {
      switch (recipient.status) {
        case 'Sent':
          stats.sent++;
          break;
        case 'Delivered':
          stats.delivered++;
          break;
        case 'Read':
          stats.read++;
          break;
        case 'Failed':
          stats.failed++;
          break;
        case 'Pending':
        default:
          stats.pending++;
          break;
      }
    });

    setStatistics(stats);
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      Pending: { color: 'default', text: t('workflowMonitor.statusPending') },
      InProgress: { color: 'processing', text: t('workflowMonitor.statusInProgress') },
      Completed: { color: 'success', text: t('workflowMonitor.statusCompleted') },
      Failed: { color: 'error', text: t('workflowMonitor.statusFailed') },
      PartiallyFailed: { color: 'warning', text: t('workflowMonitor.statusPartiallyFailed') }
    };
    
    const config = statusConfig[status] || statusConfig.Pending;
    
    return (
      <Tag color={config.color}>
        {config.text}
      </Tag>
    );
  };

  const getRecipientStatusTag = (status) => {
    const statusConfig = {
      Pending: { color: 'default', text: t('workflowMonitor.statusPending') },
      Sent: { color: 'processing', text: t('workflowMonitor.statusSent') },
      Delivered: { color: 'success', text: t('workflowMonitor.statusDelivered') },
      Read: { color: 'success', text: t('workflowMonitor.statusRead') },
      Failed: { color: 'error', text: t('workflowMonitor.statusFailed') },
      Retrying: { color: 'warning', text: t('workflowMonitor.statusRetrying') }
    };
    
    const config = statusConfig[status] || statusConfig.Pending;
    
    return (
      <Tag color={config.color}>
        {config.text}
      </Tag>
    );
  };

  const getRecipientTypeTag = (type) => {
    const typeConfig = {
      User: { color: 'blue', text: t('workflowMonitor.recipientTypeUser') },
      Contact: { color: 'green', text: t('workflowMonitor.recipientTypeContact') },
      Group: { color: 'orange', text: t('workflowMonitor.recipientTypeGroup') },
      Hashtag: { color: 'purple', text: t('workflowMonitor.recipientTypeHashtag') },
      Initiator: { color: 'red', text: t('workflowMonitor.recipientTypeInitiator') },
      PhoneNumber: { color: 'cyan', text: t('workflowMonitor.recipientTypePhoneNumber') }
    };
    
    const config = typeConfig[type] || typeConfig.User;
    
    return (
      <Tag color={config.color}>
        {config.text}
      </Tag>
    );
  };

  const getSendReasonTag = (sendReason) => {
    const reasonConfig = {
      normal: { color: 'blue', text: t('workflowMonitor.sendReasonNormal') },
      retry: { color: 'orange', text: t('workflowMonitor.sendReasonRetry') },
      escalation: { color: 'red', text: t('workflowMonitor.sendReasonEscalation') },
      overdue: { color: 'purple', text: t('workflowMonitor.sendReasonOverdue') }
    };
    
    const config = reasonConfig[sendReason] || reasonConfig.normal;
    
    return (
      <Tag color={config.color}>
        {config.text}
      </Tag>
    );
  };

  return (
    <div>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={t('workflowMonitor.sendOverview')} key="overview">
          {/* 統計卡片 */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title={t('workflowMonitor.totalRecipients')}
                  value={statistics.total}
                  prefix={<MessageOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title={t('workflowMonitor.sent')}
                  value={statistics.sent}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title={t('workflowMonitor.delivered')}
                  value={statistics.delivered}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title={t('workflowMonitor.read')}
                  value={statistics.read}
                  prefix={<EyeOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title={t('workflowMonitor.failed')}
                  value={statistics.failed}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title={t('workflowMonitor.pending')}
                  value={statistics.pending}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>

          {/* 基本信息 */}
          <Card title={t('workflowMonitor.sendBasicInfo')} style={{ marginBottom: 16 }}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label={t('workflowMonitor.messageSendId')}>{messageSend.id}</Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.workflowExecutionId')}>{messageSend.workflowExecutionId}</Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.nodeId')}>{messageSend.nodeId}</Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.nodeType')}>{getNodeTypeLabel(messageSend.nodeType, t)}</Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.messageType')}>{messageSend.messageType}</Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.status')}>{getStatusTag(messageSend.status)}</Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.totalRecipients')}>{messageSend.totalRecipients}</Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.successCount')}>{messageSend.successCount}</Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.failedCount')}>{messageSend.failedCount}</Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.startedAt')}>
                {TimezoneUtils.formatDateWithTimezone(messageSend.startedAt, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.completedAt')}>
                {messageSend.completedAt ? TimezoneUtils.formatDateWithTimezone(messageSend.completedAt, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.createdBy')}>{messageSend.createdBy}</Descriptions.Item>
              <Descriptions.Item label={t('workflowMonitor.sendReason')}>{getSendReasonTag(messageSend.sendReason)}</Descriptions.Item>
              {messageSend.relatedStepExecutionId && (
                <Descriptions.Item label={t('workflowMonitor.relatedStepExecutionId')}>{messageSend.relatedStepExecutionId}</Descriptions.Item>
              )}
            </Descriptions>
            
            {messageSend.messageContent && (
              <div style={{ marginTop: 16 }}>
                <Text strong>{t('workflowMonitor.messageContent')}:</Text>
                <div style={{ 
                  marginTop: 8,
                  padding: 12,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 6,
                  border: '1px solid #d9d9d9',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {messageSend.messageContent}
                </div>
              </div>
            )}
            
            {messageSend.errorMessage && (
              <Alert
                message={t('workflowMonitor.errorMessage')}
                description={messageSend.errorMessage}
                type="error"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Card>

          {/* 所有相關消息發送記錄 */}
          <Card title={t('workflowMonitor.allRelatedMessageSends')} style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 16, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              <Text type="secondary">
                調試信息: allMessageSends = {JSON.stringify(messageSend.allMessageSends)}
              </Text>
            </div>
            {messageSend.allMessageSends && messageSend.allMessageSends.length > 0 ? (
              <Table
                dataSource={messageSend.allMessageSends}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 1000 }}
                columns={[
                  {
                    title: t('workflowMonitor.messageSendId'),
                    dataIndex: 'id',
                    key: 'id',
                    width: 150,
                    ellipsis: true,
                    render: (text, record) => (
                      <Text code style={{ fontSize: '12px' }}>
                        {text.substring(0, 8)}...
                        {record.id === messageSend.id && (
                          <Tag color="blue" style={{ marginLeft: 8 }}>當前</Tag>
                        )}
                      </Text>
                    )
                  },
                  {
                    title: t('workflowMonitor.nodeId'),
                    dataIndex: 'nodeId',
                    key: 'nodeId',
                    width: 100,
                    render: (text) => text || '-'
                  },
                  {
                    title: t('workflowMonitor.sendReason'),
                    dataIndex: 'sendReason',
                    key: 'sendReason',
                    width: 120,
                    render: (sendReason) => getSendReasonTag(sendReason)
                  },
                  {
                    title: t('workflowMonitor.status'),
                    dataIndex: 'status',
                    key: 'status',
                    width: 100,
                    render: (status) => {
                      const statusConfig = {
                        Pending: { color: 'default', text: t('workflowMonitor.statusPending') },
                        InProgress: { color: 'processing', text: t('workflowMonitor.statusInProgress') },
                        Completed: { color: 'success', text: t('workflowMonitor.statusCompleted') },
                        Failed: { color: 'error', text: t('workflowMonitor.statusFailed') },
                        PartiallyFailed: { color: 'warning', text: t('workflowMonitor.statusPartiallyFailed') }
                      };
                      const config = statusConfig[status] || statusConfig.Pending;
                      return <Tag color={config.color}>{config.text}</Tag>;
                    }
                  },
                  {
                    title: t('workflowMonitor.totalRecipients'),
                    dataIndex: 'totalRecipients',
                    key: 'totalRecipients',
                    width: 80,
                    align: 'center'
                  },
                  {
                    title: t('workflowMonitor.successCount'),
                    dataIndex: 'successCount',
                    key: 'successCount',
                    width: 80,
                    align: 'center'
                  },
                  {
                    title: t('workflowMonitor.failedCount'),
                    dataIndex: 'failedCount',
                    key: 'failedCount',
                    width: 80,
                    align: 'center'
                  },
                  {
                    title: t('workflowMonitor.createdBy'),
                    dataIndex: 'createdBy',
                    key: 'createdBy',
                    width: 100,
                    render: (text) => text || '-'
                  },
                  {
                    title: t('workflowMonitor.startedAt'),
                    dataIndex: 'startedAt',
                    key: 'startedAt',
                    width: 120,
                    render: (date) => date ? TimezoneUtils.formatDateWithTimezone(date, userTimezoneOffset, 'MM-DD HH:mm:ss') : '-'
                  },
                  {
                    title: t('workflowMonitor.actions'),
                    key: 'actions',
                    width: 100,
                    fixed: 'right',
                    render: (_, record) => (
                      <Space>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => onViewMessageSendDetail(record.id)}
                          disabled={record.id === messageSend.id}
                        >
                          {record.id === messageSend.id ? t('workflowMonitor.current') : t('workflowMonitor.viewDetails')}
                        </Button>
                      </Space>
                    )
                  }
                ]}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">暫無相關消息發送記錄</Text>
              </div>
            )}
          </Card>
        </TabPane>
        
        <TabPane tab={t('workflowMonitor.recipientDetails')} key="recipients">
          {loadingRecipients ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>{t('workflowMonitor.loadingRecipientDetails')}</p>
            </div>
          ) : recipients.length > 0 ? (
            <Table
              dataSource={recipients}
              rowKey="id"
              className="pagination-left-table"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1000 }}
              columns={[
                {
                  title: t('workflowMonitor.recipient'),
                  dataIndex: 'recipientName',
                  key: 'recipientName',
                  width: 200,
                  render: (text, record) => (
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{text || t('workflowMonitor.unnamed')}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{record.phoneNumber}</div>
                    </div>
                  )
                },
                {
                  title: t('workflowMonitor.type'),
                  dataIndex: 'recipientType',
                  key: 'recipientType',
                  width: 100,
                  render: (type) => getRecipientTypeTag(type)
                },
                {
                  title: t('workflowMonitor.status'),
                  dataIndex: 'status',
                  key: 'status',
                  width: 100,
                  render: (status) => getRecipientStatusTag(status)
                },
                {
                  title: t('workflowMonitor.whatsAppMessageId'),
                  dataIndex: 'whatsAppMessageId',
                  key: 'whatsAppMessageId',
                  width: 200,
                  ellipsis: true,
                  render: (text) => text || '-'
                },
                {
                  title: t('workflowMonitor.sentAt'),
                  dataIndex: 'sentAt',
                  key: 'sentAt',
                  width: 150,
                  render: (date) => date ? TimezoneUtils.formatDateWithTimezone(date, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-'
                },
                {
                  title: t('workflowMonitor.deliveredAt'),
                  dataIndex: 'deliveredAt',
                  key: 'deliveredAt',
                  width: 150,
                  render: (date) => date ? TimezoneUtils.formatDateWithTimezone(date, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-'
                },
                {
                  title: t('workflowMonitor.readAt'),
                  dataIndex: 'readAt',
                  key: 'readAt',
                  width: 150,
                  render: (date) => date ? TimezoneUtils.formatDateWithTimezone(date, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-'
                },
                {
                  title: t('workflowMonitor.retryCount'),
                  dataIndex: 'retryCount',
                  key: 'retryCount',
                  width: 80,
                  render: (count, record) => (
                    <div>
                      <Text>{count || 0}</Text>
                      {record.maxRetries && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          / {record.maxRetries}
                        </div>
                      )}
                    </div>
                  )
                },
                {
                  title: t('workflowMonitor.errorMessage'),
                  dataIndex: 'errorMessage',
                  key: 'errorMessage',
                  width: 200,
                  ellipsis: true,
                  render: (text) => text || '-'
                }
              ]}
            />
          ) : (
            <Empty 
              description={t('workflowMonitor.noRecipientRecords')} 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: '40px 0' }}
            />
          )}
        </TabPane>

        <TabPane tab={t('workflowMonitor.statusAnalysis')} key="analysis">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card title={t('workflowMonitor.sendStatusDistribution')} style={{ height: '300px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('workflowMonitor.read')}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Progress 
                        percent={statistics.total > 0 ? Math.round((statistics.read / statistics.total) * 100) : 0} 
                        size="small" 
                        strokeColor="#52c41a"
                        style={{ width: '100px' }}
                      />
                      <span>{statistics.read}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('workflowMonitor.delivered')}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Progress 
                        percent={statistics.total > 0 ? Math.round((statistics.delivered / statistics.total) * 100) : 0} 
                        size="small" 
                        strokeColor="#52c41a"
                        style={{ width: '100px' }}
                      />
                      <span>{statistics.delivered}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('workflowMonitor.sent')}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Progress 
                        percent={statistics.total > 0 ? Math.round((statistics.sent / statistics.total) * 100) : 0} 
                        size="small" 
                        strokeColor="#1890ff"
                        style={{ width: '100px' }}
                      />
                      <span>{statistics.sent}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('workflowMonitor.failed')}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Progress 
                        percent={statistics.total > 0 ? Math.round((statistics.failed / statistics.total) * 100) : 0} 
                        size="small" 
                        strokeColor="#ff4d4f"
                        style={{ width: '100px' }}
                      />
                      <span>{statistics.failed}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('workflowMonitor.pending')}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Progress 
                        percent={statistics.total > 0 ? Math.round((statistics.pending / statistics.total) * 100) : 0} 
                        size="small" 
                        strokeColor="#faad14"
                        style={{ width: '100px' }}
                      />
                      <span>{statistics.pending}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title={t('workflowMonitor.timeAnalysis')} style={{ height: '300px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <Text strong>{t('workflowMonitor.sendStartTime')}:</Text>
                    <div style={{ marginTop: '4px', color: '#666' }}>
                      {TimezoneUtils.formatDateWithTimezone(messageSend.startedAt, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss')}
                    </div>
                  </div>
                  <div>
                    <Text strong>{t('workflowMonitor.sendCompleteTime')}:</Text>
                    <div style={{ marginTop: '4px', color: '#666' }}>
                      {messageSend.completedAt ? TimezoneUtils.formatDateWithTimezone(messageSend.completedAt, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : t('workflowMonitor.inProgress')}
                    </div>
                  </div>
                  <div>
                    <Text strong>{t('workflowMonitor.totalSendTime')}:</Text>
                    <div style={{ marginTop: '4px', color: '#666' }}>
                      {messageSend.completedAt ? 
                        `${TimezoneUtils.calculateDurationInMinutes(messageSend.startedAt, messageSend.completedAt).toFixed(1)} ${t('workflowMonitor.minutes')}` : 
                        t('workflowMonitor.inProgress')
                      }
                    </div>
                  </div>
                  <div>
                    <Text strong>{t('workflowMonitor.successRate')}:</Text>
                    <div style={{ marginTop: '4px', color: '#666' }}>
                      {statistics.total > 0 ? 
                        `${((statistics.sent / statistics.total) * 100).toFixed(1)}%` : 
                        '0%'
                      }
                    </div>
                  </div>
                  <div>
                    <Text strong>{t('workflowMonitor.deliveryRate')}:</Text>
                    <div style={{ marginTop: '4px', color: '#666' }}>
                      {statistics.total > 0 ? 
                        `${((statistics.delivered / statistics.total) * 100).toFixed(1)}%` : 
                        '0%'
                      }
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </div>
  );
};

// 數據集查詢結果表格組件
const DataSetQueryResultTable = ({ data, recordCount }) => {
  // 處理數據格式，支持多種數據結構
  const processedData = React.useMemo(() => {
    if (!data) return [];
    
    // 如果是數組，直接使用
    if (Array.isArray(data)) {
      return data;
    }
    
    // 如果是對象，嘗試找到數組字段
    if (typeof data === 'object') {
      // 查找可能的數組字段
      const possibleArrayFields = ['results', 'data', 'records', 'items', 'rows'];
      for (const field of possibleArrayFields) {
        if (data[field] && Array.isArray(data[field])) {
          return data[field];
        }
      }
      
      // 如果沒有找到數組字段，將對象轉為單個記錄的數組
      return [data];
    }
    
    return [];
  }, [data]);

  // 獲取所有唯一的欄位名
  const columns = React.useMemo(() => {
    if (processedData.length === 0) return [];
    
    const allKeys = new Set();
    processedData.forEach(record => {
      if (typeof record === 'object' && record !== null) {
        Object.keys(record).forEach(key => allKeys.add(key));
      }
    });
    
    return Array.from(allKeys).map(key => ({
      title: key,
      dataIndex: key,
      key: key,
      width: 150,
      ellipsis: true,
      render: (value) => {
        if (value === null || value === undefined) {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>空值</span>;
        }
        if (typeof value === 'object') {
          return (
            <Tooltip title={JSON.stringify(value, null, 2)}>
              <span style={{ color: '#1890ff', cursor: 'help' }}>
                {JSON.stringify(value)}
              </span>
            </Tooltip>
          );
        }
        return <span>{String(value)}</span>;
      }
    }));
  }, [processedData]);

  if (processedData.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        color: '#999',
        background: '#fafafa',
        borderRadius: '6px'
      }}>
        <BarChartOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
        <div>暫無查詢結果數據</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        marginBottom: '16px', 
        padding: '8px 12px', 
        background: '#f6ffed', 
        border: '1px solid #b7eb8f',
        borderRadius: '4px',
        color: '#52c41a'
      }}>
        <strong>共找到 {recordCount || processedData.length} 條記錄</strong>
      </div>
      
      <Table
        dataSource={processedData.map((record, index) => ({
          ...record,
          key: index
        }))}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content', y: 400 }}
        size="small"
        bordered
        style={{
          background: '#fff'
        }}
      />
    </div>
  );
};

export default WorkflowMonitorPage;
